import React, { useState, useMemo, useEffect } from "react";

/* ============================================================
   BU PERFORMANCE DASHBOARD
   - 2 tabs: Master Overview (so sánh BU) + BU Detail (Sales)
   - Hiện dùng SAMPLE_DATA (sample data) đúng cấu trúc doGet() trả về.
   - Khi có endpoint thật: thay loadData() bằng fetch(APPS_SCRIPT_URL).
   ============================================================ */

// ---- Cấu trúc data khớp với JSON mà Apps Script doGet sẽ trả ----
const SAMPLE_DATA = {
  months: ["2026-07"],
  latestMonth: "2026-07",
  lastUpdated: "2026-07-02 06:03 EDT",
  data: {
    "2026-07": {
      daysInMonth: 31,
      daysElapsed: 2,
      bus: {
        Heli: {
          target: { sales: 150000, traffic: 60000, roas: 3.5, ret: 0.045, adcost: 0.20 },
          channels: {
            online:      { gross: 30222, net: 28488, orders: 104, items: 365, refund: 265, sessions: 2000, purchases: 32, adSpend: null, adRev: null },
            marketplace: { gross: 1394,  net: 1394,  orders: 24,  items: 34,  refund: 0,   sessions: null, purchases: null, adSpend: null, adRev: null },
            wholesale:   { gross: 0,     net: 0,     orders: 0,   items: 0,   refund: 0,   sessions: null, purchases: null, adSpend: null, adRev: null },
          },
          stores: [
            { store: "HeliDirect", channel: "online",      gross: 30222, net: 28488, orders: 104, items: 365, refund: 265 },
            { store: "HeliDirect", channel: "marketplace", gross: 1394,  net: 1394,  orders: 24,  items: 34,  refund: 0 },
            { store: "HDRC Wholesale", channel: "wholesale", gross: 0,   net: 0,     orders: 0,   items: 0,   refund: 0 },
          ],
          daily: [
            { date: "2026-07-01", gross: 31579, net: 29882 },
            { date: "2026-07-02", gross: 37, net: 37 },
          ],
        },
        Car: null,
        Jet: null,
      },
    },
  },
};

// URL Web App doGet của từng BU. Dán URL sau khi deploy mỗi file Apps Script.
// Để "" nếu BU đó chưa deploy — dashboard vẫn chạy với các BU đã có.
const APPS_SCRIPT_URLS = {
  Heli: "https://script.google.com/macros/s/AKfycbyBv_YmGXkCJximlrbYPoZ2iF2Lltnil5oC02kS2vjIGZuKJ-YIuYNd2W2AoiKVScHAZw/exec",
  Car: "https://script.google.com/macros/s/AKfycbzYPJ6EC-hntj8rWYe5mNhRrVKHU4MdB1LEqvinyGZbG3ecnRvbaYr-dkDB27eZLgb82Q/exec",
  Jet: "https://script.google.com/macros/s/AKfycbw5P_YJuI4sAKo8ybwTwbs_T8G_R6jPphk3puswl5PErcCUtkVtfocRcBYYYn_QggFh/exec",
};
// Nếu tất cả rỗng → dùng SAMPLE_DATA.

// ---------- helpers ----------
const safe = (n) => (n == null || !isFinite(n)) ? null : n;
const fmtUSD = (n) =>
  safe(n) == null ? "—" : "$" + Math.round(n).toLocaleString("en-US");
const fmtNum = (n) => (safe(n) == null ? "—" : Math.round(n).toLocaleString("en-US"));
const fmtPct = (n) => (safe(n) == null ? "—" : (n * 100).toFixed(1) + "%");

// Gộp các channel của một BU thành số tổng (tính lại tỷ lệ từ gốc)
// Gộp chỉ 1 kênh (online/marketplace/wholesale) — cho Sales report chỉ-online
function rollupChannel(bu, channelId) {
  if (!bu || !bu.channels) return null;
  const c = bu.channels[channelId];
  if (!c) return { gross: 0, net: 0, orders: 0, items: 0, refund: 0, sessions: null, adSpend: 0, adRev: 0, aov: 0, returnPct: 0, convRate: null, roas: null, adCostPct: null, cogs: null, itemsNC: null };
  const gross = c.gross || 0, net = c.net || 0, orders = c.orders || 0, items = c.items || 0;
  const refund = c.refund || 0, sessions = c.sessions || 0, adSpend = c.adSpend || 0, adRev = c.adRev || 0;
  return {
    gross, net, orders, refund, adSpend, adRev, items,
    cogs: c.cogs != null ? c.cogs : null,          // COGS từ raw_daily (null = tháng chưa rebuild)
    itemsNC: c.itemsNC != null ? c.itemsNC : null, // items thiếu cost
    sessions: sessions || null,
    aov: orders ? gross / orders : 0,
    returnPct: net ? refund / net : 0,
    convRate: sessions ? items / sessions : null,
    roas: adSpend ? adRev / adSpend : null,
    adCostPct: gross && adSpend ? adSpend / gross : null,
  };
}

function rollupBU(bu) {
  if (!bu) return null;
  const ch = Object.values(bu.channels);
  const sum = (k) => ch.reduce((a, c) => a + (c[k] || 0), 0);
  const gross = sum("gross");
  const net = sum("net");
  const orders = sum("orders");
  const items = sum("items");
  const refund = sum("refund");
  const sessions = sum("sessions");
  const adSpend = sum("adSpend");
  const adRev = sum("adRev");
  return {
    gross, net, orders, refund, adSpend, adRev,
    sessions: sessions || null,
    aov: orders ? gross / orders : 0,
    returnPct: net ? refund / net : 0,           // Refund / Net (net đã trừ refund)
    convRate: sessions ? items / sessions : null, // items / sessions (chỉ online có sessions)
    roas: adSpend ? adRev / adSpend : null,        // null khi chưa có ad data
    adCostPct: gross && adSpend ? adSpend / gross : null,
  };
}

// Pace = doanh thu MTD ÷ (target × ngày đã qua / tổng ngày)
function pace(gross, target, elapsed, total) {
  if (!target || !elapsed) return 0;
  const expected = target * (elapsed / total);
  return expected ? gross / expected : 0;
}
function statusOf(p) {
  if (p >= 1.0) return { key: "on", label: "On track", tone: "pos" };
  if (p >= 0.85) return { key: "watch", label: "At risk", tone: "warn" };
  return { key: "behind", label: "Off track", tone: "neg" };
}

// Đánh giá 1 chỉ tiêu so với target, theo hướng.
// dir 'up'  : càng cao càng tốt (sales/traffic/roas) → đạt khi actual ≥ target
// dir 'down': càng thấp càng tốt (return/adcost)     → đạt khi actual ≤ target
// Trả { pct, tone, ok } — pct là % so target (để hiển thị), tone màu.
function evalTarget(actual, target, dir) {
  if (target == null || actual == null) return null;
  if (dir === "down") {
    // thấp tốt: actual/target ≤ 1 là đạt. Vượt trần = xấu.
    const ratio = target ? actual / target : 0;
    let tone = "pos";
    if (ratio > 1.0) tone = "neg";        // vượt trần
    else if (ratio > 0.9) tone = "warn";  // sát trần
    return { pct: ratio, tone, ok: ratio <= 1.0 };
  }
  // up: cao tốt
  const pct = target ? actual / target : 0;
  let tone = "neg";
  if (pct >= 1.0) tone = "pos";
  else if (pct >= 0.85) tone = "warn";
  return { pct, tone, ok: pct >= 1.0 };
}

// Forecast cuối tháng có khoảng tin cậy.
// Trung tâm = nhịp trung bình/ngày × tổng ngày.
// Khoảng = ± dựa trên độ lệch chuẩn nhịp ngày; càng nhiều ngày đã qua, khoảng càng hẹp.
function forecastRange(daily, grossMTD, daysElapsed, daysInMonth) {
  if (!daysElapsed || !daysInMonth) return null;
  const perDay = grossMTD / daysElapsed;
  const center = perDay * daysInMonth;

  // độ dao động giữa các ngày (nếu có ≥2 ngày data)
  let sd = 0;
  if (daily && daily.length >= 2) {
    const vals = daily.map((d) => d.gross);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
    sd = Math.sqrt(variance);
  }
  // sai số của tổng dự phóng cho các ngày còn lại ~ sd × sqrt(số ngày còn lại)
  const remaining = Math.max(daysInMonth - daysElapsed, 0);
  const margin = sd * Math.sqrt(remaining);
  return {
    center,
    low: Math.max(center - margin, grossMTD),   // không thấp hơn số đã đạt
    high: center + margin,
    wide: daysElapsed < 5,                        // đầu tháng: khoảng rộng, đánh dấu độ tin thấp
  };
}

const BU_NAMES = ["Heli", "Car", "Jet"];
// Công tắc margin / Contribution margin — tắt (false) khi nghi data cost MTD sai, bật (true) khi data chuẩn.
const SHOW_MARGIN = true;
// Công tắc note nguồn data (chú thích kỹ thuật: tên tab/sheet, "source: ...", giải thích ROAS...).
// false = ẩn hết cho dashboard gọn/sạch; true = hiện lại khi cần debug. Không ảnh hưởng cảnh báo & empty-state.
const SHOW_SOURCE_NOTES = false;
// Fee Amazon ước tính (referral + FBA...) trừ vào Margin Amazon — chỉnh % tại đây (0.15 = 15%).
const AMZ_FEE_PCT = 0.15;
// Fee sàn eBay/Walmart ước tính — trừ vào LN kênh marketplace (raw_daily).
const MKT_FEE_PCT = 0.14;
// ==== Ngưỡng bộ lọc ads tab Amazon — chỉnh số, chips + rules + mô tả điều kiện tự theo ====
const MKP_TACOS_AVG   = 0.04;  // baseline TACOS kênh (TB ~4%); "cao" = 2×, "efficient" = ≤ baseline
const MKP_MARGIN_GOOD = 20;    // % margin đủ khỏe để đề cử scale / thử ad
const MKP_MARGIN_THIN = 15;    // % dưới mức này + TACOS cao → cảnh báo
const MKP_ADS_BURN    = 25;    // $ đốt ad tối thiểu (rev=0) để kết luận
const MKP_ADS_MIN     = 20;    // $ chi ad tối thiểu đủ mẫu (TACOS cao / efficient)
const MKP_REV_TACOS   = 200;   // $ rev tối thiểu cho chip TACOS cao
const MKP_REV_NOAD    = 300;   // $ rev tối thiểu cho chip chưa chạy ad
const MKP_REV_SCALE   = 500;   // $ rev tối thiểu cho chip ad efficient

/* =====================================================================
   LỚP TÍNH TOÁN cho Brand / Product / Inventory reports
   Nhận payload thô (bus[BU].product) → tính chỉ số/phân loại/ngưỡng.
   Đặt gần đầu dashboard.jsx (sau các const config).
   All ngưỡng gom vào PC_THRESHOLDS để dễ chỉnh.
   ===================================================================== */

const PC_THRESHOLDS = {
  // BCG phân nhóm SKU
  bcgGrossHigh: 100,      // gross ≥ $100/tháng = "bán nhiều" (top ~30%)
  bcgUnitsHigh: 3,        // VÀ units ≥ 3 — SKU giá cao bán 1-2 cái không tính là "bán nhiều"
  bcgMarginHigh: 35,      // margin sau ad ≥ 35% = "margin cao"
  // Quyết định ngân sách ad (brand) — neo blended ROAS 8
  roasScale: 8,           // ROAS ≥ 8 → scale
  roasCut: 5,             // ROAS < 5 → giảm
  marginThin: 30,         // margin < 30% → thận trọng
  // Stock kho
  brandDeadStock: true,   // brand chết = gross 0 & stock > 0
};

// ---- helpers ----
function pcPct(part, whole) { return whole > 0 ? (part / whole) * 100 : 0; }
function pcRound(n, d) { const f = Math.pow(10, d || 0); return Math.round(n * f) / f; }
function pcMoney(n) {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (a >= 1e3) return "$" + (n / 1e3).toFixed(1) + "k";
  return "$" + Math.round(n).toLocaleString();
}
function pcNum(n) { return n == null ? "—" : Math.round(n).toLocaleString(); }
// Format ngày gọn: "06 Jul 2026" (bỏ giờ/timezone)
function pcDate(v) {
  if (!v) return "—";
  var d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  var mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  var dd = ("0" + d.getDate()).slice(-2);
  return dd + " " + mon[d.getMonth()] + " " + d.getFullYear();
}

/* ---- BRAND: tính chỉ số dẫn xuất cho mỗi brand ---- */
function pcComputeBrands(rawBrands) {
  if (!rawBrands) return [];
  return rawBrands.map((b) => {
    const profit = b.profit != null ? b.profit : (b.net - b.cogs - b.adSpend);
    const marginPct = pcPct(profit, b.net);            // margin đóng góp %
    const roas = b.adSpend > 0 ? b.adRev / b.adSpend : null;
    // Backward-compatible với snapshot cũ: Heli dùng Sessions làm proxy.
    const itemsViewed = b.itemsViewed != null ? b.itemsViewed : (b.sessions || 0);
    const buyPerView = pcPct(b.itemsPurch, itemsViewed);
    return {
      ...b,
      itemsViewed,
      profit: pcRound(profit, 0),
      marginPct: pcRound(marginPct, 1),
      roas: roas == null ? null : pcRound(roas, 1),
      buyPerView: pcRound(buyPerView, 1),
    };
  });
}

/* ---- BRAND: khuyến nghị ngân sách ad ---- */
function pcAdRecommendation(brand) {
  const { roas, marginPct } = brand;
  if (roas == null || brand.adSpend <= 0) return { key: "none", label: "—" };
  if (roas < 2 && brand.adSpend > 15) return { key: "cut", label: "Cut" };    // đốt nặng: ROAS<2 + spend>$15
  if (roas < 2) return { key: "cut", label: "Reduce spend" };                   // ROAS<2 nhưng spend nhỏ → giảm
  if (marginPct < 0 && roas < PC_THRESHOLDS.roasCut) return { key: "cut", label: "Cut" };  // margin âm + ROAS thấp
  if (roas >= PC_THRESHOLDS.roasScale && marginPct >= PC_THRESHOLDS.marginThin)
    return { key: "scale", label: "Scale up" };
  if (marginPct >= 0 && marginPct < PC_THRESHOLDS.marginThin) return { key: "caution", label: "Caution" };  // margin dương nhưng mỏng
  if (marginPct < 0) return { key: "cut", label: "Reduce spend" };              // margin âm (ROAS≥5) → giảm
  if (roas >= PC_THRESHOLDS.roasCut) return { key: "hold", label: "Hold / scale slightly" };
  return { key: "cut", label: "Reduce spend" };
}

/* ---- PRODUCT: chỉ số mỗi SKU + phân nhóm BCG ---- */
function pcComputeSkus(rawSkus) {
  if (!rawSkus) return [];
  return rawSkus.map((s) => {
    const cogs = s.cogs != null ? s.cogs : s.cost * s.units;
    const profit = s.profit != null ? s.profit : (s.net - cogs - s.adSpend);
    const marginAfterAd = pcPct(profit, s.net);     // margin sau ad
    const roas = s.adSpend > 0 ? s.adRev / s.adSpend : null;
    const hasCost = s.cost > 0;
    const itemsViewed = s.itemsViewed != null ? s.itemsViewed : (s.sessions || 0);
    return {
      ...s,
      itemsViewed,
      profit: pcRound(profit, 0),
      marginAfterAd: pcRound(marginAfterAd, 1),
      roas: roas == null ? null : pcRound(roas, 1),
      hasCost,
      bcg: pcBcgGroup(s.gross, marginAfterAd, s.units, hasCost),
    };
  });
}

// BCG: doanh số × margin sau ad. Chỉ SKUs sold + có cost.
function pcBcgGroup(gross, marginAfterAd, units, hasCost) {
  if (units <= 0 || !hasCost) return null;           // không phân nhóm
  const hiG = gross >= PC_THRESHOLDS.bcgGrossHigh && units >= PC_THRESHOLDS.bcgUnitsHigh;
  const hiM = marginAfterAd >= PC_THRESHOLDS.bcgMarginHigh;
  if (hiG && hiM) return "Star";
  if (hiG && !hiM) return "Cash Cow";
  if (!hiG && hiM) return "Question Mark";
  return "Dog";
}

// SKU đốt tài nguyên: có ad/traffic mà không bán
function pcWasteSkus(skus) {
  const burnAd = skus.filter((s) => s.units === 0 && s.adSpend > 0);
  const burnTraffic = skus.filter((s) => s.units === 0 && s.adSpend === 0 && s.itemsViewed > 0);
  return { burnAd, burnTraffic };
}

/* ---- INVENTORY: trạng thái + chỉ số ---- */
function pcComputeInventory(rawInv, skus) {
  if (!rawInv) return [];
  // map units bán từ skus theo SKU (để tính vòng quay)
  const soldMap = {};
  (skus || []).forEach((s) => { soldMap[s.sku] = s.units; });
  return rawInv.map((it) => {
    const sold = soldMap[it.sku] || 0;
    const turnover = it.stock > 0 ? pcPct(sold, it.stock) : 0;  // vòng quay %
    const isDiscontinued = /discontinued/i.test(it.ptype || "");
    const isOversell = it.stock < 0;
    return {
      ...it,
      sold,
      turnover: pcRound(turnover, 1),
      isDiscontinued,
      isOversell,
    };
  });
}

// Total hợp vốn tồn theo brand (cho khối phân bổ)
function pcInventoryByBrand(inv, excludeBrand) {
  const agg = {};
  inv.forEach((it) => {
    if (excludeBrand && it.brand === excludeBrand) return;
    if (!agg[it.brand]) agg[it.brand] = { brand: it.brand, stockVal: 0, stock: 0, sold: 0, skus: 0 };
    const a = agg[it.brand];
    a.stockVal += it.stockVal || 0;
    a.stock += it.stock || 0;
    a.sold += it.sold || 0;
    a.skus += 1;
  });
  return Object.values(agg).sort((x, y) => y.stockVal - x.stockVal);
}


// Cấu hình report cho mỗi BU. Thêm report mới = thêm 1 phần tử.
// ready: true → bật thật; false → "awaiting data" (mờ, hiện màn hình chờ).
// Cấu trúc điều hướng: KÊNH → nhóm report → report
// Online có đủ 7 report; Marketplace/Wholesale no data (report riêng sau).
const CHANNELS = [
  {
    id: "online", label: "Online Store", icon: "🌐", ready: true,
    groups: [
      {
        group: "Sales & Product",
        reports: [
          { id: "sales",     label: "Sales",     ready: true,  source: "raw_daily · online channel" },
          { id: "brand",     label: "Brand",     ready: true,  source: "MTD tracking (SyncWith)" },
          { id: "product",   label: "Product",   ready: true,  source: "MTD tracking (SyncWith)" },
          { id: "inventory", label: "Inventory", ready: true,  source: "sheet Inventory" },
        ],
      },
      {
        group: "Marketing",
        reports: [
          { id: "mktoverview", label: "Marketing Overview", ready: true, source: "marketing_data + target" },
          { id: "ads",   label: "Paid Ads", ready: true,  source: "MTD tracking (temp) · ROAS from GA4" },
          { id: "seo",   label: "SEO",      ready: false, source: "Search Console / GA4 organic" },
          { id: "email", label: "Email",    ready: true,  source: "CRM/Email MASTER (Klaviyo)" },
        ],
      },
    ],
  },
  {
    id: "marketplace", label: "Marketplace", icon: "🛒", ready: true,
    groups: [
      {
        group: "Marketplace",
        reports: [
          { id: "mkp", label: "Amazon Overview", ready: true, source: "tab Marketplace (Amazon) · eBay/WM: raw_daily" },
        ],
      },
    ],
  },
  {
    id: "wholesale", label: "Wholesale", icon: "📦", ready: false,
    groups: [],
  },
];

// Hold REPORT_GROUPS trỏ tới groups của online (để code cũ dùng flatMap không vỡ)
const REPORT_GROUPS = CHANNELS[0].groups;

// Sinh cảnh báo rule-based (chỉ dùng data Shopify đang có).
// Trả mảng { tone, text }. Rule cần GA4/ad sẽ tự bỏ qua khi thiếu data.
function buildAlerts(rows, daysElapsed, daysInMonth) {
  const alerts = [];
  rows.forEach(({ name, bu, r, p }) => {
    if (!bu) return;
    const tgt = bu.target || {};
    // Return% so target trần của tháng (nếu có), fallback 4.5%
    const retCeil = tgt.ret != null ? tgt.ret : 0.045;
    if (r.returnPct > retCeil) {
      alerts.push({ tone: "neg", text: `${name}: Return ${(r.returnPct * 100).toFixed(1)}% exceeds ceiling ${(retCeil * 100).toFixed(1)}%` });
    } else if (r.returnPct > retCeil * 0.9) {
      alerts.push({ tone: "warn", text: `${name}: Return ${(r.returnPct * 100).toFixed(1)}% near ceiling ${(retCeil * 100).toFixed(1)}%` });
    }
    // Pace doanh thu (chỉ khi có sales target)
    if (tgt.sales != null) {
      if (p < 0.85) {
        alerts.push({ tone: "neg", text: `${name}: pace ${(p * 100).toFixed(0)}% — below target pace` });
      } else if (p < 1.0) {
        alerts.push({ tone: "warn", text: `${name}: pace ${(p * 100).toFixed(0)}% — at risk of missing target` });
      }
    }
    // ROAS dưới target (chỉ khi có ad data + target)
    if (r.roas != null && tgt.roas != null && r.roas < tgt.roas) {
      alerts.push({ tone: "warn", text: `${name}: ROAS ${r.roas.toFixed(1)} below target ${tgt.roas.toFixed(1)}` });
    }
    // Ad Cost% vượt trần tháng (chỉ khi có ad data)
    const adCeil = tgt.adcost != null ? tgt.adcost : 0.20;
    if (r.adCostPct != null && r.adCostPct > adCeil) {
      alerts.push({ tone: "warn", text: `${name}: Ad Cost ${(r.adCostPct * 100).toFixed(0)}% exceeds ceiling ${(adCeil * 100).toFixed(0)}%` });
    }
  });
  // ưu tiên đỏ trước, giới hạn 5 dòng
  alerts.sort((a, b) => (a.tone === "neg" ? -1 : 1) - (b.tone === "neg" ? -1 : 1));
  return alerts;
}

// Gộp payload từ nhiều BU (mỗi BU 1 file doGet) thành 1 data thống nhất.
// Mỗi payload: { months, latestMonth, lastUpdated, data:{ym:{daysInMonth,daysElapsed,bus:{BU:{...}}}} }
// results: { Heli: payload|null, Car: payload|null, Jet: payload|null }
function mergePayloads(results) {
  const monthsSet = new Set();
  let lastUpdated = null;
  let requestedMonth = null;
  let latestLiveMonth = null;
  // gom mọi tháng xuất hiện ở bất kỳ BU nào
  Object.values(results).forEach((pl) => {
    if (!pl || !pl.months) return;
    pl.months.forEach((m) => monthsSet.add(m));
    (pl.snapshotMonths || []).forEach((m) => monthsSet.add(m));
    if (!requestedMonth && pl.requestedMonth) requestedMonth = pl.requestedMonth;
    if (pl.latestMonth && (!latestLiveMonth || pl.latestMonth > latestLiveMonth)) latestLiveMonth = pl.latestMonth;
    if (pl.lastUpdated && (!lastUpdated || pl.lastUpdated > lastUpdated)) lastUpdated = pl.lastUpdated;
  });
  const months = Array.from(monthsSet).sort();
  const data = {};
  months.forEach((ym) => {
    // khung tháng: lấy daysInMonth/daysElapsed từ payload nào có tháng này
    let daysInMonth = 30, daysElapsed = 0;
    const bus = { Heli: null, Car: null, Jet: null };
    BU_NAMES.forEach((name) => {
      const pl = results[name];
      if (pl && pl.data && pl.data[ym]) {
        const blk = pl.data[ym];
        daysInMonth = blk.daysInMonth;
        daysElapsed = blk.daysElapsed;
        // mỗi file trả bus[name] là data của chính nó
        bus[name] = blk.bus && blk.bus[name] ? blk.bus[name] : null;
      }
    });
    data[ym] = { daysInMonth, daysElapsed, bus };
  });
  // Product/Inventory: snapshot MTD (không theo tháng) — gắn riêng theo BU
  const product = {};
  BU_NAMES.forEach((name) => {
    const pl = results[name];
    product[name] = pl && pl.product ? pl.product : null;
  });
  // channelTargets + email + marketing: LƯU THEO BU (mỗi BU 1 bộ riêng, không ghi đè nhau)
  const channelTargets = {};
  const email = {};
  const marketing = {};
  const mkp = {};   // tab Marketplace per BU
  const customer = {};   // customer_monthly per BU
  const brandMkt = {};       // brand eBay/WM per BU
  const brandTargets = {};   // tab brand_target per BU
  const adSpendReal = {};    // tab ad_spend_real per BU
  const seo = {};
  const snapshotInfo = {};
  const snapshotAvailability = {};
  BU_NAMES.forEach((name) => {
    const pl = results[name];
    channelTargets[name] = (pl && pl.channelTargets && Object.keys(pl.channelTargets).length) ? pl.channelTargets : null;
    email[name] = (pl && pl.email && pl.email.hasData) ? pl.email : null;
    marketing[name] = (pl && pl.marketing && !pl.marketing.error && Object.keys(pl.marketing).length) ? pl.marketing : null;
    mkp[name] = (pl && pl.mkp && !pl.mkp.error && pl.mkp.skus && pl.mkp.skus.length) ? pl.mkp : null;
    customer[name] = (pl && pl.customer && Object.keys(pl.customer).length) ? pl.customer : null;
    brandMkt[name] = (pl && pl.brandMkt && Object.keys(pl.brandMkt).length) ? pl.brandMkt : null;
    brandTargets[name] = (pl && pl.brandTargets && Object.keys(pl.brandTargets).length) ? pl.brandTargets : null;
    adSpendReal[name] = (pl && pl.adSpendReal && Object.keys(pl.adSpendReal).length) ? pl.adSpendReal : null;
    seo[name] = pl && pl.seo ? pl.seo : null;
    snapshotInfo[name] = {
      sourceMode: pl && pl.sourceMode ? pl.sourceMode : "unavailable",
      status: pl && pl.snapshotStatus ? pl.snapshotStatus : null,
      createdAt: pl && pl.snapshotCreatedAt ? pl.snapshotCreatedAt : null,
      schemaVersion: pl && pl.snapshotSchemaVersion ? Number(pl.snapshotSchemaVersion) : null,
      error: pl && pl.snapshotError ? pl.snapshotError : null,
    };
    snapshotAvailability[name] = pl && pl.snapshotAvailability ? pl.snapshotAvailability : {};
  });
  const availableBUs = BU_NAMES.filter((name) => results[name]);
  const snapshotMonths = availableBUs.length
    ? Object.keys(snapshotAvailability[availableBUs[0]] || {}).filter((ym) =>
        availableBUs.every((name) => snapshotAvailability[name] && snapshotAvailability[name][ym])
      ).sort()
    : [];
  const snapshotStates = Object.values(snapshotInfo);
  const isHistorical = snapshotStates.some((x) => x.sourceMode === "snapshot" || x.sourceMode === "snapshot-missing");
  const snapshotStatus = snapshotStates.some((x) => x.sourceMode === "snapshot-missing")
    ? "missing"
    : snapshotStates.some((x) => x.status === "draft")
      ? "draft"
      : isHistorical ? "final" : null;
  const snapshotSchemaVersions = snapshotStates
    .filter((x) => x.sourceMode === "snapshot" && x.schemaVersion)
    .map((x) => x.schemaVersion);
  const snapshotSchemaVersion = snapshotSchemaVersions.length
    ? Math.min.apply(null, snapshotSchemaVersions)
    : null;
  return {
    months,
    latestMonth: latestLiveMonth || months[months.length - 1] || null,
    requestedMonth: requestedMonth || months[months.length - 1] || null,
    lastUpdated,
    isHistorical,
    snapshotStatus,
    snapshotSchemaVersion,
    snapshotInfo,
    snapshotMonths,
    snapshotAvailability,
    data,
    product,
    channelTargets,
    email,
    marketing,
    mkp,
    customer,
    brandMkt,
    brandTargets,
    adSpendReal,
    seo,
  };
}

function appendQuery(url, params) {
  const query = Object.entries(params || {})
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  if (!query) return url;
  return url + (url.indexOf("?") >= 0 ? "&" : "?") + query;
}

// Fixture chỉ hoạt động trong Vite dev để kiểm tra UI snapshot mà không chạm Apps Script thật.
function buildDevSnapshotFixture(name, mode, forceSnapshot) {
  const month = "2026-07";
  const sampleBU = JSON.parse(JSON.stringify(SAMPLE_DATA.data[month].bus.Heli));
  const missing = forceSnapshot && mode === "missing" && name === "Jet";
  const fixtureStatus = mode === "final" ? "final" : "draft";
  const sourceMode = forceSnapshot ? (missing ? "snapshot-missing" : "snapshot") : "live";
  return {
    months: [month],
    latestMonth: month,
    requestedMonth: month,
    snapshotMonths: [month],
    snapshotAvailability: {
      [month]: {
        status: fixtureStatus,
        fileId: `dev-${name.toLowerCase()}-${month}`,
        fileName: `${name}_${month}_${fixtureStatus}_fixture.json`,
      },
    },
    lastUpdated: "2026-07-31 23:59 EDT",
    sourceMode,
    snapshotStatus: forceSnapshot && !missing ? fixtureStatus : null,
    snapshotSchemaVersion: forceSnapshot && !missing ? 2 : null,
    snapshotCreatedAt: forceSnapshot && !missing ? "2026-07-31T23:59:00.000Z" : null,
    snapshotError: missing ? `No snapshot for ${month} · ${name}` : null,
    data: {
      [month]: {
        daysInMonth: 31,
        daysElapsed: 31,
        bus: { [name]: sampleBU },
      },
    },
    product: missing ? null : {
      brands: [],
      products: [],
      inventory: [],
    },
    mkp: missing ? null : { skus: [] },
    channelTargets: {},
    email: null,
    marketing: {},
    customer: {},
    brandMkt: {},
    brandTargets: {},
    adSpendReal: {},
    seo: { hasData: false, status: "not_available", source: null },
  };
}

// Tải một BU; fallback JSONP khi fetch bị CORS chặn.
function fetchDashboardPayload(name, url, requestedMonth, forceSnapshot) {
  if (import.meta.env.DEV) {
    const fixtureMode = new URLSearchParams(window.location.search).get("fixture");
    if (fixtureMode === "snapshot" || fixtureMode === "final" || fixtureMode === "missing") {
      return Promise.resolve(buildDevSnapshotFixture(name, fixtureMode, forceSnapshot));
    }
  }
  const requestUrl = appendQuery(url, {
    month: requestedMonth || null,
    snapshot: forceSnapshot ? "1" : null,
  });
  return new Promise((resolve) => {
    fetch(requestUrl)
      .then((res) => { if (!res.ok) throw new Error("HTTP " + res.status); return res.json(); })
      .then((json) => resolve(json && !json.error ? json : null))
      .catch(() => {
        const cbName = "__buDash_" + Math.random().toString(36).slice(2);
        const script = document.createElement("script");
        let done = false;
        const cleanup = () => { delete window[cbName]; if (script.parentNode) script.parentNode.removeChild(script); };
        window[cbName] = (json) => { done = true; resolve(json && !json.error ? json : null); cleanup(); };
        script.src = appendQuery(requestUrl, { callback: cbName });
        script.onerror = () => { resolve(null); cleanup(); };
        document.body.appendChild(script);
        setTimeout(() => { if (!done) { resolve(null); cleanup(); } }, 12000);
      });
  });
}

class ReportErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidUpdate(prev) { if (prev.reportKey !== this.props.reportKey && this.state.err) this.setState({ err: null }); }
  render() {
    if (this.state.err) {
      return (
        <div className="noData">
          <div className="noDataTitle">Report failed to render</div>
          <div className="noDataHint">Details: {String(this.state.err && this.state.err.message || this.state.err)}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App({ embedded = false, visibleBUs, initialBU } = {}) {
  // === Password lock (obfuscation only — NOT real security). Embedded in KPI dashboard → skip. ===
  const DASH_PASSWORD = "2026";
  const [unlocked, setUnlocked] = useState(() => {
    if (embedded) return true;
    try { return localStorage.getItem("dash_unlocked") === "1"; } catch (e) { return false; }
  });
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  function tryUnlock(e) {
    if (e) e.preventDefault();
    if (pwInput === DASH_PASSWORD) {
      setUnlocked(true);
      try { localStorage.setItem("dash_unlocked", "1"); } catch (e) {}
    } else {
      setPwError(true);
    }
  }

  const [data, setData] = useState(SAMPLE_DATA);
  const [loadState, setLoadState] = useState(
    BU_NAMES.some((n) => APPS_SCRIPT_URLS[n]) ? "loading" : "sample"
  );
  const [view, setView] = useState("overview"); // 'overview' | 'detail'
  const [selectedBU, setSelectedBU] = useState(initialBU && BU_NAMES.includes(initialBU) ? initialBU : "Heli");
  const [activeReport, setActiveReport] = useState("sales");
  const [activeChannel, setActiveChannel] = useState("online");
  const [selectedMonth, setSelectedMonth] = useState(null);
  const monthCacheRef = React.useRef({});
  const [snapshotMode, setSnapshotMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("source") === "draft"
        ? "draft"
        : "live";
    } catch (e) {
      return "live";
    }
  });
  const forceSnapshotTest = snapshotMode === "draft";

  function changeSnapshotMode(nextMode) {
    setSnapshotMode(nextMode);
    setActiveChannel("online");
    if (["inventory", "email", "seo", "ads", "mkp"].includes(activeReport)) {
      setActiveReport("sales");
    }
    try {
      const url = new URL(window.location.href);
      if (nextMode === "draft") url.searchParams.set("source", "draft");
      else url.searchParams.delete("source");
      window.history.replaceState({}, "", url);
    } catch (e) {}
  }

  useEffect(() => {
    const urls = APPS_SCRIPT_URLS;
    const active = BU_NAMES.filter((n) => urls[n]); // BU đã có URL
    if (active.length === 0) return; // chưa deploy URL nào → dùng SAMPLE_DATA
    let alive = true;
    const cacheKey = `${selectedMonth || "__current__"}|${forceSnapshotTest ? "snapshot" : "auto"}`;
    const cached = monthCacheRef.current[cacheKey];
    if (cached) {
      setData(cached);
      setLoadState("live");
      return () => { alive = false; };
    }
    setLoadState("loading");

    Promise.all(active.map((n) =>
      fetchDashboardPayload(n, urls[n], selectedMonth, forceSnapshotTest).then((pl) => [n, pl])
    ))
      .then((pairs) => {
        if (!alive) return;
        const results = { Heli: null, Car: null, Jet: null };
        let anyOk = false;
        pairs.forEach(([n, pl]) => { results[n] = pl; if (pl) anyOk = true; });
        if (!anyOk) { setLoadState("error"); return; }
        const merged = mergePayloads(results);
        monthCacheRef.current[cacheKey] = merged;
        if (merged.requestedMonth) {
          const resolvedKey = `${merged.requestedMonth}|${forceSnapshotTest ? "snapshot" : "auto"}`;
          monthCacheRef.current[resolvedKey] = merged;
        }
        setData(merged);
        if (!selectedMonth) setSelectedMonth(merged.requestedMonth || merged.latestMonth);
        setLoadState("live");
      });

    return () => { alive = false; };
  }, [selectedMonth, forceSnapshotTest]);

  // data của tháng đang chọn (fallback tháng mới nhất nếu tháng chọn không có)
  const months = data.months || [];
  const activeMonth = data.data && data.data[selectedMonth] ? selectedMonth : (data.requestedMonth || data.latestMonth);
  const monthData = data.data ? data.data[activeMonth] : null;
  const isCurrentMonth = activeMonth === data.latestMonth;
  const snapshotAvailable = (data.snapshotMonths || []).includes(activeMonth);
  const snapshotSourceLabel = data.snapshotStatus === "final"
    ? "Final snapshot"
    : data.snapshotStatus === "missing"
      ? "Snapshot unavailable"
      : "Draft snapshot";

  function changeReportingMonth(nextMonth) {
    setSelectedMonth(nextMonth);
    changeSnapshotMode(nextMonth === data.latestMonth ? "live" : "draft");
  }

  // gói monthData thành shape mà Overview/Detail đang dùng (bus, daysInMonth, daysElapsed)
  const scoped = monthData
    ? { ...monthData, month: activeMonth, lastUpdated: data.lastUpdated, product: data.product || {}, channelTargets: data.channelTargets || {}, email: data.email || null, marketing: data.marketing || null, mkp: data.mkp || {}, customer: data.customer || {}, brandMkt: data.brandMkt || {}, brandTargets: data.brandTargets || {}, adSpendReal: data.adSpendReal || {}, seo: data.seo || {}, isHistorical: !!data.isHistorical, snapshotStatus: data.snapshotStatus || null, snapshotSchemaVersion: data.snapshotSchemaVersion || null, snapshotInfo: data.snapshotInfo || {} }
    : { bus: {}, daysInMonth: 30, daysElapsed: 0, month: activeMonth, lastUpdated: data.lastUpdated, product: data.product || {}, channelTargets: data.channelTargets || {}, email: data.email || null, marketing: data.marketing || null, mkp: data.mkp || {}, customer: data.customer || {}, brandMkt: data.brandMkt || {}, brandTargets: data.brandTargets || {}, adSpendReal: data.adSpendReal || {}, seo: data.seo || {}, isHistorical: !!data.isHistorical, snapshotStatus: data.snapshotStatus || null, snapshotSchemaVersion: data.snapshotSchemaVersion || null, snapshotInfo: data.snapshotInfo || {} };
  const snapshotErrors = Object.entries(scoped.snapshotInfo || {})
    .filter(([, info]) => info && info.error)
    .map(([name, info]) => `${name}: ${info.error}`);

  if (!unlocked) {
    return (
      <div className="lockScreen">
        <style>{CSS}</style>
        <form className="lockBox" onSubmit={tryUnlock}>
          <div className="lockMark">◆</div>
          <div className="lockTitle">BU Performance Dashboard</div>
          <div className="lockSub">Enter password to access</div>
          <input
            className={"lockInput" + (pwError ? " lockErr" : "")}
            type="password"
            value={pwInput}
            autoFocus
            placeholder="Password"
            onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
          />
          {pwError && <div className="lockErrMsg">Incorrect password</div>}
          <button className="lockBtn" type="submit">Access</button>
        </form>
      </div>
    );
  }

  return (
    <div className={"app" + (embedded ? " embedded" : "")}>
      <style>{CSS}</style>
      <header className="topbar">
        <div className="brand">
          <span className="mark">◆</span>
          <span className="brandName">BU Performance</span>
          <select
            className="monthPick"
            value={activeMonth}
            onChange={(e) => changeReportingMonth(e.target.value)}
            aria-label="Reporting month"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m}{m === data.latestMonth && snapshotMode === "live" ? " · Live" : ""}
              </option>
            ))}
          </select>
          <select
            className={"sourcePick " + snapshotMode}
            value={snapshotMode}
            onChange={(e) => changeSnapshotMode(e.target.value)}
            aria-label="Data source"
          >
            <option value="live" disabled={!isCurrentMonth}>Live data</option>
            <option value="draft" disabled={!snapshotAvailable && snapshotMode !== "draft"}>
              {snapshotSourceLabel}
            </option>
          </select>
          <span className="period">MTD · day {scoped.daysElapsed}/{scoped.daysInMonth}</span>
          {scoped.isHistorical && (
            <span className={"snapshotBadge " + (scoped.snapshotStatus || "")}>
              Historical snapshot · {scoped.snapshotStatus === "final" ? "Final" : scoped.snapshotStatus === "draft" ? "Draft" : "Missing"}
              {scoped.snapshotSchemaVersion ? ` · v${scoped.snapshotSchemaVersion}` : ""}
            </span>
          )}
          {scoped.lastUpdated && <span className="updated">updated {scoped.lastUpdated}</span>}
        </div>
        <nav className="tabs">
          <button
            className={view === "overview" ? "tab active" : "tab"}
            onClick={() => setView("overview")}
          >◐ Overview</button>
          <span className="tabDiv" />
          {BU_NAMES.map((n) => (
            <button
              key={n}
              className={(view === "detail" && selectedBU === n) ? "tab active" : "tab"}
              onClick={() => { setSelectedBU(n); setView("detail"); }}
              disabled={!data.bus && false}
            >{n}</button>
          ))}
        </nav>
      </header>

      {snapshotMode === "draft" && (
        <div className={"banner snapshotTest " + snapshotMode}>
          <span className="bDot" />
          Snapshot view · currently showing{" "}
          <strong>
            {snapshotMode === "draft"
              ? `${activeMonth} ${data.snapshotStatus === "final" ? "Final" : "Draft"} snapshot`
              : `${activeMonth} Live data`}
          </strong>.
          Switching source only changes what this browser displays; it does not overwrite Google Sheets.
        </div>
      )}
      {forceSnapshotTest && snapshotErrors.length > 0 && (
        <div className="banner error">
          <span className="bDot" />
          Snapshot is unavailable for: {snapshotErrors.join(" · ")}
        </div>
      )}
      {loadState === "error" && (
        <div className="banner error">
          <span className="bDot" />
          Could not connect to data source — showing <strong>sample data</strong>, not real numbers.
          Check APPS_SCRIPT_URLS or Web App access permissions.
        </div>
      )}
      {loadState === "sample" && (
        <div className="banner info">
          Viewing <strong>sample data</strong>. Paste each BU URL into APPS_SCRIPT_URLS to connect real data.
        </div>
      )}
      {loadState === "live" && scoped.isHistorical && scoped.snapshotStatus === "missing" && (
        <div className="banner error">
          Historical snapshot is incomplete. Reports that need MTD/Marketplace are unavailable for one or more BUs.
        </div>
      )}
      {loadState === "loading" ? (
        <div className="loadingWrap">
          <div className="spinner" />
          <span>{forceSnapshotTest ? "Loading snapshot…" : "Loading Live data…"}</span>
        </div>
      ) : view === "overview" ? (
        <Overview
          data={scoped}
          onOpen={(bu) => { setSelectedBU(bu); setView("detail"); }}
        />
      ) : (
        <Detail
          data={scoped}
          selectedBU={selectedBU}
          setSelectedBU={setSelectedBU}
          activeReport={activeReport}
          setActiveReport={setActiveReport}
          activeChannel={activeChannel}
          setActiveChannel={setActiveChannel}
          onBack={() => setView("overview")}
        />
      )}

      <footer className="foot">
        {loadState === "live" && (scoped.isHistorical ? "● historical snapshot data" : "● live data from Google Sheets")}
        {loadState === "loading" && "loading data…"}
        {loadState === "sample" && "sample data · paste BU URLs into APPS_SCRIPT_URLS to connect real source"}
        {loadState === "error" && "could not load real data — showing sample data"}
        {" · GA4 & paid ads coming later"}
      </footer>
    </div>
  );
}

/* ================= MASTER OVERVIEW ================= */
function Overview({ data, onOpen }) {
  const ctAll = data.channelTargets || {};
  const [ovMode, setOvMode] = React.useState("compact");   // 'compact' | 'full' — mặc định gọn

  const rows = BU_NAMES.map((name) => {
    // target theo kênh CỦA RIÊNG BU này (config_channel mỗi file)
    const ct = (ctAll[name] && ctAll[name][data.month]) ? ctAll[name][data.month] : {};
    // Session (kênh Online) lấy từ marketing_data của BU: tổng sessions 7 kênh
    const mkM = data.marketing && data.marketing[name] && data.marketing[name][data.month];
    let mkSess = null;
    if (mkM && mkM.rows && mkM.rows.length) {
      mkSess = mkM.rows.reduce((s2, r2) => s2 + (r2.sessions || 0), 0);
    }
    const bu = data.bus[name];
    const prod = data.product && data.product[name];
    // Total BU (mọi kênh) từ raw_daily
    const r = rollupBU(bu);
    // MTD (ad/profit/tồn) — chỉ Car
    let mtd = null;
    if (prod && prod.brands && prod.brands.length) {
      const bs = pcComputeBrands(prod.brands);
      const adSpend = bs.reduce((s, b) => s + b.adSpend, 0);
      const adRev = bs.reduce((s, b) => s + b.adRev, 0);
      const net = bs.reduce((s, b) => s + b.net, 0);
      const profit = bs.reduce((s, b) => s + b.profit, 0);
      const stockVal = (prod.inventory || []).reduce((s, i) => s + (i.stockVal || 0), 0);
      mtd = {
        adSpend, profit, net,
        roas: adSpend > 0 ? adRev / adSpend : null,
        marginPct: net > 0 ? (profit / net) * 100 : null,
        stockVal,
      };
    }
    // Số thực theo kênh (raw_daily)
    const chOnline = bu ? rollupChannel(bu, "online") : null;
    // Conv = orders Shopify online channel (raw_daily) ÷ sessions (marketing_data)
    const mkConv = (mkSess > 0 && chOnline && chOnline.orders != null) ? chOnline.orders / mkSess : null;
    // Spend thật online (tab ad_spend_real) — dùng cho Overview thay spend-SKU (MTD thiếu non-product)
    const asrBU = data.adSpendReal && data.adSpendReal[name];
    const realOnlineSpend = (asrBU && data.month && asrBU[data.month]) ? asrBU[data.month].spend : null;
    // Doanh thu ad online THẬT — từ ad_spend_real (GA4 channel, cùng nguồn spend → nhất quán).
    const realOnlineRev = (asrBU && data.month && asrBU[data.month]) ? asrBU[data.month].rev : null;
    // Marketplace ads: tổng từ tab Marketplace (Amazon+eBay+Walmart) của BU
    const mkpBU = data.mkp && data.mkp[name];
    let mkpAds = null, mkpAdRev = null, mkpRev = null, mkpUnits = null, mkpSess = null;
    let mkpProfit = null, mkpProfitDen = 0;   // LN Amazon (SKU khớp cost MTD) + rev phần phủ
    if (mkpBU && mkpBU.skus && mkpBU.skus.length) {
      mkpAds = mkpBU.skus.reduce((s2, x) => s2 + (x.ads || 0), 0);
      mkpAdRev = mkpBU.skus.reduce((s2, x) => s2 + (x.adRev || 0), 0);
      mkpRev = mkpBU.skus.reduce((s2, x) => s2 + (x.rev || 0), 0);
      mkpUnits = mkpBU.skus.reduce((s2, x) => s2 + (x.units || 0), 0);
      mkpSess = mkpBU.skus.reduce((s2, x) => s2 + (x.sess || 0), 0);
      // LN Amazon = Σ(Rev − Cost×Units − Ads − Refund − fee ước) trên SKU có cost
      const cMap = {};
      ((prod && prod.skus) || []).forEach((p) => { if (p.cost > 0) cMap[p.sku] = p.cost; });
      let num = 0, den = 0;
      mkpBU.skus.forEach((s2) => {
        const c = cMap[s2.sku];
        if (!c || !(s2.rev > 0)) return;
        num += s2.rev - c * (s2.units || 0) - (s2.ads || 0) - (s2.refAmt || 0) - AMZ_FEE_PCT * s2.rev;
        den += s2.rev;
      });
      if (den > 0) { mkpProfit = num; mkpProfitDen = den; }
    }
    const chMkt = bu ? rollupChannel(bu, "marketplace") : null;
    const chWhole = bu ? rollupChannel(bu, "wholesale") : null;
    // TỔNG BU = raw_daily (online + eBay/WM + wholesale) + Amazon (tab Marketplace)
    // Quy ước data: raw_daily kênh marketplace CHỈ chứa eBay+Walmart; Amazon nằm riêng ở tab.
    const buTotal = r ? r.gross + (mkpRev || 0) : null;
    // Total ad spend BU = spend THẬT online (nếu có, else MTD) + ads Amazon
    const onlineSpendForTot = realOnlineSpend != null ? realOnlineSpend : (mtd ? mtd.adSpend : 0);
    const spendTot = onlineSpendForTot + (mkpAds || 0);
    // adRev online: ưu tiên Paid Ads revenue (marketing_data); fallback adRev-SKU (MTD)
    const onlineAdRev = realOnlineRev != null ? realOnlineRev : (mtd && mtd.roas != null ? mtd.roas * mtd.adSpend : 0);
    const adRevTot = onlineAdRev + (mkpAdRev || 0);
    const roasBlend = spendTot > 0 ? adRevTot / spendTot : null;
    // LN eBay/WM + Wholesale từ COGS raw_daily (cột mới): Net − COGS (− fee sàn ước với MKP)
    const mkpWmProfit = (chMkt && chMkt.cogs != null && chMkt.net > 0)
      ? chMkt.net - chMkt.cogs - MKT_FEE_PCT * chMkt.net : null;
    const wsProfit = (chWhole && chWhole.cogs != null && chWhole.net > 0)
      ? chWhole.net - chWhole.cogs : null;
    // LN online — Overview ưu tiên raw_daily (đủ store, gồm PAU); fallback MTD khi tháng CHƯA rebuild
    // (chOnline.cogs == null). Ad luôn ưu tiên spend THẬT (ad_spend_real); chưa nhập thì dùng spend-SKU (MTD).
    const skuAdSpend = mtd ? mtd.adSpend : 0;
    const onlineAdReal = realOnlineSpend != null ? realOnlineSpend : skuAdSpend;
    let onlineProfitReal, onlineProfitDen, onlineCmSrc;
    if (chOnline && chOnline.cogs != null) {
      // raw_daily: Net − COGS − ad thật (COGS từ Inventory qua raw_daily; đủ cả 4 store)
      onlineProfitReal = chOnline.net - chOnline.cogs - onlineAdReal;
      onlineProfitDen = chOnline.net || 0;
      onlineCmSrc = "raw_daily";
    } else if (mtd) {
      // fallback MTD (option A): tháng chưa rebuild → dùng số MTD như cũ
      onlineProfitReal = realOnlineSpend != null ? mtd.profit - (realOnlineSpend - skuAdSpend) : mtd.profit;
      onlineProfitDen = mtd.net || 0;
      onlineCmSrc = "mtd";
    } else {
      onlineProfitReal = 0;
      onlineProfitDen = 0;
      onlineCmSrc = null;
    }
    // Contribution margin CẢ BU = online (raw_daily / fallback MTD) + Amazon (tab) + eBay/WM + Wholesale (phần có data)
    const buProfit = onlineProfitReal + (mkpProfit || 0) + (mkpWmProfit || 0) + (wsProfit || 0);
    const buProfitDen = onlineProfitDen + mkpProfitDen
      + (mkpWmProfit != null ? chMkt.net : 0) + (wsProfit != null ? chWhole.net : 0);
    const buMarginPct = buProfitDen > 0 ? (buProfit / buProfitDen) * 100 : null;
    const hasAmzProfit = mkpProfit != null;
    // Phạm vi sub: liệt kê nguồn đã gồm; đủ 4 → "whole BU"
    const scopeParts = [];
    if (mtd) scopeParts.push("online");
    if (hasAmzProfit) scopeParts.push("Amz");
    if (mkpWmProfit != null) scopeParts.push("MKP");
    if (wsProfit != null) scopeParts.push("WS");
    const profitScope = scopeParts.length >= 4 ? "whole BU" : scopeParts.join(" + ");
    // Độ phủ cost ở kênh dùng COGS raw_daily: items thiếu cost / tổng items — cao thì margin ảo
    const covNC = (mkpWmProfit != null ? (chMkt.itemsNC || 0) : 0) + (wsProfit != null ? (chWhole.itemsNC || 0) : 0);
    const covIt = (mkpWmProfit != null ? (chMkt.items || 0) : 0) + (wsProfit != null ? (chWhole.items || 0) : 0);
    const ncPct = covIt > 0 ? (covNC / covIt) * 100 : null;
    // Contribution margin TỪNG KÊNH (cho scorecard mỗi hàng kênh)
    const chProfit = {
      online: onlineCmSrc ? { profit: onlineProfitReal, margin: onlineProfitDen > 0 ? (onlineProfitReal / onlineProfitDen) * 100 : null } : null,
      mkt: (mkpProfit != null || mkpWmProfit != null) ? (() => {
        const p = (mkpProfit || 0) + (mkpWmProfit || 0);
        const den = mkpProfitDen + (mkpWmProfit != null ? chMkt.net : 0);
        return { profit: p, margin: den > 0 ? (p / den) * 100 : null };
      })() : null,
      ws: wsProfit != null ? { profit: wsProfit, margin: chWhole.net > 0 ? (wsProfit / chWhole.net) * 100 : null } : null,
    };
    // % đạt target doanh thu tổng (dùng _total nếu có)
    const totalRevTarget = (ct._total && ct._total.revenue != null) ? ct._total.revenue : null;
    const pctTarget = (totalRevTarget && buTotal != null) ? buTotal / totalRevTarget : null;
    // Pace theo ngày: so target LŨY TIẾN (target × ngày đã qua/tổng ngày)
    const paceVal = (totalRevTarget && buTotal != null && data.daysElapsed > 0)
      ? buTotal / (totalRevTarget * (data.daysElapsed / data.daysInMonth)) : null;
    // Run rate: quy chiếu cuối tháng nếu giữ tốc độ hiện tại
    const runRate = (buTotal != null && data.daysElapsed > 0) ? (buTotal / data.daysElapsed) * data.daysInMonth : null;
    const status = paceVal == null ? { label: "No target", tone: "muted" }
      : paceVal >= 0.98 ? { label: "● On track", tone: "pos" }
      : paceVal >= 0.85 ? { label: "● At risk", tone: "warn" }
      : { label: "● Off track", tone: "neg" };
    // % spend/rev tổng BU (spend online + Amazon ÷ doanh thu tổng)
    const pctSpendRev = (spendTot > 0 && buTotal > 0) ? (spendTot / buTotal) * 100 : null;
    return { name, bu, r, mtd, chOnline, chMkt, chWhole, pctTarget, status, pctSpendRev, ct, mkSess, mkConv, paceVal, runRate, mkpAds, mkpAdRev, mkpRev, mkpUnits, mkpSess, buTotal, spendTot, roasBlend, buProfit, buMarginPct, hasAmzProfit, profitScope, ncPct, chProfit, realOnlineSpend, onlineAdRev, buProfitDen };
  });

  const company = useMemo(() => {
    const live = rows.filter((x) => x.bu);
    const gross = live.reduce((a, x) => a + (x.buTotal != null ? x.buTotal : x.r.gross), 0);
    const adSpend = live.reduce((a, x) => a + (x.spendTot || 0), 0);
    const stockVal = live.reduce((a, x) => a + (x.mtd ? x.mtd.stockVal : 0), 0);
    const profit = live.reduce((a, x) => a + (x.buProfit || 0), 0);
    const profitDen = live.reduce((a, x) => a + (x.buProfitDen || 0), 0);
    const adRev = rows.reduce((a, x) => a + (x.onlineAdRev || 0) + (x.mkpAdRev || 0), 0);
    return {
      gross, adSpend, stockVal, profit,
      roas: adSpend ? adRev / adSpend : null,
      margin: profitDen > 0 ? (profit / profitDen) * 100 : null,
      pctAdRev: gross > 0 ? (adSpend / gross) * 100 : null,
    };
  }, [rows]);

  // helper: 1 ô chỉ số kênh + target
  function metricCell(label, valStr, actual, target, lowerBetter) {
    let pct = null, tone = "";
    if (target != null && target !== 0 && actual != null) {
      pct = actual / target;
      if (lowerBetter) tone = actual <= target ? "pos" : "warn";
      else tone = pct >= 1 ? "pos" : pct >= 0.8 ? "warn" : "neg";
    }
    return (
      <div className="ovCell" key={label}>
        <div className="ovCellLabel">{label}</div>
        <div className="ovCellVal">{valStr}</div>
        {pct != null
          ? <div className={"ovCellTgt " + tone}>{lowerBetter ? (actual <= target ? "≤" : "▲") : (pct >= 1 ? "▲" : "▼")} {Math.round(pct * 100)}% / {fmtTargetVal(target, label)}</div>
          : <div className="ovCellTgt muted">{target == null ? "" : ""}</div>}
      </div>
    );
  }

  return (
    <main className="wrap">
      {/* DẢI CÔNG TY */}
      <div className="ovSectionLabel">COMPANY TOTAL</div>
      <section className="ovCoGrid">
        <div className="ovCoCard"><div className="ovCoLbl">Total revenue</div><div className="ovCoVal">{fmtUSD(company.gross)}</div><div className="ovCoSub">3 BU</div></div>
        <div className="ovCoCard"><div className="ovCoLbl">Total ad spend</div><div className="ovCoVal">{company.adSpend ? fmtUSD(company.adSpend) : "—"}</div><div className="ovCoSub">online + Amazon</div></div>
        <div className="ovCoCard"><div className="ovCoLbl">Blended ROAS</div><div className={"ovCoVal " + (company.roas == null ? "" : company.roas >= 4 ? "posT" : company.roas >= 2 ? "warnT" : "negT")}>{company.roas == null ? "—" : company.roas.toFixed(1)}</div><div className="ovCoSub">rev ad ÷ spend</div></div>
        <div className="ovCoCard"><div className="ovCoLbl">Ad / Revenue</div><div className="ovCoVal">{company.pctAdRev == null ? "—" : company.pctAdRev.toFixed(1) + "%"}</div><div className="ovCoSub">spend ÷ total rev</div></div>
        {SHOW_MARGIN && <div className="ovCoCard"><div className="ovCoLbl">Contribution margin</div><div className={"ovCoVal " + (company.profit >= 0 ? "posT" : "negT")}>{company.profit ? fmtUSD(company.profit) : "—"}</div><div className={"ovCoSub " + (company.margin == null ? "" : company.margin >= 20 ? "posT" : company.margin >= 10 ? "warnT" : "negT")}>{company.margin == null ? "—" : "margin " + Math.round(company.margin) + "%"}</div></div>}
        {!data.isHistorical && <div className="ovCoCard"><div className="ovCoLbl">Inventory value</div><div className="ovCoVal">{company.stockVal ? fmtUSD(company.stockVal) : "—"}</div><div className="ovCoSub">3 BU</div></div>}
      </section>

      {/* KHỐI TỪNG BU */}
      <div className="ovSectionHead">
        <span className="ovSectionLabel">BU DETAIL</span>
        <div className="ovModeToggle">
          <button className={ovMode === "compact" ? "on" : ""} onClick={() => setOvMode("compact")}>Compact</button>
          <button className={ovMode === "full" ? "on" : ""} onClick={() => setOvMode("full")}>Full</button>
        </div>
      </div>
      {rows.map((row) => (
        ovMode === "compact"
          ? <BUBlockCompact key={row.name} row={row} onOpen={onOpen} data={data} />
          : <BUBlockFull key={row.name} row={row} ct={row.ct} onOpen={onOpen} metricCell={metricCell} data={data} />
      ))}
    </main>
  );
}

// format giá trị target hiển thị theo loại chỉ số
function fmtTargetVal(t, label) {
  if (label === "Revenue") return "$" + (t >= 1000 ? (t / 1000).toFixed(0) + "k" : t);
  if (label === "Session") return t >= 1000 ? (t / 1000).toFixed(0) + "k" : String(t);
  if (label === "Conv") return (t * 100).toFixed(1) + "%";
  if (label === "% ad/rev") return (t * 100).toFixed(0) + "%";
  if (label === "% spend/rev") return (t * 100).toFixed(0) + "%";
  if (label === "ROAS") return t.toFixed(0);
  return String(t);
}

// 1 khối BU
// ===== BU BLOCK — CHẾ ĐỘ GỌN (Overview mode 'compact') =====
// Dùng lại toàn bộ biến đã tính trong row (map ở Overview). Mỗi kênh 1 dòng.
function BUBlockCompact({ row, onOpen, data }) {
  const [showBrand, setShowBrand] = React.useState(false);
  const { name, bu, r, mtd, chMkt, chWhole, status, paceVal, runRate,
          buTotal, spendTot, roasBlend, buMarginPct, mkpRev, mkpAds, mkpAdRev,
          pctTarget } = row;

  if (!bu) {
    return (
      <div className="ovcRow ovcEmpty">
        <span className="ovcName">{name}</span>
        <span className="badge muted">no data</span>
      </div>
    );
  }

  const total = (buTotal != null ? buTotal : r.gross) || 0;
  const pctBU = (v) => total > 0 ? Math.round((v / total) * 100) : 0;
  const ct = (data.channelTargets && data.channelTargets[name] && data.channelTargets[name][data.month]) || {};
  const tTotal = ct._total || {};
  const pctTgt = (tTotal.revenue > 0) ? Math.round((total / tTotal.revenue) * 100) : null;

  // Badge pace: theo paceVal (đã tính lũy tiến trong row)
  const paceTone = paceVal == null ? "muted" : paceVal >= 0.98 ? "pos" : paceVal >= 0.85 ? "warn" : "neg";
  const paceLabel = paceVal == null ? "No target" : paceVal >= 0.98 ? "On track" : paceVal >= 0.85 ? "At risk" : "Off track";

  // Số kênh
  const onlineRev = r.gross - (chMkt ? chMkt.gross : 0) - (chWhole ? chWhole.gross : 0);
  const mktTotalRev = (chMkt ? chMkt.gross : 0) + (mkpRev || 0);
  const wsRev = chWhole ? chWhole.gross : 0;
  const onlineProfit = row.chProfit && row.chProfit.online;
  const mkpTacos = (mkpRev > 0 && mkpAds != null) ? (mkpAds / mkpRev) * 100 : null;
  const mkpRoas = (mkpAds > 0 && mkpAdRev != null) ? mkpAdRev / mkpAds : null;
  const onlineSpendUse = row.realOnlineSpend != null ? row.realOnlineSpend : (mtd ? mtd.adSpend : null);
  const onlineRoas = row.onlineAdRev > 0 && onlineSpendUse > 0 ? row.onlineAdRev / onlineSpendUse : null;
  const onlinePctAdRev = (onlineSpendUse != null && onlineRev > 0) ? (onlineSpendUse / onlineRev) * 100 : null;

  // Brand: gộp online (product.brands) + Amazon (mkp.skus) + eBay/WM (brandMkt), so target
  const brandList = (() => {
    const prod = data.product && data.product[name];
    const mkpBU = data.mkp && data.mkp[name];
    const bMktArr = (data.brandMkt && data.brandMkt[name] && data.brandMkt[name][data.month]) || [];
    const tgArr = (data.brandTargets && data.brandTargets[name] && data.brandTargets[name][data.month]) || [];
    const bm = {};
    const touch = (nm) => { const k = String(nm || "?").trim().toLowerCase() || "?"; return bm[k] || (bm[k] = { name: String(nm || "?").trim() || "?", rev: 0, target: null }); };
    if (prod && prod.brands) prod.brands.forEach((b) => { touch(b.brand).rev += b.gross || 0; });
    if (mkpBU && mkpBU.skus) mkpBU.skus.forEach((s) => { touch(s.brand).rev += s.rev || 0; });
    bMktArr.forEach((b) => { touch(b.brand).rev += b.rev || 0; });
    tgArr.forEach((t) => { touch(t.brand).target = (touch(t.brand).target || 0) + (t.revenue || 0); });
    return Object.values(bm).filter((b) => b.rev > 0 || b.target).sort((a, b2) => b2.rev - a.rev);
  })();
  const paceDen = data.daysElapsed > 0 ? data.daysElapsed / data.daysInMonth : null;
  const brandPct = (b) => b.target > 0 ? Math.round((b.rev / b.target) * 100) : null;
  const brandBadges = brandList.filter((b) => b.target > 0).slice(0, 2);
  const brandRest = brandList.length - brandBadges.length;

  // Màu semantic cho chỉ số (tone: pos/warn/neg/"" ); parts = [{t, tone}]
  const chanCard = (dot, label, rev, parts) => (
    <div className="ovcCardCh" style={{ borderLeftColor: dot }}>
      <div className="ovcChTop"><span className="ovcChLabel">{label}</span><span className="ovcChPct">{pctBU(rev)}% BU</span></div>
      <div className="ovcChRev">{fmtUSD(rev)}</div>
      <div className="ovcChMetrics">
        {parts.map((p, i) => <span key={i} className={p.tone || "sub"}>{p.t}</span>)}
      </div>
    </div>
  );
  const toneRoas = (v) => v == null ? "sub" : v >= 4 ? "posT" : v >= 2 ? "warnT" : "negT";
  const toneLN = (v) => v == null ? "sub" : v >= 20 ? "posT" : v >= 10 ? "warnT" : "negT";

  return (
    <div className="ovcCard">
      <div className="ovcHead">
        <span className="ovcName">{name}</span>
        <span className={"badge " + paceTone}>● {paceLabel}</span>
        {runRate != null && <span className="ovcRR">RR {fmtUSD(runRate)}{paceVal != null ? ` · ${Math.round(paceVal * 100)}% target` : ""}</span>}
        <span className="ovcDetail" onClick={() => onOpen(name)}>View details →</span>
      </div>

      <div className="ovcTotRow">
        <span className="ovcTotVal">{fmtUSD(total)}</span>
        {pctTgt != null && (
          <div className="ovcBar">
            <div className="ovcBarLbl">{pctTgt}% target</div>
            <div className="ovcBarTrack"><div className="ovcBarFill" style={{ width: Math.min(100, pctTgt) + "%" }} /></div>
          </div>
        )}
        <span className="ovcTotMeta">
          {spendTot > 0 ? `ad ${fmtUSD(spendTot)}` : "ad —"}
          {roasBlend != null ? <> · ROAS <b>{roasBlend.toFixed(1)}</b></> : ""}
          {(spendTot > 0 && total > 0) ? <> · ad/rev <b>{((spendTot / total) * 100).toFixed(1)}%</b></> : ""}
          {SHOW_MARGIN && buMarginPct != null ? <> · LN <b>{Math.round(buMarginPct)}%</b></> : ""}
        </span>
      </div>

      <div className="ovcChans">
        {chanCard("#534AB7", "Online", onlineRev, (() => {
          const p = [];
          if (onlineSpendUse != null) p.push({ t: `spend ${fmtUSD(onlineSpendUse)}`, tone: "sub" });
          if (onlineRoas != null) p.push({ t: `ROAS ${onlineRoas.toFixed(1)}`, tone: toneRoas(onlineRoas) });
          if (onlinePctAdRev != null) p.push({ t: `ad/rev ${onlinePctAdRev.toFixed(1)}%`, tone: "sub" });
          if (SHOW_MARGIN && onlineProfit && onlineProfit.margin != null) p.push({ t: `LN ${Math.round(onlineProfit.margin)}%`, tone: toneLN(onlineProfit.margin) });
          return p;
        })())}
        {mktTotalRev > 0 && chanCard("#9A82C4", "Marketplace", mktTotalRev, (() => {
          const p = [];
          if (mkpAds > 0) p.push({ t: `spend ${fmtUSD(mkpAds)}`, tone: "sub" });
          if (mkpRoas != null) p.push({ t: `ROAS ${mkpRoas.toFixed(1)}`, tone: toneRoas(mkpRoas) });
          if (mkpTacos != null) p.push({ t: `TACOS ${mkpTacos.toFixed(1)}%`, tone: "sub" });
          return p;
        })())}
        {wsRev > 0 && chanCard("#5B9E6F", "Wholesale", wsRev, (() => {
          const p = [];
          const wm = SHOW_MARGIN && chWhole && chWhole.cogs != null && chWhole.net > 0 ? ((chWhole.net - chWhole.cogs) / chWhole.net) * 100 : null;
          if (wm != null) p.push({ t: `LN ${Math.round(wm)}%`, tone: toneLN(wm) });
          return p;
        })())}
      </div>

      {brandList.length > 0 && (
        <div className="ovcBrand">
          <span className="ovcBrandLbl">▸ Brand:</span>
          {brandBadges.map((b) => {
            const p = brandPct(b);
            const tone = p == null ? "" : p >= 98 ? "" : p >= 70 ? "warnT" : "negT";
            return <span key={b.name} className={"ovcBrandItem " + tone}>{b.name} <b>{p}%</b></span>;
          })}
          {brandBadges.length === 0 && <span className="muted" style={{ fontSize: 12 }}>no target set</span>}
          {brandRest > 0 && <span className="muted ovcBrandMore">+{brandRest} brand</span>}
          <span className="ovcBrandToggle" onClick={() => setShowBrand(!showBrand)}>{showBrand ? "collapse ↑" : "expand ↓"}</span>
        </div>
      )}

      {showBrand && (
        <div className="ovcBrandPanel">
          {brandList.slice(0, 8).map((b) => {
            const p = brandPct(b);
            const pace = (b.target > 0 && paceDen) ? (b.rev / (b.target * paceDen)) * 100 : null;
            const tone = pace == null ? "" : pace >= 98 ? "posT" : pace >= 85 ? "warnT" : "negT";
            return (
              <div className="ovcBpRow" key={b.name}>
                <span className="ovcBpName">{b.name}</span>
                <span className="ovcBpTrack">{b.target > 0
                  ? <span className={"ovcBpFill " + (pace != null && pace < 85 ? "neg" : "")} style={{ width: Math.min(100, p) + "%" }} />
                  : <span className="ovcBpFill none" />}</span>
                <span className="ovcBpVal">{fmtUSD(b.rev)}<span className="muted">{b.target > 0 ? ` / ${fmtUSD(b.target)}` : " / —"}</span></span>
                <span className={"ovcBpBadge " + tone}>{p != null ? `${p}%` : "—"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BUBlockFull({ row, ct, onOpen, metricCell, data }) {
  const [showAllBrandPerf, setShowAllBrandPerf] = React.useState(false);
  const [bpq, setBpq] = React.useState("");
  const { name, bu, r, mtd, chOnline, chMkt, chWhole, pctTarget, status, pctSpendRev, mkSess, mkConv, paceVal, runRate, mkpAds, mkpAdRev, mkpRev, mkpUnits, mkpSess, buTotal, spendTot, roasBlend, buProfit, buMarginPct, hasAmzProfit, profitScope, ncPct, chProfit, realOnlineSpend, onlineAdRev } = row;
  const tOnline = ct.online || {};
  const tMkt = ct.marketplace || {};
  const tWhole = ct.wholesale || {};
  const tTotal = ct._total || {};

  if (!bu) {
    return (
      <div className="ovBU ovBUEmpty">
        <div className="ovBUHead">
          <span className="ovBUName">{name}</span>
          <span className="badge muted">no data</span>
        </div>
        <div className="ovBUEmptyHint">Connect Shopify for this BU to view details.</div>
      </div>
    );
  }

  const total = (buTotal != null ? buTotal : r.gross) || 0;   // gồm cả Amazon (tab Marketplace)
  // Ô Contribution margin cho từng hàng kênh (ẩn theo SHOW_MARGIN; kênh no data → "—")
  const profitCell = (cp) => (
    <div className="ovCell"><div className="ovCellLabel">Contribution margin</div>
      <div className={"ovCellVal " + (cp && cp.profit < 0 ? "negT" : "posT")}>{cp ? fmtUSD(cp.profit) : "—"}</div>
      <div className="ovCellTgt muted">{cp && cp.margin != null ? "margin " + cp.margin.toFixed(0) + "%" : "no COGS yet"}</div></div>
  );
  const pctOfBU = (v) => total > 0 ? Math.round((v / total) * 100) : 0;

  return (
    <div className="ovBU">
      <div className="ovBUHead">
        <div className="ovBUHeadL">
          <span className="ovBUName">{name}</span>
          <span className={"badge " + status.tone}>{status.label}</span>
          {runRate != null && (
            <span className="rrTag">RR {fmtUSD(runRate)}{paceVal != null && (
              <span className={status.tone === "pos" ? "posT" : status.tone === "warn" ? "warnT" : "negT"}> · {Math.round(paceVal * 100)}% target</span>
            )}</span>
          )}
        </div>
        <span className="ovBULink" onClick={() => onOpen(name)}>View details →</span>
      </div>

      {/* Total BU: 5 ô */}
      <div className="ovTotalRow">
        <div className="ovTot"><div className="ovTotLabel">Gross revenue</div><div className="ovTotVal">{fmtUSD(buTotal != null ? buTotal : r.gross)}</div><div className="ovTotSub">{pctTarget != null ? Math.round(pctTarget * 100) + "% target" : "—"}</div></div>
        <div className="ovTot"><div className="ovTotLabel">Total ad spend</div><div className="ovTotVal">{spendTot > 0 ? fmtUSD(spendTot) : "—"}</div><div className="ovTotSub">{roasBlend != null ? "ROAS " + roasBlend.toFixed(1) : "—"}</div></div>
        <div className="ovTot"><div className="ovTotLabel">% spend/rev</div><div className={"ovTotVal " + (pctSpendRev != null && tTotal.pctAdRev != null ? (pctSpendRev / 100 <= tTotal.pctAdRev ? "posT" : "warnT") : "")}>{pctSpendRev != null ? pctSpendRev.toFixed(1) + "%" : "—"}</div><div className="ovTotSub">{tTotal.pctAdRev != null ? "≤ " + (tTotal.pctAdRev * 100).toFixed(0) + "%" : "whole BU"}</div></div>
        {SHOW_MARGIN && (<div className="ovTot"><div className="ovTotLabel">Contribution margin</div><div className="ovTotVal posT">{buProfit != null && (mtd || hasAmzProfit) ? fmtUSD(buProfit) : "—"}</div><div className="ovTotSub">{buMarginPct != null ? "margin " + buMarginPct.toFixed(0) + "%" + (profitScope ? " · " + profitScope : "") + (ncPct != null && ncPct > 10 ? ` · ⚠ ${Math.round(ncPct)}% items missing cost` : "") : "—"}</div></div>)}
        {!data.isHistorical && <div className="ovTot"><div className="ovTotLabel">Inventory value</div><div className="ovTotVal">{mtd ? fmtUSD(mtd.stockVal) : "—"}</div></div>}
      </div>

      {/* KÊNH ONLINE */}
      <div className="ovChan">
        <div className="ovChanHead"><span className="ovDot online" /><span className="ovChanName">Online</span><span className="ovChanPct online">{pctOfBU(chOnline.gross)}% of BU revenue</span></div>
        <div className="ovChanCells">
          {metricCell("Revenue", fmtUSD(chOnline.gross), chOnline.gross, tOnline.revenue, false)}
          {metricCell("Session", mkSess == null ? "—" : fmtNum(mkSess), mkSess, tOnline.session, false)}
          {metricCell("Conv", mkConv == null ? "—" : fmtPct(mkConv), mkConv, tOnline.conversion, false)}
          <div className="ovCell"><div className="ovCellLabel">AOV</div><div className="ovCellVal">{chOnline.orders > 0 ? fmtUSD(chOnline.gross / chOnline.orders) : "—"}</div><div className="ovCellTgt muted">gross/order</div></div>
          {(() => {
            const spendUse = realOnlineSpend != null ? realOnlineSpend : (mtd ? mtd.adSpend : null);
            const roasReal = (onlineAdRev > 0 && spendUse > 0) ? onlineAdRev / spendUse : null;
            return metricCell("ROAS", roasReal != null ? roasReal.toFixed(1) : "—", roasReal, tOnline.roas, false);
          })()}
          <div className="ovCell"><div className="ovCellLabel">Ad spend</div><div className="ovCellVal">{realOnlineSpend != null ? fmtUSD(realOnlineSpend) : (mtd ? fmtUSD(mtd.adSpend) : "—")}</div><div className="ovCellTgt muted">{realOnlineSpend != null ? "real spend" : "by SKU (MTD)"}</div></div>
          {(() => {
            const spendUse = realOnlineSpend != null ? realOnlineSpend : (mtd ? mtd.adSpend : null);
            const pct = (spendUse != null && chOnline.gross > 0) ? spendUse / chOnline.gross : null;
            return metricCell("% ad/rev", pct != null ? (pct * 100).toFixed(1) + "%" : "—", pct, tOnline.pctAdRev, true);
          })()}
          {SHOW_MARGIN && profitCell(chProfit && chProfit.online)}
        </div>
      </div>

      {/* KÊNH MARKETPLACE */}
      <div className="ovChan">
        <div className="ovChanHead"><span className="ovDot mkt" /><span className="ovChanName">Marketplace</span><span className="ovChanPct mkt">{pctOfBU((chMkt.gross || 0) + (mkpRev || 0))}% of BU revenue</span>{SHOW_SOURCE_NOTES && (mkpRev == null ? <span className="ovChanNote">ad/ROAS awaiting dedicated sheet</span> : <span className="ovChanNote">Amazon: tab Marketplace · eBay/WM: raw_daily</span>)}</div>
        <div className="ovChanCells">
          {mkpRev == null ? (
            <>
              {metricCell("Revenue", fmtUSD(chMkt.gross), chMkt.gross, tMkt.revenue, false)}
              <div className="ovCell"><div className="ovCellLabel">AOV</div><div className="ovCellVal">{chMkt.orders > 0 ? fmtUSD(chMkt.gross / chMkt.orders) : "—"}</div><div className="ovCellTgt muted">gross/order</div></div>
              <div className="ovCell"><div className="ovCellLabel">Ad spend</div><div className="ovCellVal muted2">—</div><div className="ovCellTgt muted">awaiting sheet</div></div>
              <div className="ovCell"><div className="ovCellLabel">ROAS</div><div className="ovCellVal muted2">—</div><div className="ovCellTgt muted">awaiting sheet</div></div>
              <div className="ovCell"><div className="ovCellLabel">% ad/rev</div><div className="ovCellVal muted2">—</div><div className="ovCellTgt muted">awaiting sheet</div></div>
              {SHOW_MARGIN && profitCell(chProfit && chProfit.mkt)}
            </>
          ) : (
            <>
              {metricCell("Revenue", fmtUSD((chMkt.gross || 0) + mkpRev), (chMkt.gross || 0) + mkpRev, tMkt.revenue, false)}
              <div className="ovCell"><div className="ovCellLabel">Session</div><div className="ovCellVal">{mkpSess != null ? fmtNum(mkpSess) : "—"}</div><div className="ovCellTgt muted">Amazon</div></div>
              <div className="ovCell"><div className="ovCellLabel">Ad spend</div><div className="ovCellVal">{fmtUSD(mkpAds)}</div><div className="ovCellTgt muted">Amazon (tab)</div></div>
              {metricCell("ROAS", mkpAds > 0 ? (mkpAdRev / mkpAds).toFixed(1) : "—", mkpAds > 0 ? mkpAdRev / mkpAds : null, tMkt.roas, false)}
              {metricCell("% ad/rev", mkpRev > 0 ? ((mkpAds / mkpRev) * 100).toFixed(1) + "%" : "—", mkpRev > 0 ? mkpAds / mkpRev : null, tMkt.pctAdRev, true)}
              {SHOW_MARGIN && profitCell(chProfit && chProfit.mkt)}
            </>
          )}
        </div>
      </div>

      {/* KÊNH WHOLESALE */}
      <div className="ovChan">
        <div className="ovChanHead"><span className="ovDot whole" /><span className="ovChanName">Wholesale</span><span className="ovChanPct whole">{pctOfBU(chWhole.gross)}% of BU revenue</span></div>
        <div className="ovChanCells">
          {metricCell("Revenue", fmtUSD(chWhole.gross), chWhole.gross, tWhole.revenue, false)}
          <div className="ovCell"><div className="ovCellLabel">Orders</div><div className="ovCellVal">{fmtNum(chWhole.orders)}</div><div className="ovCellTgt muted">no target</div></div>
          <div className="ovCell"><div className="ovCellLabel">AOV</div><div className="ovCellVal">{chWhole.orders > 0 ? fmtUSD(chWhole.gross / chWhole.orders) : "—"}</div><div className="ovCellTgt muted">gross/order</div></div>
          {SHOW_MARGIN && profitCell(chProfit && chProfit.ws)}
        </div>
      </div>

      {/* Brand performance — online (MTD) + Amazon (tab) + eBay/WM (brand_mkp_monthly), so brand_target */}
      {(() => {
        const prod = data.product && data.product[name];
        const mkpBU2 = data.mkp && data.mkp[name];
        const bMktArr = (data.brandMkt && data.brandMkt[name] && data.brandMkt[name][data.month]) || [];
        const tgArr = (data.brandTargets && data.brandTargets[name] && data.brandTargets[name][data.month]) || [];
        // Gộp 3 nguồn theo brand (key: lowercase trim)
        const bm = {};
        const touch = (nm) => {
          const k = String(nm || "(unknown)").trim().toLowerCase() || "(unknown)";
          return bm[k] || (bm[k] = { name: String(nm || "(unknown)").trim() || "(unknown)", online: 0, amz: 0, mkt: 0, target: null });
        };
        if (prod && prod.brands) prod.brands.forEach((b) => { touch(b.brand).online += b.gross || 0; });
        if (mkpBU2 && mkpBU2.skus) mkpBU2.skus.forEach((s) => { touch(s.brand).amz += s.rev || 0; });
        bMktArr.forEach((b) => { touch(b.brand).mkt += b.rev || 0; });
        tgArr.forEach((t) => { touch(t.brand).target = (touch(t.brand).target || 0) + (t.revenue || 0); });
        const list = Object.values(bm).map((b) => ({ ...b, total: b.online + b.amz + b.mkt }))
          .filter((b) => b.total > 0 || b.target).sort((a, b2) => b2.total - a.total);
        if (!list.length) return null;
        const paceDen = data.daysElapsed > 0 ? data.daysElapsed / data.daysInMonth : null;
        const top = list.slice(0, 5);
        const rest = list.slice(5);
        const restRev = rest.reduce((x, b) => x + b.total, 0);
        const brandRow = (b) => {
          const pct = b.target > 0 ? (b.total / b.target) * 100 : null;
          const pace = (b.target > 0 && paceDen) ? b.total / (b.target * paceDen) : null;
          const tone = pace == null ? "" : pace >= 0.98 ? "posT" : pace >= 0.85 ? "warnT" : "negT";
          const tip = `online ${fmtUSD(b.online)} · Amazon ${fmtUSD(b.amz)} · eBay/WM ${fmtUSD(b.mkt)}`;
          return (
            <div className="obRow" key={b.name} title={tip}>
              <span className="obName">{b.name}</span>
              <span className="obTrack">{b.target > 0
                ? <span className={"obFill " + (pace != null && pace < 0.85 ? "neg" : "")} style={{ width: Math.min(100, pct) + "%" }} />
                : <span className="obFill none" />}</span>
              <span className="obVal">{fmtUSD(b.total)}<span className="obTgt">{b.target > 0 ? " / " + fmtUSD(b.target) : " / —"}</span></span>
              <span className={"obBadge " + tone}>{pct != null
                ? `● ${Math.round(pct)}% · pace ${Math.round(pace * 100)}%`
                : "no target yet"}</span>
            </div>
          );
        };
        return (
          <div className="ovBrandBlock">
            <div className="ovChanHead"><span className="ovDot" style={{ background: "#9A82C4" }} /><span className="ovChanName">Brand performance</span>
              {SHOW_SOURCE_NOTES && <span className="ovChanNote">online + Amazon + eBay/WM · target: brand_target</span>}
              {list.length > 5 && <button className="obAll" onClick={() => setShowAllBrandPerf(true)}>View all ({list.length}) →</button>}
            </div>
            {top.map(brandRow)}
            {rest.length > 0 && <div className="obRest muted">Others ({rest.length} brand): {fmtUSD(restRev)}</div>}
            {showAllBrandPerf && (
              <div className="mdOverlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAllBrandPerf(false); }}>
                <div className="mdPanel">
                  <div className="mdHead"><span className="mdTitle">Brand performance — {name} ({list.length})</span>
                    <button className="mdClose" onClick={() => setShowAllBrandPerf(false)}>✕</button></div>
                  <input className="searchBox mdSearch" placeholder="🔍 Search brand" value={bpq} autoFocus onChange={(e) => setBpq(e.target.value)} />
                  <div className="mdList">
                    {list.filter((b) => !bpq || b.name.toLowerCase().includes(bpq.toLowerCase())).map(brandRow)}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Gợi ý */}
      <div className="ovHint">
        <span className="ovHintLabel">Tip:</span> {buildBUHint(row, ct)}
      </div>
    </div>
  );
}

// Sinh gợi ý ngắn cho 1 BU
function buildBUHint(row, ct) {
  const { chOnline, chMkt, mtd, pctTarget } = row;
  const parts = [];
  const tOnline = ct.online || {};
  if (chOnline && tOnline.conversion != null && chOnline.convRate != null && chOnline.convRate < tOnline.conversion) {
    parts.push(`Online conversion below target (${fmtPct(chOnline.convRate)} vs ${(tOnline.conversion * 100).toFixed(1)}%) — good traffic but weak conversion.`);
  }
  if (mtd && mtd.roas != null && mtd.roas >= 8) {
    parts.push(`ROAS ${mtd.roas.toFixed(1)} high — consider scaling ad.`);
  }
  if (ct.marketplace && ct.marketplace.revenue) {
    const mktTotal = (chMkt ? chMkt.gross : 0) + (row.mkpRev || 0);   // eBay/WM (raw_daily) + Amazon (tab)
    if (mktTotal > 0) {
      const p = mktTotal / ct.marketplace.revenue;
      if (p < 1) parts.push(`Marketplace below target ${Math.round((1 - p) * 100)}%.`);
    }
  }
  if (parts.length === 0) return "All channels within target range.";
  return parts.join(" ");
}


/* ================= EMAIL REPORT ================= */
function EmailReport({ data, selectedBU }) {
  const em = data.email && data.email[selectedBU];
  if (!em || em.hasData === false) {
    return (
      <div className="noData">
        <div className="noDataTitle">No email data</div>
        <div className="noDataHint">Create tab <code>email_crm</code> in sheet (CRM/Email MASTER file) to view report.</div>
      </div>
    );
  }
  const kpi = em.kpi || {};
  const rc = em.revChannel || {};
  const weekly = em.weekly || { rows: [] };
  const camps = em.campaigns || [];
  const deliver = em.deliverability || [];
  const lh = em.listHealth || {};
  const cust = em.customers || {};

  // helper KPI card với target
  const kpiCard = (rec, fmt, lowerBetter) => {
    if (!rec) return null;
    const v = rec.value;
    const pct = rec.vsTarget;
    let tone = "muted";
    if (pct != null) tone = pct >= 1 ? "pos" : pct >= 0.8 ? "warn" : "neg";
    return (
      <div className="emKpi">
        <div className="emKpiLabel">{shortLabel(rec.label)}</div>
        <div className="emKpiVal">{v == null ? "—" : fmt(v)}</div>
        {pct != null && rec.target != null
          ? <div className={"emKpiTgt " + tone}>{Math.round(pct * 100)}% / {fmt(rec.target)}</div>
          : <div className="emKpiTgt muted">{rec.target != null ? "target " + fmt(rec.target) : ""}</div>}
      </div>
    );
  };

  // build insights rule-based (khối G)
  const insights = buildEmailInsights(em);

  const weekCols = maxWeeks(weekly.rows);

  return (
    <>
      {/* ① Executive Summary */}
      <SectionLabel>① Executive Summary</SectionLabel>
      <div className="emKpiRow">
        {kpiCard(kpi.gmv, fmtUSD)}
        {kpiCard(kpi.repeatPurchase, fmtPct)}
        {kpiCard(kpi.revPerRecipient, (x) => "$" + x.toFixed(2))}
        {kpi.engagedSubs && (
          <div className="emKpi">
            <div className="emKpiLabel">Engaged Subs 30D</div>
            <div className="emKpiVal">{kpi.engagedSubs.value == null ? "—" : fmtNum(kpi.engagedSubs.value)}</div>
            <div className="emKpiTgt muted">{lh.engagedNetChange != null ? (lh.engagedNetChange >= 0 ? "+" : "") + fmtNum(lh.engagedNetChange) : "reachable"}</div>
          </div>
        )}
        {kpi.newToReturn && (
          <div className="emKpi">
            <div className="emKpiLabel">New → Return</div>
            <div className="emKpiVal">{kpi.newToReturn.value == null ? "—" : fmtPct(kpi.newToReturn.value)}</div>
            <div className="emKpiTgt muted">2nd-purchase</div>
          </div>
        )}
      </div>

      {/* ② + ③ */}
      <div className="emTwoCol">
        <div>
          <SectionLabel>② Revenue: Campaign vs Flow</SectionLabel>
          <div className="emCard">
            {rc.campaign && (
              <div className="emBar">
                <div className="emBarTop"><span>Campaign</span><span className="onlineC">{fmtUSD(rc.campaign.rev)} · {fmtPct(rc.campaign.share)}</span></div>
                <div className="emBarTrack online"><div style={{ width: fmtPctWidth(rc.campaign.share) }} className="emBarFill online" /></div>
              </div>
            )}
            {rc.flow && (
              <div className="emBar">
                <div className="emBarTop"><span>Flow</span><span className="mktC">{fmtUSD(rc.flow.rev)} · {fmtPct(rc.flow.share)}</span></div>
                <div className="emBarTrack mkt"><div style={{ width: fmtPctWidth(rc.flow.share) }} className="emBarFill mkt" /></div>
              </div>
            )}
            {rc.total && (
              <div className="emTotal"><span>Total attributed</span><strong>{fmtUSD(rc.total.rev)}</strong></div>
            )}
          </div>
        </div>
        <div>
          <SectionLabel>③ Weekly trend (GMV)</SectionLabel>
          <div className="emCard">
            <table className="dataTable compact">
              <thead><tr><th>Site</th>{Array.from({ length: weekCols }, (_, i) => <th key={i} className="r">W{i + 1}</th>)}<th className="r">MTD</th></tr></thead>
              <tbody>
                {weekly.rows.filter((row) => !/total/i.test(row.label)).map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    {Array.from({ length: weekCols }, (_, w) => <td key={w} className="r">{row.weeks[w] == null ? "—" : fmtUSD(row.weeks[w])}</td>)}
                    <td className="r posT">{row.mtd == null ? "—" : fmtUSD(row.mtd)}</td>
                  </tr>
                ))}
                {weekly.rows.filter((row) => /total/i.test(row.label)).map((row, i) => (
                  <tr key={"t" + i} className="totalRow">
                    <td>Total</td>
                    {Array.from({ length: weekCols }, (_, w) => <td key={w} className="r">{row.weeks[w] == null ? "—" : fmtUSD(row.weeks[w])}</td>)}
                    <td className="r">{row.mtd == null ? "—" : fmtUSD(row.mtd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ④ Campaign Performance */}
      <SectionLabel>④ Campaign Performance</SectionLabel>
      <div className="emCard">
        <table className="dataTable compact">
          <thead><tr><th>Campaign</th><th>Site</th><th className="r">Revenue</th><th className="r">Session</th><th className="r">Orders</th><th className="r">% Rev</th></tr></thead>
          <tbody>
            {camps.map((c, i) => (
              <tr key={i}>
                <td className="nameCol">{c.name}</td>
                <td><span className={"emSite " + siteClass(c.site)}>{c.site}</span></td>
                <td className="r">{fmtUSD(c.revenue)}</td>
                <td className="r">{c.session == null ? "—" : fmtNum(c.session)}</td>
                <td className="r">{c.orders == null ? "—" : fmtNum(c.orders)}</td>
                <td className="r">{c.pctRev == null ? "—" : fmtPct(c.pctRev)}</td>
              </tr>
            ))}
            {em.campaignTotal && (
              <tr className="totalRow">
                <td>TOTAL</td><td></td>
                <td className="r">{fmtUSD(em.campaignTotal.revenue)}</td><td></td>
                <td className="r">{fmtNum(em.campaignTotal.orders)}</td><td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ⑤ + ⑥ */}
      <div className="emTwoCol">
        <div>
          <SectionLabel>⑤ Deliverability & List Health</SectionLabel>
          <div className="emCard">
            <table className="dataTable compact">
              <thead><tr><th>Metric</th><th className="r">MTD</th><th className="r">Benchmark</th><th className="c">Status</th></tr></thead>
              <tbody>
                {deliver.map((d, i) => {
                  const poor = /poor|🔴/i.test(d.status);
                  return (
                    <tr key={i} className={poor ? "warnRow" : ""}>
                      <td>{d.metric}</td>
                      <td className={"r" + (poor ? " negT" : "")}>{d.mtd == null ? "—" : fmtPct(d.mtd)}</td>
                      <td className="r muted2">{d.benchmark == null ? "—" : fmtPct(d.benchmark)}</td>
                      <td className="c">{statusIcon(d.status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(lh.totalProfiles != null || lh.newSubs != null) && (
              <div className="emListHealth">
                {lh.totalProfiles != null && <span>Profiles: <strong>{fmtNum(lh.totalProfiles)}</strong></span>}
                {lh.newSubs != null && <span>New subs: <strong>{fmtNum(lh.newSubs)}</strong></span>}
                {lh.engaged30d != null && <span>Engaged 30D: <strong>{fmtNum(lh.engaged30d)}</strong></span>}
              </div>
            )}
          </div>
        </div>
        <div>
          <SectionLabel>⑥ Customers & Retention</SectionLabel>
          <div className="emCard">
            <div className="emCustRow">
              {cust.newCust && <div className="emCustBox"><div className="emCustLabel">New</div><div className="emCustVal">{fmtNum(cust.newCust.value)}</div><div className="emCustPct">{cust.newCust.pct == null ? "" : fmtPct(cust.newCust.pct)}</div></div>}
              {cust.returnCust && <div className="emCustBox"><div className="emCustLabel">Returning</div><div className="emCustVal">{fmtNum(cust.returnCust.value)}</div><div className="emCustPct">{cust.returnCust.pct == null ? "" : fmtPct(cust.returnCust.pct)}</div></div>}
            </div>
            {(cust.discountNew || cust.discountReturn) && (
              <div className="emDiscount">
                {cust.discountNew && <div>Discount → New: <span className="negT">{fmtUSD(cust.discountNew.value)}{cust.discountNew.pct != null ? " (" + fmtPct(cust.discountNew.pct) + ")" : ""}</span></div>}
                {cust.discountReturn && <div>Discount → Return: <span>{fmtUSD(cust.discountReturn.value)}{cust.discountReturn.pct != null ? " (" + fmtPct(cust.discountReturn.pct) + ")" : ""}</span></div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ⑦ (G) Insights rule-based */}
      <SectionLabel>⑦ Insights & Actions <span className="iTag rule">rule</span></SectionLabel>
      <div className="emCard">
        {insights.length === 0 ? (
          <div className="insightBody muted">Not enough data to generate insights.</div>
        ) : (
          insights.map((ins, i) => (
            <div key={i} className="emInsight">
              <span className={"dot " + ins.tone} />
              <span>{ins.text}</span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// nhận định rule-based cho email
function buildEmailInsights(em) {
  const out = [];
  const kpi = em.kpi || {}, rc = em.revChannel || {}, deliver = em.deliverability || [], cust = em.customers || {};
  // bounce/spam poor
  deliver.forEach((d) => {
    if (/poor|🔴/i.test(d.status)) {
      out.push({ tone: "neg", text: `${d.metric} ${d.mtd != null ? fmtPct(d.mtd) : ""} exceeds benchmark ${d.benchmark != null ? fmtPct(d.benchmark) : ""} — needs action (check list, verify domain, remove bad emails).` });
    }
  });
  // GMV vs target
  if (kpi.gmv && kpi.gmv.vsTarget != null && kpi.gmv.vsTarget < 0.5) {
    out.push({ tone: "warn", text: `Email GMV only reached ${Math.round(kpi.gmv.vsTarget * 100)}% of monthly target — increase send frequency or improve offer.` });
  }
  // Campaign >> Flow → flow yếu
  if (rc.campaign && rc.flow && rc.flow.share != null && rc.flow.share < 0.2) {
    out.push({ tone: "warn", text: `Flows only make up ${fmtPct(rc.flow.share)} of email revenue — automation (welcome, abandoned cart, post-purchase) has large headroom.` });
  }
  // Repeat purchase tốt
  if (kpi.repeatPurchase && kpi.repeatPurchase.vsTarget != null && kpi.repeatPurchase.vsTarget >= 1) {
    out.push({ tone: "pos", text: `Repeat Purchase Rate ${fmtPct(kpi.repeatPurchase.value)} exceeds target — strong returning customers, keep up post-purchase care.` });
  }
  // discount lệch về new
  if (cust.discountNew && cust.discountNew.pct != null && cust.discountNew.pct > 0.9) {
    out.push({ tone: "warn", text: `${fmtPct(cust.discountNew.pct)} of discount cost goes to new customers — consider retention offers for returning to raise LTV.` });
  }
  // open/click tốt
  const open = deliver.find((d) => /open/i.test(d.metric));
  const click = deliver.find((d) => /click/i.test(d.metric));
  if (open && click && !/poor/i.test(open.status) && !/poor/i.test(click.status)) {
    out.push({ tone: "pos", text: `Open ${open.mtd != null ? fmtPct(open.mtd) : ""} & Click ${click.mtd != null ? fmtPct(click.mtd) : ""} both above benchmark — content & subject are effective.` });
  }
  return out;
}

// helpers
function shortLabel(s) {
  return s.replace(/\s*\(.*?\)\s*/g, "").replace(/MTD/i, "").trim();
}
function maxWeeks(rows) {
  let m = 0;
  (rows || []).forEach((r) => { (r.weeks || []).forEach((w, i) => { if (w != null && i + 1 > m) m = i + 1; }); });
  return Math.max(m, 2);
}
function fmtPctWidth(x) { return Math.max(0, Math.min(100, (x || 0) * 100)) + "%"; }
function siteClass(s) {
  const l = (s || "").toLowerCase();
  if (l.indexOf("drc") >= 0 || l.indexOf("direct") >= 0) return "online";
  if (l.indexOf("hd") >= 0) return "mkt";
  return "whole";
}
function statusIcon(s) {
  if (/poor|🔴/i.test(s)) return <span className="stIcon neg">🔴</span>;
  if (/good|✅/i.test(s)) return <span className="stIcon pos">✅</span>;
  return <span className="stIcon muted">–</span>;
}


/* ================= MARKETING OVERVIEW ================= */
function MarketingOverview({ data, selectedBU }) {
  const [viewBy, setViewBy] = React.useState("channel"); // 'channel' | 'website'
  const mk = data.marketing && data.marketing[selectedBU];
  if (!mk || (mk.error) || !mk[data.month]) {
    return (
      <div className="noData">
        <div className="noDataTitle">No marketing data</div>
        <div className="noDataHint">Create tab <code>marketing_data</code> + <code>marketing_target</code> in sheet to view overview.</div>
      </div>
    );
  }
  const M = mk[data.month];
  const rows = M.rows || [];
  const targets = M.targets || {};

  // gộp theo kênh
  const byChannel = {};
  rows.forEach((r) => {
    const c = r.channel;
    if (!byChannel[c]) byChannel[c] = { channel: c, revenue: 0, sessions: 0, orders: 0 };
    byChannel[c].revenue += r.revenue || 0;
    byChannel[c].sessions += r.sessions || 0;
    byChannel[c].orders += r.orders || 0;
  });
  const channels = Object.values(byChannel).sort((a, b) => b.revenue - a.revenue);
  const totRev = channels.reduce((s, c) => s + c.revenue, 0);
  const totSess = channels.reduce((s, c) => s + c.sessions, 0);
  const totOrders = channels.reduce((s, c) => s + c.orders, 0);

  // gộp theo website
  const byWeb = {};
  rows.forEach((r) => {
    const w = r.website || "—";
    if (!byWeb[w]) byWeb[w] = { website: w, revenue: 0, sessions: 0, orders: 0 };
    byWeb[w].revenue += r.revenue || 0;
    byWeb[w].sessions += r.sessions || 0;
    byWeb[w].orders += r.orders || 0;
  });
  const websites = Object.values(byWeb).sort((a, b) => b.revenue - a.revenue);

  // ad spend/ROAS cho Paid Ads: ưu tiên THẬT (ad_spend_real, GA4 channel); else MTD-SKU
  let paidSpend = null, paidRoasFromMtd = null;
  const prod = data.product && data.product[selectedBU];
  const isCurrentMonth = prod && prod.updatedMonth && data.month === prod.updatedMonth;
  const asrMk = data.adSpendReal && data.adSpendReal[selectedBU];
  const asrMkM = (asrMk && data.month && asrMk[data.month]) ? asrMk[data.month] : null;
  if (asrMkM) {
    paidSpend = asrMkM.spend;
    paidRoasFromMtd = asrMkM.spend > 0 ? asrMkM.rev / asrMkM.spend : null;
  } else if (prod && prod.brands && isCurrentMonth) {
    const bs = pcComputeBrands(prod.brands);
    paidSpend = bs.reduce((s, b) => s + b.adSpend, 0);
    const adRev = bs.reduce((s, b) => s + b.adRev, 0);
    paidRoasFromMtd = paidSpend > 0 ? adRev / paidSpend : null;
  }
  // gross online (raw_daily) — dùng cho % ad/rev, nhất quán với Overview/Sales
  const buObj = data.bus && data.bus[selectedBU];
  const onlineRoll = buObj ? rollupChannel(buObj, "online") : null;
  const grossOnline = onlineRoll ? onlineRoll.gross : null;

  // scorecard
  const blendedRoas = paidRoasFromMtd;
  const pctAdRev = (paidSpend != null && grossOnline && grossOnline > 0) ? (paidSpend / grossOnline) * 100 : null;
  // target online (config_channel của BU) — cho ô Session + % ad/rev
  const ctBU = data.channelTargets && data.channelTargets[selectedBU];
  const ctOnline = (ctBU && ctBU[data.month] && ctBU[data.month].online) ? ctBU[data.month].online : {};
  const sessTarget = ctOnline.session != null ? ctOnline.session : null;
  const sessPct = (sessTarget && totSess > 0) ? totSess / sessTarget : null;
  const sessTone = sessPct == null ? "" : sessPct >= 1 ? "posT" : sessPct >= 0.8 ? "warnT" : "negT";
  const adRevTarget = ctOnline.pctAdRev != null ? ctOnline.pctAdRev : null; // dạng 0.04 = 4%
  const adRevOk = (adRevTarget != null && pctAdRev != null) ? (pctAdRev / 100) <= adRevTarget : null;
  const roasTarget = ctOnline.roas != null ? ctOnline.roas : null;
  const roasPct = (roasTarget && blendedRoas != null) ? blendedRoas / roasTarget : null;
  const roasTone = roasPct == null ? "warnT" : roasPct >= 1 ? "posT" : roasPct >= 0.8 ? "warnT" : "negT";

  const colorFor = (ch) => {
    const l = ch.toLowerCase();
    if (l.indexOf("paid") >= 0) return "#6E86B8";
    if (l.indexOf("email") >= 0) return "#5B9E6F";
    if (l.indexOf("organic") >= 0) return "#C79A4B";
    if (l.indexOf("direct") >= 0) return "#9A82C4";
    if (l.indexOf("social") >= 0) return "#D4687A";
    if (l.indexOf("referral") >= 0) return "#6BA8B8";
    return "#8B929C";
  };

  const insights = buildMktInsights(channels, targets, totRev, paidRoasFromMtd);

  return (
    <>
      {/* ① Scorecard */}
      <SectionLabel>① Marketing status</SectionLabel>
      <div className="mkScoreRow">
        <div className="mkScore"><div className="mkScoreLabel">Total ad spend</div><div className="mkScoreVal">{paidSpend == null ? "—" : fmtUSD(paidSpend)}</div><div className="mkScoreSub">Google+FB</div></div>
        <div className="mkScore"><div className="mkScoreLabel">Blended ROAS</div><div className={"mkScoreVal " + roasTone}>{blendedRoas == null ? "—" : blendedRoas.toFixed(1) + " ⚠"}</div><div className={"mkScoreSub " + (roasPct != null ? roasTone : "")}>{roasPct != null ? Math.round(roasPct * 100) + "% / target " + roasTarget : "paid"}</div></div>
        <div className="mkScore"><div className="mkScoreLabel">Rev from GA4</div><div className="mkScoreVal">{fmtUSD(totRev)}</div><div className="mkScoreSub">7 channels total (GA4)</div></div>
        <div className="mkScore"><div className="mkScoreLabel">% GA4/Shopify</div><div className="mkScoreVal">{(grossOnline && grossOnline > 0) ? Math.round((totRev / grossOnline) * 100) + "%" : "—"}</div><div className="mkScoreSub">of {grossOnline ? fmtUSD(grossOnline) : "—"} online</div></div>
        <div className="mkScore"><div className="mkScoreLabel">Session</div><div className="mkScoreVal">{fmtNum(totSess)}</div><div className={"mkScoreSub " + sessTone}>{sessPct != null ? Math.round(sessPct * 100) + "% / " + fmtNum(sessTarget) : "7 channels total"}</div></div>
        <div className="mkScore hl"><div className="mkScoreLabel">% ad/rev</div><div className={"mkScoreVal " + (adRevOk == null ? "" : adRevOk ? "posT" : "warnT")}>{pctAdRev == null ? "—" : pctAdRev.toFixed(1) + "%"}</div><div className="mkScoreSub">{adRevTarget != null ? "target ≤ " + (adRevTarget * 100).toFixed(1) + "%" : "spend / gross online"}</div></div>
      </div>

      {/* toggle xem theo kênh / website */}
      <div className="mkToggle">
        <span className="mkToggleLabel">View by:</span>
        <button className={viewBy === "channel" ? "mkTgBtn active" : "mkTgBtn"} onClick={() => setViewBy("channel")}>Channel</button>
        <button className={viewBy === "website" ? "mkTgBtn active" : "mkTgBtn"} onClick={() => setViewBy("website")}>Website</button>
      </div>

      {viewBy === "channel" ? (
        <>
          {/* ② Đóng góp doanh số + target */}
          <SectionLabel>② Revenue by channel</SectionLabel>
          <div className="mkCard">
            {channels.map((c) => {
              const tgt = targets[c.channel];
              const share = totRev > 0 ? c.revenue / totRev : 0;
              const pctTgt = tgt ? c.revenue / tgt : null;
              return (
                <div className="mkBarRow" key={c.channel}>
                  <div className="mkBarHead">
                    <span className="mkBarName"><span className="mkDot" style={{ background: colorFor(c.channel) }} />{c.channel}</span>
                    <span className="mkBarNums">
                      {fmtUSD(c.revenue)} · {fmtPct(share)}
                      {pctTgt != null && <span className={"mkTgtBadge " + (pctTgt >= 1 ? "pos" : pctTgt >= 0.8 ? "warn" : "neg")}>{Math.round(pctTgt * 100)}% / {fmtUSD(tgt)}</span>}
                    </span>
                  </div>
                  <div className="mkBarTrack"><div className="mkBarFill" style={{ width: Math.max(1, share * 100) + "%", background: colorFor(c.channel) }} /></div>
                </div>
              );
            })}
            <div className="mkCardFoot">Total revenue: <strong>{fmtUSD(totRev)}</strong></div>
          </div>

          {/* ③ Đóng góp traffic */}
          <SectionLabel>③ Traffic contribution (sessions)</SectionLabel>
          <div className="mkCard">
            {channels.slice().sort((a, b) => b.sessions - a.sessions).map((c) => {
              const share = totSess > 0 ? c.sessions / totSess : 0;
              return (
                <div className="mkBarRow" key={c.channel}>
                  <div className="mkBarHead">
                    <span className="mkBarName"><span className="mkDot" style={{ background: colorFor(c.channel) }} />{c.channel}</span>
                    <span className="mkBarNums">{fmtNum(c.sessions)} · {fmtPct(share)}</span>
                  </div>
                  <div className="mkBarTrack"><div className="mkBarFill" style={{ width: Math.max(1, share * 100) + "%", background: colorFor(c.channel) }} /></div>
                </div>
              );
            })}
            <div className="mkCardFoot">Total sessions: <strong>{fmtNum(totSess)}</strong></div>
          </div>

          {/* ④ Channel performance */}
          <SectionLabel>④ Channel performance</SectionLabel>
          <div className="mkCard">
            <table className="dataTable compact">
              <thead><tr><th>Channel</th><th className="r">Spend</th><th className="r">Revenue</th><th className="r">Sessions</th><th className="r">Orders</th><th className="r">ROAS</th><th className="r">Conv</th></tr></thead>
              <tbody>
                {channels.map((c) => {
                  const isPaid = c.channel.toLowerCase().indexOf("paid") >= 0;
                  const spend = isPaid ? paidSpend : null;
                  const roas = isPaid ? paidRoasFromMtd : null;
                  const conv = c.sessions > 0 ? c.orders / c.sessions : null;
                  return (
                    <tr key={c.channel}>
                      <td><span className="mkDot" style={{ background: colorFor(c.channel) }} />{c.channel}</td>
                      <td className="r">{spend == null ? "—" : fmtUSD(spend)}</td>
                      <td className="r">{fmtUSD(c.revenue)}</td>
                      <td className="r">{fmtNum(c.sessions)}</td>
                      <td className="r">{fmtNum(c.orders)}</td>
                      <td className={"r" + (roas != null ? " posT" : "")}>{roas == null ? "—" : roas.toFixed(1)}</td>
                      <td className="r">{conv == null ? "—" : fmtPct(conv)}</td>
                    </tr>
                  );
                })}
                <tr className="totalRow">
                  <td>Total marketing</td>
                  <td className="r">{paidSpend == null ? "—" : fmtUSD(paidSpend)}</td>
                  <td className="r">{fmtUSD(totRev)}</td>
                  <td className="r">{fmtNum(totSess)}</td>
                  <td className="r">{fmtNum(totOrders)}</td>
                  <td className="r">—</td><td className="r"></td>
                </tr>
              </tbody>
            </table>
            {SHOW_SOURCE_NOTES && <div className="mkNote">ROAS is for Paid Ads only (Google+FB spend from MTD). Other channels have no ad spend.</div>}
          </div>
        </>
      ) : (
        <>
          {/* Breakdown theo website: stacked bar + thẻ nhóm */}
          <SectionLabel>② Revenue by website</SectionLabel>
          <div className="mkCard">
            <div className="mkStackLegend">
              {["Paid Ads","Organic Search","Email","Direct","Social","Referral","Others"].map((c) => (
                <span key={c} className="mkLegItem"><span className="mkDot" style={{ background: colorFor(c) }} />{c}</span>
              ))}
            </div>
            {websites.map((w) => {
              const wRows = rows.filter((r) => (r.website || "—") === w.website);
              const byCh = {};
              wRows.forEach((r) => { byCh[r.channel] = (byCh[r.channel] || 0) + (r.revenue || 0); });
              const segs = Object.entries(byCh).map(([ch, rev]) => ({ ch, rev, pct: w.revenue > 0 ? rev / w.revenue : 0 }))
                .sort((a, b) => b.rev - a.rev);
              return (
                <div className="mkStackRow" key={w.website}>
                  <div className="mkStackHead">
                    <span className="mkStackName">{w.website}</span>
                    <span className="mkStackTot">{fmtUSD(w.revenue)}</span>
                  </div>
                  <div className="mkStackBar">
                    {segs.map((s) => (
                      <div key={s.ch} className="mkStackSeg" style={{ width: (s.pct * 100) + "%", background: colorFor(s.ch) }}
                        title={s.ch + ": " + fmtUSD(s.rev) + " (" + fmtPct(s.pct) + ")"}>
                        {s.pct >= 0.12 ? Math.round(s.pct * 100) + "%" : ""}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Thẻ nhóm mỗi website */}
          <SectionLabel>③ Website detail</SectionLabel>
          <div className="mkWebGrid">
            {websites.map((w) => {
              const wRows = rows.filter((r) => (r.website || "—") === w.website);
              const byCh = {};
              wRows.forEach((r) => {
                if (!byCh[r.channel]) byCh[r.channel] = { rev: 0, sess: 0, ord: 0 };
                byCh[r.channel].rev += r.revenue || 0;
                byCh[r.channel].sess += r.sessions || 0;
                byCh[r.channel].ord += r.orders || 0;
              });
              const chList = Object.entries(byCh).map(([ch, v]) => ({ ch, ...v })).sort((a, b) => b.rev - a.rev);
              const top = chList.slice(0, 4);
              const rest = chList.slice(4);
              const restRev = rest.reduce((s, x) => s + x.rev, 0);
              return (
                <div className="mkWebCard" key={w.website}>
                  <div className="mkWebHead">
                    <span className="mkWebName">{w.website}</span>
                    <span className="mkWebTot">{fmtUSD(w.revenue)}</span>
                  </div>
                  <div className="mkWebRows">
                    {top.map((c) => (
                      <div className="mkWebLine" key={c.ch}>
                        <span className="mkWebCh"><span className="mkDot" style={{ background: colorFor(c.ch) }} />{c.ch}</span>
                        <span className="mkWebNum">{fmtUSD(c.rev)} · {w.revenue > 0 ? fmtPct(c.rev / w.revenue) : "—"}</span>
                      </div>
                    ))}
                    {rest.length > 0 && (
                      <div className="mkWebLine muted">
                        <span>+ {rest.length} more channels</span>
                        <span>{fmtUSD(restRev)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ⑤ Recommendations */}
      <SectionLabel>⑤ Customer <span className="iTag rule">online · Shopify · new = no prior purchase before this month</span></SectionLabel>
      {(() => {
        const cAll = data.customer && data.customer[selectedBU];
        const c = cAll && data.month ? cAll[data.month] : null;
        if (!c || !c.customers) return (
          <div className="mkCard"><div className="insightBody muted">No customer data this month — deploy the new .gs then run rebuildCurrentMonth to populate customer_monthly.</div></div>
        );
        const pctNew = c.customers > 0 ? (c.newC / c.customers) * 100 : 0;
        const pctRet = c.customers > 0 ? (c.retC / c.customers) * 100 : 0;
        const totRevC = (c.revN || 0) + (c.revR || 0);
        const pctRevRet = totRevC > 0 ? (c.revR / totRevC) * 100 : null;
        const aovNew = c.ordN > 0 ? c.revN / c.ordN : null;
        const aovRet = c.ordR > 0 ? c.revR / c.ordR : null;
        const ordPerCust = c.customers > 0 ? (c.ordN + c.ordR) / c.customers : null;
        return (
          <div className="kpiGrid" style={{ marginBottom: 14 }}>
            <PCKpi label="Customers" value={fmtNum(c.customers)} sub="distinct · this month" />
            <PCKpi label="New customers" value={fmtNum(c.newC)} sub={pctNew.toFixed(0) + "% of customers"} />
            <PCKpi label="Returning customers" value={fmtNum(c.retC)} sub={"repeat rate " + pctRet.toFixed(0) + "%"} tone={pctRet >= 25 ? "good" : undefined} />
            <PCKpi label="Rev from returning" value={pctRevRet == null ? "—" : pctRevRet.toFixed(0) + "%"} sub={`new ${pcMoney(c.revN)} · returning ${pcMoney(c.revR)}`} />
            <PCKpi label="AOV new vs returning" value={aovNew != null ? pcMoney(aovNew) : "—"} sub={aovRet != null ? "returning " + pcMoney(aovRet) : "—"} />
            <PCKpi label="Orders/customer" value={ordPerCust != null ? ordPerCust.toFixed(2) : "—"} sub={fmtNum(c.ordN + c.ordR) + " online orders"} />
          </div>
        );
      })()}

      <SectionLabel>⑥ Recommendations <span className="iTag rule">rule</span></SectionLabel>
      <div className="mkCard">
        {insights.length === 0 ? (
          <div className="insightBody muted">Not enough data to generate insights.</div>
        ) : insights.map((ins, i) => (
          <div key={i} className="mkInsight"><span className={"dot " + ins.tone} /><span>{ins.text}</span></div>
        ))}
      </div>
    </>
  );
}

function buildMktInsights(channels, targets, totRev, paidRoas) {
  const out = [];
  const byCh = {};
  channels.forEach((c) => { byCh[c.channel] = c; });
  // kênh đóng góp doanh số cao nhất
  if (channels.length) {
    const top = channels[0];
    out.push({ tone: "pos", text: `${top.channel} contributes the most revenue (${fmtUSD(top.revenue)}, ${totRev > 0 ? fmtPct(top.revenue / totRev) : ""}).` });
  }
  // kênh traffic cao nhưng doanh số thấp (chuyển đổi kém)
  const bySess = channels.slice().sort((a, b) => b.sessions - a.sessions);
  if (bySess.length && totRev > 0) {
    const topSess = bySess[0];
    const revShare = topSess.revenue / totRev;
    const totSess = channels.reduce((s, c) => s + c.sessions, 0);
    const sessShare = totSess > 0 ? topSess.sessions / totSess : 0;
    if (sessShare - revShare > 0.1) {
      out.push({ tone: "warn", text: `${topSess.channel} brings ${fmtPct(sessShare)} of traffic but only ${fmtPct(revShare)} of revenue — low conversion, needs improvement.` });
    }
  }
  // kênh dưới target nhiều nhất
  let worstGap = null;
  channels.forEach((c) => {
    const t = targets[c.channel];
    if (t && t > 0) {
      const gap = c.revenue / t;
      if (gap < 0.8 && (worstGap == null || gap < worstGap.gap)) worstGap = { channel: c.channel, gap, target: t, rev: c.revenue };
    }
  });
  if (worstGap) {
    out.push({ tone: "warn", text: `${worstGap.channel} only reached ${Math.round(worstGap.gap * 100)}% of target (${fmtUSD(worstGap.rev)}/${fmtUSD(worstGap.target)}) — needs strong push.` });
  }
  // email efficient chi phí
  if (byCh["Email"] && totRev > 0 && byCh["Email"].revenue / totRev > 0.1) {
    out.push({ tone: "pos", text: `Email contributes ${fmtPct(byCh["Email"].revenue / totRev)} of revenue with no ad spend — cost-efficient channel.` });
  }
  // paid ROAS
  if (paidRoas != null && paidRoas >= 8) {
    out.push({ tone: "pos", text: `Paid Ads ROAS ${paidRoas.toFixed(1)} high — room to increase budget.` });
  }
  return out;
}


/* ================= INVENTORY REPORT — BẢN HELI =================
   Đọc product.inventory kiểu Heli (velocity/coverDay/health — cover tự tính từ Daily sales).
   5 mục như Car + bảng chi tiết bổ sung cột Heli + search SKU/brand.
   Car/Jet vẫn dùng InventoryReport cũ; Detail chọn theo BU. */
function InventoryReportHeli({ data, selectedBU }) {
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);
  const [flt, setFlt] = React.useState("all");

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.inventory || !prod.inventory.length) return <PCNoData bu={selectedBU} what="Inventory" />;

  const inv = prod.inventory;

  // ① KPI
  const totVal = inv.reduce((s, i) => s + (i.stockVal || 0), 0);
  const lowCnt = inv.filter((i) => /low/i.test(i.health || "")).length;
  const oosCnt = inv.filter((i) => i.stock === 0).length;
  const overCnt = inv.filter((i) => /over|high cover/i.test(i.health || "")).length;
  const onOrderCnt = inv.filter((i) => (i.onOrder || 0) > 0).length;
  const oversell = inv.filter((i) => (i.stock || 0) < 0).length;

  // ② brand
  const brandMap = {};
  inv.forEach((i) => { brandMap[i.brand] = (brandMap[i.brand] || 0) + (i.stockVal || 0); });
  const byBrand = Object.keys(brandMap).map((b) => ({ brand: b, stockVal: brandMap[b] }))
    .sort((a, b) => b.stockVal - a.stockVal);
  const maxBrandVal = byBrand[0] ? byBrand[0].stockVal : 1;

  // ③ vốn kẹt: tồn có giá trị + (không bán 30 ngày HOẶC cover ≥ 180)
  const stuck = inv.filter((i) => (i.stockVal || 0) > 0 && ((i.sales30 || 0) === 0 || (i.coverDay || 0) >= 180))
    .sort((a, b) => (b.stockVal || 0) - (a.stockVal || 0));
  const stuckVal = stuck.reduce((s, i) => s + (i.stockVal || 0), 0);

  // ④ flag + filter + search
  function invFlag(i) {
    if ((i.stock || 0) < 0) return "oversell";
    if (/low/i.test(i.health || "")) return "urgent";
    if (/over|high cover/i.test(i.health || "")) return "overstock";
    if (/healthy/i.test(i.health || "")) return "healthy";
    return "";
  }
  const tableRows = inv.filter((i) => {
    if (flt !== "all") { if (invFlag(i) !== flt) return false; }
    if (q) {
      const s = q.toLowerCase();
      if (!((i.sku || "").toLowerCase().includes(s) || (i.brand || "").toLowerCase().includes(s) || (i.title || "").toLowerCase().includes(s))) return false;
    }
    return true;
  });
  const sorted = tableRows.slice().sort((a, b) =>
    flt === "urgent" ? ((a.coverDay == null ? 1e9 : a.coverDay) - (b.coverDay == null ? 1e9 : b.coverDay)) : (b.stockVal || 0) - (a.stockVal || 0));
  const pageSize = 15;
  const pageRows = sorted.slice(pg * pageSize, pg * pageSize + pageSize);

  // ⑤ gợi ý rule-based — cover < leadtime = rủi ro hết hàng trước khi hàng mới về
  const urgentList = inv.filter((i) => /low/i.test(i.health || "") && i.coverDay != null && i.coverDay < (i.leadtime || 45));
  const urgentNoOrder = urgentList.filter((i) => (i.onOrder || 0) <= 0);
  const overStuck = inv.filter((i) => (i.coverDay || 0) >= 180 && (i.stockVal || 0) > 0);
  const overStuckVal = overStuck.reduce((s, i) => s + (i.stockVal || 0), 0);

  const healthCell = (h) => {
    if (/low/i.test(h || "")) return <span className="hlLow">{h}</span>;
    if (/over|high cover/i.test(h || "")) return <span className="hlOver">{h}</span>;
    if (/healthy/i.test(h || "")) return <span className="hlOk">{h}</span>;
    return <span className="muted">{h || "—"}</span>;
  };

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Inventory
          <span className="repTag">velocity · cover · health</span></div>
      </div>

      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Inventory value" value={pcMoney(totVal)} sub="stock × cost" />
        <PCKpi label="Low Stock" value={pcNum(lowCnt)} tone="warn" />
        <PCKpi label="Out of stock" value={pcNum(oosCnt)} tone="warn" />
        <PCKpi label="Overstock" value={pcNum(overCnt)} sub="incl. High Cover" />
        <PCKpi label="On order" value={pcNum(onOrderCnt)} tone="good" />
        <PCKpi label="Oversell" value={pcNum(oversell)} tone="warn" />
      </div>

      <SectionLabel>② Stock capital by brand</SectionLabel>
      <div className="panel">
        {byBrand.slice(0, 7).map((b) => (
          <div className="pcBarRow" key={b.brand}>
            <div className="pcBarTop"><span>{b.brand}</span>
              <span className="muted">{pcMoney(b.stockVal)} · {pcRound(pcPct(b.stockVal, totVal), 0)}%</span></div>
            <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(b.stockVal, maxBrandVal) + "%" }} /></div>
          </div>
        ))}
      </div>

      <SectionLabel>③ Locked capital — high stock, slow sales (no sale 30 days or cover ≥ 180)</SectionLabel>
      <div className="panel">
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Brand</th><th>Stock capital</th><th>Velocity/day</th><th>Cover days</th><th>Stock</th></tr></thead>
          <tbody>
            {stuck.slice(0, 12).map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.brand}</td>
                <td className="warnT">{pcMoney(i.stockVal)}</td>
                <td>{(i.velocity || 0).toFixed(2)}</td>
                <td className={(i.coverDay || 0) >= 180 ? "warnT" : ""}>{pcNum(i.coverDay)}</td>
                <td>{i.stock}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">{stuck.length} stuck SKUs · total {pcMoney(stuckVal)} — showing top 12 by stock capital</div>
      </div>

      <SectionLabel>④ Inventory detail table</SectionLabel>
      <div className="panel">
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search SKU / brand / name" value={q}
            onChange={(e) => { setQ(e.target.value); setPg(0); }} />
          <div className="chipRow">
            {[["all","All"],["urgent","🔴 Reorder urgently"],["overstock","⚠ Overstock"],["healthy","✓ Healthy"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (flt === k ? "chipOn" : "")} onClick={() => { setFlt(k); setPg(0); }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Name</th><th>Stock</th><th>Velocity/day</th><th>Cover days</th><th>On order</th><th className="l">Health</th></tr></thead>
          <tbody>
            {pageRows.map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.title || i.brand}</td>
                <td className={(i.stock || 0) < 0 ? "bad" : ""}>{i.stock}</td>
                <td>{(i.velocity || 0).toFixed(2)}</td>
                <td>{pcNum(i.coverDay)}</td>
                <td>{i.onOrder || 0}</td>
                <td className="l">{healthCell(i.health)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">{tableRows.length} SKUs · filtered from {inv.length}</div>
        <Pager pg={pg} setPg={setPg} total={tableRows.length} pageSize={pageSize} />
      </div>

      <SectionLabel>⑤ Recommendations <span className="iTag rule">rule</span></SectionLabel>
      <div className="panel">
        {urgentList.length > 0 && (
          <div className="mkInsight"><span className="dot neg" />
            <span>{urgentList.length} Low Stock SKUs with cover shorter than leadtime — risk of stockout before restock arrives{urgentNoOrder.length > 0 ? `, of which ${urgentNoOrder.length} SKUs have no On order — reorder urgently` : ""}.</span></div>
        )}
        {overStuck.length > 0 && (
          <div className="mkInsight"><span className="dot warn" />
            <span>{overStuck.length} SKUs with cover ≥ 180 days — {pcMoney(overStuckVal)} locked capital, consider liquidating / stop restocking.</span></div>
        )}
        {urgentList.length === 0 && overStuck.length === 0 && (
          <div className="insightBody muted">Inventory healthy — no alerts.</div>
        )}
      </div>
    </div>
  );
}


/* ================= INVENTORY REPORT — BẢN JET (tối giản) =================
   Tab Inventory Jet chỉ có 13 cột — không velocity/cover/reorder.
   item: { sku,title,brand,cat,stock(null=#N/A),cost,daysOfStock,leadtime,replenish,disc,stockVal }
   5 mục: KPI · vốn theo brand · top vốn tồn · bảng chi tiết · gợi ý. */
function InventoryReportJet({ data, selectedBU }) {
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);
  const [flt, setFlt] = React.useState("all");

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.inventory || !prod.inventory.length) return <PCNoData bu={selectedBU} what="Inventory" />;

  const inv = prod.inventory;
  const isActive = (i) => /active/i.test(i.disc || "");
  const isOff = (i) => /off/i.test(i.disc || "");
  const isErr = (i) => i.stock === null || /not found/i.test(i.replenish || "");

  // ① KPI
  const totVal = inv.reduce((s, i) => s + (i.stockVal || 0), 0);
  const activeCnt = inv.filter(isActive).length;
  const oosCnt = inv.filter((i) => i.stock === 0 && isActive(i)).length;
  const oversellCnt = inv.filter((i) => i.stock != null && i.stock < 0).length;
  const offStock = inv.filter((i) => isOff(i) && (i.stock || 0) > 0);
  const offStockVal = offStock.reduce((s, i) => s + (i.stockVal || 0), 0);
  const errCnt = inv.filter(isErr).length;

  // ② brand
  const brandMap = {};
  inv.forEach((i) => { brandMap[i.brand] = (brandMap[i.brand] || 0) + (i.stockVal || 0); });
  const byBrand = Object.keys(brandMap).map((b) => ({ brand: b, stockVal: brandMap[b] }))
    .sort((a, b) => b.stockVal - a.stockVal);
  const maxBrandVal = byBrand[0] ? byBrand[0].stockVal : 1;

  // ③ top vốn tồn
  const topVal = inv.filter((i) => (i.stockVal || 0) > 0).sort((a, b) => (b.stockVal || 0) - (a.stockVal || 0));

  // ④ flag + filter + search
  function jFlag(i) {
    if (isErr(i)) return "dataerr";
    if (i.stock != null && i.stock < 0) return "oversell";
    if (i.stock === 0 && isActive(i)) return "oos";
    if (isOff(i) && (i.stock || 0) > 0) return "off";
    return "";
  }
  const tableRows = inv.filter((i) => {
    if (flt !== "all" && jFlag(i) !== flt) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!((i.sku || "").toLowerCase().includes(s) || (i.brand || "").toLowerCase().includes(s) || (i.title || "").toLowerCase().includes(s))) return false;
    }
    return true;
  });
  const sorted = tableRows.slice().sort((a, b) => (b.stockVal || 0) - (a.stockVal || 0));
  const pageSize = 15;
  const pageRows = sorted.slice(pg * pageSize, pg * pageSize + pageSize);

  // ⑤ gợi ý
  const needRestock = inv.filter((i) => i.stock === 0 && isActive(i) && /yes/i.test(i.replenish || ""));

  const statusCell = (i) => isOff(i)
    ? <span className="hlOver">off</span>
    : isActive(i) ? <span className="hlOk">active</span> : <span className="muted">{i.disc || "—"}</span>;
  const replCell = (r) => /yes/i.test(r || "")
    ? <span className="posT">YES</span>
    : /not found/i.test(r || "") ? <span className="warnT">not found</span> : <span className="muted">{r || "—"}</span>;
  const stockCell = (i) => i.stock === null
    ? <span className="negT">#N/A</span>
    : <span className={i.stock < 0 ? "bad" : i.stock === 0 && isActive(i) ? "negT" : ""}>{i.stock}</span>;

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Inventory
          {SHOW_SOURCE_NOTES && <span className="repTag">minimal version · no velocity/reorder yet</span>}</div>
      </div>

      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Inventory value" value={pcMoney(totVal)} sub="stock × cost" />
        <PCKpi label="SKUs selling" value={pcNum(activeCnt)} sub="active" />
        <PCKpi label="Out of stock" value={pcNum(oosCnt)} sub="stock 0, still active" tone="warn" />
        <PCKpi label="Oversell" value={pcNum(oversellCnt)} tone="warn" />
        <PCKpi label="Discontinued w/ stock" value={pcNum(offStock.length)} sub={offStockVal > 0 ? pcMoney(offStockVal) : "—"} tone="warn" />
        <PCKpi label="Data error" value={pcNum(errCnt)} sub="not found / #N/A" tone="warn" />
      </div>

      <SectionLabel>② Stock capital by brand</SectionLabel>
      <div className="panel">
        {byBrand.slice(0, 7).map((b) => (
          <div className="pcBarRow" key={b.brand}>
            <div className="pcBarTop"><span>{b.brand}</span>
              <span className="muted">{pcMoney(b.stockVal)} · {pcRound(pcPct(b.stockVal, totVal), 0)}%</span></div>
            <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(b.stockVal, maxBrandVal) + "%" }} /></div>
          </div>
        ))}
      </div>

      <SectionLabel>③ Top SKUs by stock capital — where the money sits</SectionLabel>
      <div className="panel">
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Brand</th><th>Stock</th><th>Cost</th><th>Stock capital</th></tr></thead>
          <tbody>
            {topVal.slice(0, 12).map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.brand}</td>
                <td>{i.stock}</td>
                <td>{pcMoney(i.cost)}</td>
                <td className="warnT">{pcMoney(i.stockVal)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">top 12 by stock capital · total inventory {pcMoney(totVal)}</div>
      </div>

      <SectionLabel>④ Inventory detail table</SectionLabel>
      <div className="panel">
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search SKU / brand / name" value={q}
            onChange={(e) => { setQ(e.target.value); setPg(0); }} />
          <div className="chipRow">
            {[["all","All"],["oos","🔴 Out of stock"],["oversell","🔴 Oversell"],["off","⚠ Discontinued (off)"],["dataerr","⚠ Data error"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (flt === k ? "chipOn" : "")} onClick={() => { setFlt(k); setPg(0); }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Name</th><th className="l">Brand</th><th>Stock</th><th>Cost</th><th>Stock capital</th><th>Days of stock</th><th>Leadtime</th><th>Replenish</th><th>Status</th></tr></thead>
          <tbody>
            {pageRows.map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.title || "—"}</td>
                <td className="l muted">{i.brand}</td>
                <td>{stockCell(i)}</td>
                <td>{pcMoney(i.cost)}</td>
                <td>{i.stockVal > 0 ? pcMoney(i.stockVal) : "—"}</td>
                <td>{pcNum(i.daysOfStock)}</td>
                <td>{pcNum(i.leadtime)}</td>
                <td>{replCell(i.replenish)}</td>
                <td>{statusCell(i)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">{tableRows.length} SKUs · filtered from {inv.length}</div>
        <Pager pg={pg} setPg={setPg} total={tableRows.length} pageSize={pageSize} />
      </div>

      <SectionLabel>⑤ Recommendations <span className="iTag rule">rule</span></SectionLabel>
      <div className="panel">
        {needRestock.length > 0 && (
          <div className="mkInsight"><span className="dot neg" />
            <span>{needRestock.length} out-of-stock SKUs still selling (active + replenishment YES) — needs restock.</span></div>
        )}
        {offStock.length > 0 && (
          <div className="mkInsight"><span className="dot warn" />
            <span>{offStock.length} discontinued (off) SKUs still holding {pcMoney(offStockVal)} — consider liquidating to recover capital.</span></div>
        )}
        {errCnt > 0 && (
          <div className="mkInsight"><span className="dot warn" />
            <span>{errCnt} SKUs with data errors (not found / #N/A) — recheck lookup in Inventory sheet.</span></div>
        )}
        {needRestock.length === 0 && offStock.length === 0 && errCnt === 0 && (
          <div className="insightBody muted">Inventory healthy — no alerts.</div>
        )}
      </div>
    </div>
  );
}


/* ================= MARKETPLACE REPORT =================
   Tab 'Marketplace' (gộp Amazon+eBay+Walmart, MTD). Mỗi dòng 1 SKU.
   %: tự tính — TACOS=ads/rev · ACOS=ads/adRev · CR=units/sess ·
   %Organic=1-adsSess/sess · CTR=adsSess/impr · %Promo=promo/rev. */
function MarketplaceReport({ data, selectedBU }) {
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);
  const [flt, setFlt] = React.useState("all");
  const [showAllB, setShowAllB] = React.useState(false);   // popup toàn brand
  const [bq, setBq] = React.useState("");
  const [sortK, setSortK] = React.useState("rev");         // sort bảng SKU
  const [sortAsc, setSortAsc] = React.useState(false);

  const mkpBU = data.mkp && data.mkp[selectedBU];
  if (!mkpBU || !mkpBU.skus || !mkpBU.skus.length) return <PCNoData bu={selectedBU} what="Amazon (tab Marketplace)" />;

  const skus = mkpBU.skus;

  // ---- tổng ----
  const tot = skus.reduce((a, s) => ({
    rev: a.rev + (s.rev || 0), units: a.units + (s.units || 0), ads: a.ads + (s.ads || 0),
    adRev: a.adRev + (s.adRev || 0), sess: a.sess + (s.sess || 0), adsSess: a.adsSess + (s.adsSess || 0),
    impr: a.impr + (s.impr || 0), promo: a.promo + (s.promo || 0),
    refCase: a.refCase + (s.refCase || 0), refAmt: a.refAmt + (s.refAmt || 0),
  }), { rev: 0, units: 0, ads: 0, adRev: 0, sess: 0, adsSess: 0, impr: 0, promo: 0, refCase: 0, refAmt: 0 });
  const tacos = tot.rev > 0 ? (tot.ads / tot.rev) * 100 : null;
  const acos = tot.adRev > 0 ? (tot.ads / tot.adRev) * 100 : null;
  const ctrAll = tot.impr > 0 ? (tot.adsSess / tot.impr) * 100 : null;
  // Margin Amazon: (Rev − COGS − Ads − Refund − Fee) ÷ Rev, với Fee = AMZ_FEE_PCT × Rev (ước tính).
  // Total Revenue sheet ĐÃ trừ Promotion (user xác nhận) → KHÔNG trừ promo nữa (tránh trừ 2 lần).
  // Refund CHƯA nằm trong Rev → trừ. COGS join từ product.skus (cột Cost MTD) qua SKU; thiếu cost → loại.
  const costMap = {};
  const prodSkus = data.product && data.product[selectedBU] && data.product[selectedBU].skus;
  if (prodSkus) prodSkus.forEach((p) => { if (p.cost > 0) costMap[p.sku] = p.cost; });
  let mgNum = 0, mgDen = 0;
  skus.forEach((s) => {
    const c = costMap[s.sku];
    if (!c || !(s.rev > 0)) return;
    mgNum += s.rev - c * (s.units || 0) - (s.ads || 0) - (s.refAmt || 0) - AMZ_FEE_PCT * s.rev;
    mgDen += s.rev;
  });
  const margin = mgDen > 0 ? (mgNum / mgDen) * 100 : null;
  const mgCover = tot.rev > 0 ? (mgDen / tot.rev) * 100 : null;
  // Margin per SKU (① cột bảng + lọc margin âm) — cùng công thức thẻ Margin
  const skuMargin = (s) => {
    const c = costMap[s.sku];
    if (!c || !(s.rev > 0)) return null;
    return ((s.rev - c * (s.units || 0) - (s.ads || 0) - (s.refAmt || 0) - AMZ_FEE_PCT * s.rev) / s.rev) * 100;
  };
  // ④ Join tồn kho: SKU Amazon → stock/health từ tab Inventory
  const invMap = {};
  const invArr = (data.product && data.product[selectedBU] && data.product[selectedBU].inventory) || [];
  invArr.forEach((iv) => { if (iv.sku) invMap[iv.sku] = iv; });
  // ⑥ Concentration: top 5 SKU chiếm bao nhiêu % revenue
  const top5Rev = skus.slice().sort((a, b) => (b.rev || 0) - (a.rev || 0)).slice(0, 5).reduce((x, s) => x + (s.rev || 0), 0);
  const top5Pct = tot.rev > 0 ? (top5Rev / tot.rev) * 100 : null;
  // ② Theo loại sản phẩm (cột Type) — rev/ads/margin per category
  const catMap = {};
  skus.forEach((s) => {
    const k = s.cat || "(unknown)";
    const c2 = catMap[k] || (catMap[k] = { cat: k, rev: 0, ads: 0, mgNum: 0, mgDen: 0 });
    c2.rev += s.rev || 0; c2.ads += s.ads || 0;
    const cc = costMap[s.sku];
    if (cc && s.rev > 0) { c2.mgNum += s.rev - cc * (s.units || 0) - (s.ads || 0) - (s.refAmt || 0) - AMZ_FEE_PCT * s.rev; c2.mgDen += s.rev; }
  });
  const byCat = Object.keys(catMap).map((k) => catMap[k]).sort((a, b) => b.rev - a.rev);
  const maxCatRev = byCat[0] ? Math.max(byCat[0].rev, 1) : 1;
  // ③ Phân loại traffic × conversion (cột Classification sheet)
  const segDefs = [
    ["hiT-loC", "High Traffic · Low Conv", (s) => /high traffic/i.test(s.cls || "") && /low conv/i.test(s.cls || "")],
    ["hiT-hiC", "High Traffic · High Conv", (s) => /high traffic/i.test(s.cls || "") && /high conv/i.test(s.cls || "")],
    ["loT", "Low Traffic", (s) => /low traffic/i.test(s.cls || "")],
    ["noCls", "Unclassified", (s) => !(s.cls || "").trim()],
  ];
  const segStats = segDefs.map(([k, lbl, fn]) => {
    const list = skus.filter(fn);
    return { k, lbl, n: list.length, rev: list.reduce((x, s) => x + (s.rev || 0), 0) };
  });
  const tacosTone = tacos == null ? "" : tacos < 10 ? "posT" : tacos <= 20 ? "warnT" : "negT";

  // eBay + Walmart từ raw_daily (quy ước: raw_daily kênh marketplace = eBay+WM, Amazon ở tab này)
  const chMkt = data.bus && data.bus[selectedBU] ? rollupChannel(data.bus[selectedBU], "marketplace") : null;
  const ebayWmRev = chMkt ? chMkt.gross : null;
  const amzShare = (ebayWmRev != null && tot.rev > 0) ? (tot.rev / (tot.rev + ebayWmRev)) * 100 : null;

  // ---- brand ----
  const bMap = {};
  skus.forEach((s) => {
    const b = bMap[s.brand] || (bMap[s.brand] = { brand: s.brand, rev: 0, ads: 0, mgNum: 0, mgDen: 0 });
    b.rev += s.rev || 0; b.ads += s.ads || 0;
    const cb = costMap[s.sku];
    if (cb && s.rev > 0) { b.mgNum += s.rev - cb * (s.units || 0) - (s.ads || 0) - (s.refAmt || 0) - AMZ_FEE_PCT * s.rev; b.mgDen += s.rev; }
  });
  const byBrand = Object.keys(bMap).map((k) => bMap[k]).sort((a, b) => b.rev - a.rev);
  const topB = 8;
  const restB = byBrand.slice(topB);
  const restRev = restB.reduce((x, b) => x + b.rev, 0);
  const restAds = restB.reduce((x, b) => x + b.ads, 0);
  const maxBrandRev = Math.max(byBrand[0] ? byBrand[0].rev : 1, restRev, 1);

  // ---- filter + search ----
  const hasInvStatus = skus.some((s) => s.invStatus);
  // Predicate thống nhất — dùng cho chips, khối ④ segment, và rules ⑥
  const predMap = {
    "hiT-loC": (s) => /high traffic/i.test(s.cls || "") && /low conv/i.test(s.cls || ""),
    "hiT-hiC": (s) => /high traffic/i.test(s.cls || "") && /high conv/i.test(s.cls || ""),
    loT: (s) => /low traffic/i.test(s.cls || ""),
    noCls: (s) => !(s.cls || "").trim(),
    oos: (s) => /oos/i.test(s.invStatus || ""),
    refund: (s) => (s.refCase || 0) > 0 || (s.refAmt || 0) > 0,
    dead: (s) => /dead/i.test(s.life || ""),
    mgneg: (s) => { const m = skuMargin(s); return m != null && m < 0; },
    // Group ads — neo baseline TACOS + margin gate (xem config MKP_* đầu file)
    burnad: (s) => (s.ads || 0) >= MKP_ADS_BURN && (s.rev || 0) === 0,
    hitacos: (s) => {
      if (!((s.rev || 0) >= MKP_REV_TACOS && (s.ads || 0) >= MKP_ADS_MIN && s.ads / s.rev >= 2 * MKP_TACOS_AVG)) return false;
      const m = skuMargin(s);
      return m == null || m < MKP_MARGIN_THIN;   // margin mỏng/âm hoặc chưa có cost → cảnh báo
    },
    adgood: (s) => {
      if (!((s.ads || 0) >= MKP_ADS_MIN && (s.rev || 0) >= MKP_REV_SCALE && s.ads / s.rev <= MKP_TACOS_AVG)) return false;
      const m = skuMargin(s);
      return m != null && m >= MKP_MARGIN_GOOD;  // chỉ đề cử scale khi chắc đang lời
    },
    noad: (s) => {
      if (!((s.rev || 0) >= MKP_REV_NOAD && (s.ads || 0) === 0)) return false;
      const m = skuMargin(s);
      return m != null && m >= MKP_MARGIN_GOOD;  // đệm margin đủ gánh chi phí ad mới
    },
    deadrev: (s) => /dead/i.test(s.life || "") && (s.rev || 0) > 0,
    lowstock: (s) => { const iv = invMap[s.sku]; return !!(iv && /low stock|no stock|oversell/i.test(iv.health || "") && (s.rev || 0) >= 300); },
    orgstrong: (s) => (s.sess || 0) > 100 && (1 - (s.adsSess || 0) / s.sess) > 0.8 && (s.units || 0) / s.sess > 0.01,
  };
  // Chips 2 cụm: vận hành | ads (count đếm trên toàn tab; 0 → mờ)
  const chipsOps = [["refund", "↩ Has refund"], ["dead", "⚠ Dead"]];
  if (hasInvStatus) chipsOps.unshift(["oos", "🔻 Low stock (OOS)"]);
  if (SHOW_MARGIN) chipsOps.push(["mgneg", "💸 Negative margin"]);
  const chipsAds = [
    ["burnad", "🔥 Burning ad"],
    ["hitacos", `📈 TACOS ≥${Math.round(2 * MKP_TACOS_AVG * 100)}%`],
    ["noad", "💤 No ad yet"],
    ["adgood", "⚡ Efficient ad"],
  ];
  const chipCount = {};
  [...chipsOps, ...chipsAds].forEach(([k]) => { chipCount[k] = skus.filter(predMap[k]).length; });
  // Mô tả điều kiện từng bộ lọc — hover chip hoặc đang lọc sẽ thấy
  const tacosHi = Math.round(2 * MKP_TACOS_AVG * 100), tacosOk = Math.round(MKP_TACOS_AVG * 100);
  const chipDesc = {
    oos: "Inventory Status contains 'OOS' — low stock on marketplace",
    refund: "Has Return/Refund this month (case or $)",
    dead: "Classification column (2nd) = Dead",
    mgneg: `Margin < 0% — formula: (Rev − Cost×Units − Ads − Refund − fee ${Math.round(AMZ_FEE_PCT * 100)}%) ÷ Rev`,
    burnad: `Ads ≥ $${MKP_ADS_BURN} and Revenue = $0`,
    hitacos: `TACOS ≥ ${tacosHi}% (2× baseline ${tacosOk}%) · Rev ≥ $${MKP_REV_TACOS} · Ads ≥ $${MKP_ADS_MIN} · margin < ${MKP_MARGIN_THIN}% or missing cost`,
    noad: `Rev ≥ $${MKP_REV_NOAD} · no ad spend ($0) · margin ≥ ${MKP_MARGIN_GOOD}% — candidate to test ad`,
    adgood: `TACOS ≤ ${tacosOk}% · Ads ≥ $${MKP_ADS_MIN} · Rev ≥ $${MKP_REV_SCALE} · margin ≥ ${MKP_MARGIN_GOOD}% — candidate to increase budget`,
    "hiT-loC": "Classification column: High Traffic · Low Conversion (auto-classified by sheet)",
    "hiT-hiC": "Classification column: High Traffic · High Conversion (auto-classified by sheet)",
    loT: "Classification column: Low Traffic (auto-classified by sheet)",
    noCls: "No value in Classification column",
    deadrev: "Tagged Dead but still has revenue this month",
    lowstock: "Stock Low/No Stock/Oversell (Inventory tab) and Rev ≥ $300",
    orgstrong: "Sessions > 100 · organic > 80% · CR > 1% — healthy organic",
  };
  const tableRows = skus.filter((s) => {
    if (flt !== "all" && predMap[flt] && !predMap[flt](s)) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!((s.sku || "").toLowerCase().includes(t) || (s.brand || "").toLowerCase().includes(t) || (s.title || "").toLowerCase().includes(t))) return false;
    }
    return true;
  });
  // Sort động theo header (null xếp cuối); margin chọn lần đầu → asc (lỗ nhất lên đầu)
  const sortVal = (s) => {
    switch (sortK) {
      case "units": return s.units || 0;
      case "ads": return s.ads || 0;
      case "tacos": return s.rev > 0 ? s.ads / s.rev : null;
      case "margin": return skuMargin(s);
      case "sess": return s.sess || 0;
      case "refund": return s.refAmt || 0;
      default: return s.rev || 0;
    }
  };
  const sorted = tableRows.slice().sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return sortAsc ? va - vb : vb - va;
  });
  const clickSort = (k) => {
    if (sortK === k) setSortAsc(!sortAsc);
    else { setSortK(k); setSortAsc(k === "margin"); }
    setPg(0);
  };
  const thSort = (k, lbl) => (
    <th className="thSort" onClick={() => clickSort(k)}>{lbl}{sortK === k ? (sortAsc ? " ↑" : " ↓") : ""}</th>
  );
  const pageSize = 15;
  const pageRows = sorted.slice(pg * pageSize, pg * pageSize + pageSize);

  // ---- gợi ý rule ----
  const burnAd = skus.filter(predMap.burnad);
  const burnAdVal = burnAd.reduce((x, s) => x + (s.ads || 0), 0);
  const hiTacos = skus.filter(predMap.hitacos);
  const refunds = skus.filter((s) => (s.refAmt || 0) > 0);
  const refundVal = refunds.reduce((x, s) => x + (s.refAmt || 0), 0);
  const orgStrong = skus.filter((s) => (s.sess || 0) > 100 && (1 - (s.adsSess || 0) / s.sess) > 0.8 && (s.units || 0) / s.sess > 0.01);
  const deadRev = skus.filter((s) => /dead/i.test(s.life || "") && (s.rev || 0) > 0);
  // ④ SKUs selling tốt trên Amazon (rev ≥ $300) mà kho sắp cạn (join tab Inventory)
  const lowStockHot = skus.filter((s) => {
    const iv = invMap[s.sku];
    return iv && /low stock|no stock|oversell/i.test(iv.health || "") && (s.rev || 0) >= 300;
  });
  const lowStockHotRev = lowStockHot.reduce((x, s) => x + (s.rev || 0), 0);

  const pctCell = (v, tone) => v == null ? <span className="muted">—</span> : <span className={tone || ""}>{pcRound(v, 1)}%</span>;

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Amazon
          {SHOW_SOURCE_NOTES && <span className="repTag">tab Marketplace · MTD</span>}</div>
      </div>

      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Revenue (Amazon)" value={pcMoney(tot.rev)}
          sub={amzShare != null ? `${pcRound(amzShare, 0)}% of MKP channel · eBay+WM ${pcMoney(ebayWmRev)}` : "tab Marketplace"} />
        <PCKpi label="Units" value={pcNum(tot.units)} />
        <PCKpi label="Ads Spent" value={pcMoney(tot.ads)} sub={`ad rev ${pcMoney(tot.adRev)}`} />
        <PCKpi label="TACOS" value={tacos == null ? "—" : pcRound(tacos, 1) + "%"} tone={tacos == null ? undefined : tacos < 10 ? "good" : "warn"} sub="ads / revenue" />
        <PCKpi label="ACOS" value={acos == null ? "—" : pcRound(acos, 1) + "%"} sub="ads / ad revenue" />
        {SHOW_MARGIN && (
        <PCKpi label="Margin" value={margin == null ? "—" : pcRound(margin, 1) + "%"} tone={margin != null && margin < 0 ? "warn" : undefined}
          sub={margin == null ? "needs cost in MTD" : `after est. fee ${Math.round(AMZ_FEE_PCT * 100)}% · covers ${pcRound(mgCover, 0)}% rev`} />
        )}
        <PCKpi label="Sessions" value={pcNum(tot.sess)} sub={ctrAll != null ? `CTR ${pcRound(ctrAll, 2)}%` : ""} />
        <PCKpi label="Promotion" value={pcMoney(tot.promo)} sub={tot.rev > 0 ? pcRound((tot.promo / tot.rev) * 100, 1) + "% rev" : ""} />
        <PCKpi label="Refund" value={pcMoney(tot.refAmt)} sub={`${pcNum(tot.refCase)} case`} tone={tot.refAmt > 0 ? "warn" : undefined} />
        <PCKpi label="Top 5 SKUs" value={top5Pct == null ? "—" : pcRound(top5Pct, 0) + "% rev"} sub={`${pcMoney(top5Rev)} · concentration`} />
      </div>

      <SectionLabel>② Revenue by brand</SectionLabel>
      <div className="panel">
        {byBrand.slice(0, topB).map((b) => {
          const bt = b.rev > 0 ? (b.ads / b.rev) * 100 : null;
          const bm = b.mgDen > 0 ? (b.mgNum / b.mgDen) * 100 : null;
          return (
            <div className="pcBarRow" key={b.brand}>
              <div className="pcBarTop"><span>{b.brand}</span>
                <span className="muted">{pcMoney(b.rev)} · ads {pcMoney(b.ads)}{bt != null ? ` · TACOS ${pcRound(bt, 1)}%` : ""}{SHOW_MARGIN && bm != null ? ` · margin ${pcRound(bm, 0)}%` : ""}</span></div>
              <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(b.rev, maxBrandRev) + "%" }} /></div>
            </div>
          );
        })}
        {restB.length > 0 && (
          <div className="pcBarRow brandRest" onClick={() => { setBq(""); setShowAllB(true); }} title="View all brands">
            <div className="pcBarTop"><span className="muted">Others ({restB.length}) <span className="restHint">· click to view all ↗</span></span>
              <span className="muted">{pcMoney(restRev)}{restAds > 0 ? ` · ads ${pcMoney(restAds)}` : ""}</span></div>
            <div className="pcBarTrack"><div className="pcBarFill rest" style={{ width: pcPct(restRev, maxBrandRev) + "%" }} /></div>
          </div>
        )}
      </div>

      {/* Popup toàn bộ brand Amazon */}
      {showAllB && (() => {
        const list = byBrand.filter((b) => !bq || b.brand.toLowerCase().includes(bq.toLowerCase()));
        return (
          <div className="mdOverlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAllB(false); }}>
            <div className="mdPanel">
              <div className="mdHead">
                <span className="mdTitle">All brand Amazon ({byBrand.length})</span>
                <button className="mdClose" onClick={() => setShowAllB(false)}>✕</button>
              </div>
              <input className="searchBox mdSearch" placeholder="🔍 Search brand" value={bq} autoFocus
                onChange={(e) => setBq(e.target.value)} />
              <div className="mdList">
                {list.map((b, i2) => {
                  const bt = b.rev > 0 ? (b.ads / b.rev) * 100 : null;
                  return (
                    <div className="mdRow" key={b.brand}>
                      <span className="mdRank">{i2 + 1}</span>
                      <span className="mdName">{b.brand}</span>
                      <span className="mdVal">{pcMoney(b.rev)}</span>
                      <span className="mdPct muted">{bt != null ? "TACOS " + pcRound(bt, 1) + "%" : "—"}</span>
                    </div>
                  );
                })}
                {list.length === 0 && <div className="mdEmpty muted">No brand matches "{bq}"</div>}
              </div>
            </div>
          </div>
        );
      })()}

      <SectionLabel>③ By product type</SectionLabel>
      <div className="panel">
        {byCat.slice(0, 8).map((c2) => {
          const ct2 = c2.rev > 0 ? (c2.ads / c2.rev) * 100 : null;
          const cm = c2.mgDen > 0 ? (c2.mgNum / c2.mgDen) * 100 : null;
          return (
            <div className="pcBarRow" key={c2.cat}>
              <div className="pcBarTop"><span>{c2.cat}</span>
                <span className="muted">{pcMoney(c2.rev)} · ads {pcMoney(c2.ads)}{ct2 != null ? ` · TACOS ${pcRound(ct2, 1)}%` : ""}{SHOW_MARGIN && cm != null ? ` · margin ${pcRound(cm, 0)}%` : ""}</span></div>
              <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(c2.rev, maxCatRev) + "%" }} /></div>
            </div>
          );
        })}
      </div>

      <SectionLabel>④ Traffic × conversion segments <span className="iTag rule">click to filter table</span></SectionLabel>
      <div className="mkSegGrid">
        {segStats.map((sg) => (
          <div key={sg.k} className={"mkSegCard " + (flt === sg.k ? "on" : "")}
            onClick={() => { setFlt(flt === sg.k ? "all" : sg.k); setPg(0); }}>
            <div className="mkSegLbl">{sg.lbl}</div>
            <div className="mkSegN">{sg.n} <span className="muted">SKU</span></div>
            <div className="mkSegRev">{pcMoney(sg.rev)} · {tot.rev > 0 ? pcRound((sg.rev / tot.rev) * 100, 0) : 0}% rev</div>
          </div>
        ))}
      </div>

      <SectionLabel>⑤ SKU detail table</SectionLabel>
      <div className="panel">
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search SKU / brand / name" value={q}
            onChange={(e) => { setQ(e.target.value); setPg(0); }} />
          <div className="chipRow">
            <button className={"chip " + (flt === "all" ? "chipOn" : "")} onClick={() => { setFlt("all"); setPg(0); }}>All</button>
            {chipsOps.map(([k, lbl]) => (
              <button key={k} disabled={chipCount[k] === 0} title={chipDesc[k]}
                className={"chip " + (flt === k ? "chipOn" : "") + (chipCount[k] === 0 ? " chipOff" : "")}
                onClick={() => { setFlt(flt === k ? "all" : k); setPg(0); }}>{lbl} · {chipCount[k]}</button>
            ))}
            <span className="chipSep" />
            {chipsAds.map(([k, lbl]) => (
              <button key={k} disabled={chipCount[k] === 0} title={chipDesc[k]}
                className={"chip " + (flt === k ? "chipOn" : "") + (chipCount[k] === 0 ? " chipOff" : "")}
                onClick={() => { setFlt(flt === k ? "all" : k); setPg(0); }}>{lbl} · {chipCount[k]}</button>
            ))}
            {![...chipsOps, ...chipsAds].some(([k]) => k === flt) && flt !== "all" && (
              <button className="chip chipOn" onClick={() => { setFlt("all"); setPg(0); }}>✕ clear filter</button>
            )}
          </div>
          {flt !== "all" && chipDesc[flt] && (
            <div className="chipHint">Condition: {chipDesc[flt]}</div>
          )}
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr>
            <th className="l">SKU</th><th className="l">Name</th>
            {thSort("rev", "Rev")}{thSort("units", "Units")}{thSort("ads", "Ads")}{thSort("tacos", "TACOS")}
            {SHOW_MARGIN && thSort("margin", "Margin")}
            {thSort("sess", "Sess")}{thSort("refund", "Refund")}
          </tr></thead>
          <tbody>
            {pageRows.map((s) => {
              const st = s.rev > 0 ? (s.ads / s.rev) * 100 : null;
              const sa = s.adRev > 0 ? (s.ads / s.adRev) * 100 : null;
              const scr = s.sess > 0 ? ((s.units || 0) / s.sess) * 100 : null;
              return (
                <tr key={s.sku}>
                  <td className="l">{s.sku}{/dead/i.test(s.life || "") && <span className="hlLow" style={{ marginLeft: 4 }}>Dead</span>}</td>
                  <td className="l muted tdName" title={s.title || ""}>{s.title || "—"}</td>
                  <td>{pcMoney(s.rev)}</td>
                  <td>{s.units || 0}</td>
                  <td>{s.ads > 0 ? (<>{pcMoney(s.ads)}<div className="tdSub">{sa != null ? "ACOS " + pcRound(sa, 1) + "%" : ""}</div></>) : "—"}</td>
                  <td>{pctCell(st, st != null && st > 20 ? "negT" : st != null && st > 10 ? "warnT" : "")}</td>
                  {SHOW_MARGIN && (() => { const m = skuMargin(s); return (
                    <td className={m != null && m < 0 ? "negT" : ""}>{m == null ? "—" : pcRound(m, 0) + "%"}</td>
                  ); })()}
                  <td>{s.sess > 0 ? (<>{pcNum(s.sess)}<div className="tdSub">{scr != null ? "CR " + pcRound(scr, 1) + "%" : ""}</div></>) : "—"}</td>
                  <td className={s.refAmt > 0 ? "warnT" : ""}>{s.refAmt > 0 ? pcMoney(s.refAmt) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <div className="fineNote">{tableRows.length} SKUs · filtered from {skus.length}</div>
        <Pager pg={pg} setPg={setPg} total={tableRows.length} pageSize={pageSize} />
      </div>

      <SectionLabel>⑥ Recommendations <span className="iTag rule">rule · click to filter table</span></SectionLabel>
      <div className="panel">
        {burnAd.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("burnad"); setPg(0); }} title="Click to filter table by this group"><span className="dot neg" />
            <span>{burnAd.length} SKUs burning ad with no revenue (ads ≥ ${MKP_ADS_BURN}, revenue $0) — total {pcMoney(burnAdVal)}/month, pause ad or check listing.</span></div>
        )}
        {hiTacos.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("hitacos"); setPg(0); }} title="Click to filter table by this group"><span className="dot warn" />
            <span>{hiTacos.length} SKUs with TACOS ≥ {Math.round(2 * MKP_TACOS_AVG * 100)}% and thin margin — ad eating too deep, review bid/keywords.</span></div>
        )}
        {refunds.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("refund"); setPg(0); }} title="Click to filter table by this group"><span className="dot warn" />
            <span>{refunds.length} SKUs with refunds, total {pcMoney(refundVal)} — check quality/product description.</span></div>
        )}
        {deadRev.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("deadrev"); setPg(0); }} title="Click to filter table by this group"><span className="dot warn" />
            <span>{deadRev.length} SKUs tagged Dead but still have revenue — review classification.</span></div>
        )}
        {lowStockHot.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("lowstock"); setPg(0); }} title="Click to filter table by this group"><span className="dot neg" />
            <span>{lowStockHot.length} SKUs with revenue ≥ $300 running low ({pcMoney(lowStockHotRev)} rev/month) — stockout on Amazon loses ranking, prioritize restock.</span></div>
        )}
        {orgStrong.length > 0 && (
          <div className="mkInsight click" onClick={() => { setFlt("orgstrong"); setPg(0); }} title="Click to filter table by this group"><span className="dot pos" />
            <span>{orgStrong.length} SKUs with healthy organic (&gt;80% organic, good CR) — can reduce ad to optimize TACOS.</span></div>
        )}
        {burnAd.length === 0 && hiTacos.length === 0 && refunds.length === 0 && deadRev.length === 0 && (
          <div className="insightBody muted">No alerts.</div>
        )}
      </div>
    </div>
  );
}


/* ================= BU DETAIL ================= */
// Shell tầng BU: sidebar (kênh accordion → report) + nội dung + breadcrumb.
// BU switch nằm ở topbar (Kiểu B) — truyền xuống qua props để render trên đầu.
function Detail({ data, selectedBU, setSelectedBU, activeReport, setActiveReport, onBack,
                  activeChannel, setActiveChannel }) {
  const bu = data.bus[selectedBU];
  const fullFrozenHistory = data.isHistorical && Number(data.snapshotSchemaVersion) >= 2;
  const historicalHidden = fullFrozenHistory
    ? new Set(["inventory", "seo"])
    : new Set(["inventory", "email", "seo", "ads", "mkp"]);
  const visibleChannels = CHANNELS
    .filter((c) => !(data.isHistorical && !fullFrozenHistory && c.id === "marketplace"))
    .map((c) => ({
      ...c,
      groups: (c.groups || []).map((g) => ({
        ...g,
        reports: data.isHistorical ? g.reports.filter((r) => !historicalHidden.has(r.id)) : g.reports,
      })).filter((g) => g.reports.length > 0),
    }));
  const chan = visibleChannels.find((c) => c.id === activeChannel) || visibleChannels[0] || CHANNELS[0];
  const chanReports = (chan.groups || []).flatMap((g) => g.reports);
  const current = chanReports.find((r) => r.id === activeReport) || chanReports[0] || null;
  const reportId = current ? current.id : "sales";
  const chanLabel = chan.label;
  const repLabel = current ? current.label : "";

  return (
    <div className="detailShell">
      <aside className="sidebar">
        <nav className="reportNav">
          {visibleChannels.map((c) => {
            const isOpen = c.id === chan.id;
            return (
              <div key={c.id} className="chanBlock">
                <button
                  className={"chanHead" + (isOpen ? " chanOpen" : "") + (c.ready ? "" : " chanPending")}
                  onClick={() => {
                    if (!c.ready) { setActiveChannel(c.id); return; }
                    setActiveChannel(c.id);
                    // mở kênh → nhảy tới report đầu tiên nếu report hiện không thuộc kênh này
                    const ids = (c.groups || []).flatMap((g) => g.reports).map((r) => r.id);
                    if (!ids.includes(activeReport) && ids.length) setActiveReport(ids[0]);
                  }}
                >
                  <span>{c.icon} {c.label}</span>
                  <span className="chanCaret">{c.ready ? (isOpen ? "▼" : "▶") : "no data"}</span>
                </button>
                {isOpen && c.ready && (c.groups || []).map((g) => (
                  <div key={g.group} className="navGroup">
                    <div className="navGroupTitle">{g.group}</div>
                    {g.reports.map((rep) => (
                      <button
                        key={rep.id}
                        className={"navItem" + (rep.id === reportId ? " active" : "") + (rep.ready ? "" : " pending")}
                        onClick={() => setActiveReport(rep.id)}
                      >
                        <span>{rep.label}</span>
                        {!rep.ready && <span className="navDot" title="awaiting data" />}
                      </button>
                    ))}
                  </div>
                ))}
                {isOpen && !c.ready && (
                  <div className="chanEmpty">This channel has no data — dedicated report coming later.</div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <main className="reportArea">
        <div className="breadcrumb">
          <span className="bcItem" onClick={onBack}>Overview</span>
          <span className="bcSep">›</span>
          <span className="bcItem">{selectedBU}</span>
          <span className="bcSep">›</span>
          <span className="bcItem">{chanLabel}</span>
          {repLabel && (<><span className="bcSep">›</span><span className="bcCurrent">{repLabel}</span></>)}
        </div>
        {!chan.ready ? (
          <div className="noData">
            <div className="noDataTitle">{chanLabel} · {selectedBU} — no data</div>
            <div className="noDataHint">This channel will get a dedicated report when its data source is available.</div>
          </div>
        ) : !bu ? (
          <div className="noData">
            <div className="noDataTitle">{selectedBU} has no data yet</div>
            <div className="noDataHint">Connect Shopify for this BU to view details.</div>
          </div>
        ) : current && current.ready ? (
          <ReportErrorBoundary reportKey={reportId + selectedBU + (data.isHistorical ? "-history" : "-live")}>
          {reportId === "mkp" ? (
            <MarketplaceReport data={data} selectedBU={selectedBU} />
          ) : reportId === "sales" ? (
            <SalesReport data={data} selectedBU={selectedBU} />
          ) : reportId === "brand" ? (
            <BrandReport data={data} selectedBU={selectedBU} />
          ) : reportId === "product" ? (
            <ProductReport data={data} selectedBU={selectedBU} />
          ) : reportId === "inventory" ? (
            selectedBU === "Heli" ? (
            <InventoryReportHeli data={data} selectedBU={selectedBU} />
          ) : selectedBU === "Jet" ? (
            <InventoryReportHeli data={data} selectedBU={selectedBU} />
          ) : (
            <InventoryReport data={data} selectedBU={selectedBU} />
          )
          ) : reportId === "ads" ? (
            <PaidAdsReport data={data} selectedBU={selectedBU} />
          ) : reportId === "email" ? (
            <EmailReport data={data} selectedBU={selectedBU} />
          ) : reportId === "mktoverview" ? (
            <MarketingOverview data={data} selectedBU={selectedBU} />
          ) : (
            <SalesReport data={data} selectedBU={selectedBU} />
          )}
          </ReportErrorBoundary>
        ) : (
          <ReportComingSoon report={current} bu={selectedBU} />
        )}
      </main>
    </div>
  );
}

// Màn hình cho report no data
function ReportComingSoon({ report, bu }) {
  return (
    <div className="comingSoon">
      <div className="csTitle">{report ? report.label : "Report"} · {bu}</div>
      <div className="csHint">
        This report activates when {report && report.source ? report.source : "corresponding data source"} is available.
      </div>
      <div className="csSkelRows">
        {[0, 1, 2].map((i) => <div key={i} className="funBarSkel" />)}
      </div>
    </div>
  );
}

// Nội dung report Sales (đã dựng) — giờ là 1 report trong shell
/* ============================ PAID ADS REPORT (từ MTD, ROAS từ GA4) ============================ */
function PaidAdsReport({ data, selectedBU }) {
  const [chan, setChan] = React.useState("all");   // all | google | fb
  const [seg, setSeg] = React.useState("all");
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.skus) return <PCNoData bu={selectedBU} what="Paid Ads" />;

  const skus = pcComputeSkus(prod.skus);

  // Items/Sessions từ MTD-SKU (GA4 attributed theo SKU) — giữ để hiển thị
  let gSku = { items: 0, sess: 0 }, fSku = { items: 0, sess: 0 };
  skus.forEach((s) => {
    if (s.g) { gSku.items += s.g.items || 0; gSku.sess += s.g.sess || 0; }
    if (s.f) { fSku.items += s.f.items || 0; fSku.sess += s.f.sess || 0; }
  });

  // Spend + Rev THẬT từ ad_spend_real (GA4 channel) — nguồn chính cho mọi số tổng.
  const asr = data.adSpendReal && data.adSpendReal[selectedBU];
  const asrM = (asr && data.month && asr[data.month]) ? asr[data.month] : null;
  const useReal = !!asrM;   // có nhập ad_spend_real → dùng thật; else fallback MTD-SKU
  const g = useReal
    ? { spend: asrM.gSpend, rev: asrM.gRev, items: gSku.items, sess: gSku.sess }
    : (() => { let o = { spend: 0, rev: 0, items: 0, sess: 0 }; skus.forEach((s) => { if (s.g) { o.spend += s.g.spend || 0; o.rev += s.g.rev || 0; o.items += s.g.items || 0; o.sess += s.g.sess || 0; } }); return o; })();
  const f = useReal
    ? { spend: asrM.fSpend, rev: asrM.fRev, items: fSku.items, sess: fSku.sess }
    : (() => { let o = { spend: 0, rev: 0, items: 0, sess: 0 }; skus.forEach((s) => { if (s.f) { o.spend += s.f.spend || 0; o.rev += s.f.rev || 0; o.items += s.f.items || 0; o.sess += s.f.sess || 0; } }); return o; })();
  const totSpend = g.spend + f.spend;
  const totRev = g.rev + f.rev;
  const totItems = g.items + f.items;
  const totSess = g.sess + f.sess;
  const roasAll = totSpend > 0 ? totRev / totSpend : null;
  const gRoas = g.spend > 0 ? g.rev / g.spend : null;
  const fRoas = f.spend > 0 ? f.rev / f.spend : null;

  // Theo store: ưu tiên ad_spend_real (thật, có gSpend/fSpend/gRev/fRev per store); else adByStore (MTD)
  const stores = useReal
    ? Object.keys(asrM.byStore || {}).map((st) => {
        const b = asrM.byStore[st];
        return { store: st, gSpend: b.gSpend, fSpend: b.fSpend, gRev: b.gRev, fRev: b.fRev,
                 spend: (b.gSpend || 0) + (b.fSpend || 0), rev: (b.gRev || 0) + (b.fRev || 0) };
      })
    : (prod.adByStore || []).map((st) => ({
        store: st.store, gSpend: st.gSpend, fSpend: st.fSpend, gRev: st.gRev, fRev: st.fRev,
        spend: (st.gSpend || 0) + (st.fSpend || 0), rev: (st.gRev || 0) + (st.fRev || 0) }));

  // Lấy ad theo kênh đang chọn cho 1 SKU
  function chanData(s) {
    if (chan === "google") return s.g || { spend: 0, rev: 0, items: 0, sess: 0 };
    if (chan === "fb") return s.f || { spend: 0, rev: 0, items: 0, sess: 0 };
    // all = gộp
    const gg = s.g || { spend: 0, rev: 0, items: 0, sess: 0 };
    const ff = s.f || { spend: 0, rev: 0, items: 0, sess: 0 };
    return { spend: gg.spend + ff.spend, rev: gg.rev + ff.rev, items: gg.items + ff.items, sess: gg.sess + ff.sess };
  }
  // Segment 1 SKU theo kênh
  function adSeg(s) {
    const d = chanData(s);
    if (d.spend <= 0) return "";
    const roas = d.rev / d.spend;
    if (d.spend > 15 && roas >= 8 && s.marginAfterAd >= 30) return "good";
    if (d.spend > 15 && roas < 2) return "burn";
    if (d.spend > 15 && roas >= 2 && roas < 5) return "reduce";
    if (d.spend > 15 && roas >= 5 && roas < 8) return "margin";
    if (d.spend <= 15 && roas >= 8) return "potential";
    if (d.sess > 0 && d.items === 0) return "noclick";
    return "";
  }
  const SEGS = [
    ["good", "🏆 Good"], ["burn", "💸 Wasting money"], ["reduce", "📉 Reduce spend"],
    ["margin", "⚠ Marginal"], ["potential", "🌱 Potential"], ["noclick", "👀 Clicked no buy"],
  ];
  const segLabel = Object.fromEntries(SEGS);
  const segCount = {}; SEGS.forEach(([k]) => segCount[k] = 0);
  const adSkus = skus.filter((s) => chanData(s).spend > 0 || chanData(s).sess > 0);
  adSkus.forEach((s) => { const sg = adSeg(s); if (sg) segCount[sg]++; });

  const rows = adSkus.filter((s) => {
    if (seg !== "all" && adSeg(s) !== seg) return false;
    if (q && !((s.sku || "").toLowerCase().includes(q.toLowerCase()) || (s.brand || "").toLowerCase().includes(q.toLowerCase()) || (s.name || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }).sort((a, b) => chanData(b).spend - chanData(a).spend);
  const pageSize = 15;
  const pageRows = rows.slice(pg * pageSize, pg * pageSize + pageSize);

  const ctrAll = null; // MTD SKU-level không có impression → CTR chỉ ở tổng (dưới)

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Paid Ads
          <span className="repTag">{data.month} MTD</span></div>
        {SHOW_SOURCE_NOTES && <span className="repNote">{useReal ? "source: ad_spend_real (GA4 channel) · SKU: MTD" : "source: MTD (temp) · ROAS from GA4"}</span>}
      </div>
      {!useReal && <div className="warnNote">⚠ ad_spend_real not entered — using temp spend/ROAS from MTD (GA4 by SKU, missing non-product). Enter ad_spend_real tab for real numbers.</div>}

      {/* ① Overview */}
      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Total spend" value={pcMoney(totSpend)} />
        <PCKpi label={useReal ? "Rev from ad" : "Rev from ad (GA4)"} value={pcMoney(totRev)} tone="good" />
        <PCKpi label="ROAS" value={roasAll == null ? "—" : pcRound(roasAll, 1)} tone={useReal ? undefined : "warn"} sub={useReal ? "GA4 channel" : "GA4 · ⚠ high"} />
        <PCKpi label="Items from ad" value={pcNum(totItems)} />
        <PCKpi label="Sessions from ad" value={pcNum(totSess)} />
        <PCKpi label="Google : FB" value={`${pcRound(pcPct(g.spend, totSpend),0)}:${pcRound(pcPct(f.spend, totSpend),0)}`} sub="by spend" small />
      </div>

      {/* ② Google vs Facebook */}
      <SectionLabel>② Google vs Facebook</SectionLabel>
      <div className="splitGrid">
        <div className="platCard ggl">
          <div className="platName">Google Ads</div>
          <table className="miniTbl"><tbody>
            <tr><td>Spend</td><td>{pcMoney(g.spend)}</td></tr>
            <tr><td>Rev (GA4)</td><td>{pcMoney(g.rev)}</td></tr>
            <tr><td>ROAS</td><td className="good">{gRoas == null ? "—" : pcRound(gRoas, 1)}</td></tr>
            <tr><td>Items</td><td>{pcNum(g.items)}</td></tr>
            <tr><td>Sessions</td><td>{pcNum(g.sess)}</td></tr>
          </tbody></table>
        </div>
        <div className="platCard fb">
          <div className="platName">Facebook Ads</div>
          <table className="miniTbl"><tbody>
            <tr><td>Spend</td><td>{pcMoney(f.spend)}</td></tr>
            <tr><td>Rev (GA4)</td><td>{pcMoney(f.rev)}</td></tr>
            <tr><td>ROAS</td><td className="good">{fRoas == null ? "—" : pcRound(fRoas, 1)}</td></tr>
            <tr><td>Items</td><td>{pcNum(f.items)}</td></tr>
            <tr><td>Sessions</td><td>{pcNum(f.sess)}</td></tr>
          </tbody></table>
        </div>
      </div>
      <div className="alertBox info">
        <div className="abBody">
          Google takes <b>{pcRound(pcPct(g.spend, totSpend),0)}% of budget</b> but generates <b>{pcRound(pcPct(g.rev, totRev),0)}% revenue</b>
          {gRoas && fRoas && gRoas > fRoas ? " — more efficient than Facebook." : "."}
        </div>
      </div>

      {/* ③ Budget vs revenue allocation */}
      <SectionLabel>③ Budget vs revenue allocation</SectionLabel>
      <div className="panel">
        <div className="allocLabel">Budget (spend)</div>
        <div className="allocBar">
          <div className="allocG" style={{ width: pcPct(g.spend, totSpend) + "%" }}>G {pcRound(pcPct(g.spend, totSpend),0)}%</div>
          <div className="allocF" style={{ width: pcPct(f.spend, totSpend) + "%" }}>FB {pcRound(pcPct(f.spend, totSpend),0)}%</div>
        </div>
        <div className="allocLabel">Revenue (GA4)</div>
        <div className="allocBar">
          <div className="allocG" style={{ width: pcPct(g.rev, totRev) + "%" }}>G {pcRound(pcPct(g.rev, totRev),0)}%</div>
          <div className="allocF" style={{ width: pcPct(f.rev, totRev) + "%" }}>FB {pcRound(pcPct(f.rev, totRev),0)}%</div>
        </div>
      </div>

      {/* ④ By store */}
      <SectionLabel>④ By store</SectionLabel>
      <div className="panel">
        {stores && stores.length ? (
          <div className="tblWrap"><table className="dataTbl">
            <thead><tr><th className="l">Store</th><th>Spend</th><th>Rev (GA4)</th><th>ROAS</th><th>Google spend</th><th>FB spend</th></tr></thead>
            <tbody>
              {stores.map((st) => {
                const roas = st.spend > 0 ? st.rev / st.spend : null;
                return (
                  <tr key={st.store}>
                    <td className="l">{st.store}</td>
                    <td>{pcMoney(st.spend)}</td>
                    <td className={st.spend > 0 && st.rev === 0 ? "bad" : ""}>{pcMoney(st.rev)}</td>
                    <td className={roas == null ? "" : roas < 2 ? "bad" : roas >= 5 ? "good" : "warn"}>{roas == null ? "—" : pcRound(roas, 1)}{st.spend > 0 && st.rev === 0 ? " ⚠" : ""}</td>
                    <td className="muted">{pcMoney(st.gSpend)}</td>
                    <td className="muted">{pcMoney(st.fSpend)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        ) : <PCNoData bu={selectedBU} what="ad by store" />}
        <div className="fineNote">Any store with spend but $0 rev → check tracking / consider pausing ad</div>
      </div>

      {/* ⑤ Product performance by channel */}
      <SectionLabel>⑤ Product performance by channel</SectionLabel>
      <div className="panel">
        <div className="filterRow">
          <div className="chipRow">
            <span className="chanLbl">Channel:</span>
            {[["all","All"],["google","Google"],["fb","Facebook"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (chan === k ? "chipOn" : "")} onClick={() => { setChan(k); setSeg("all"); setPg(0); }}>{lbl}</button>
            ))}
          </div>
          <input className="searchBox" placeholder="🔍 Search SKU / brand" value={q} onChange={(e) => { setQ(e.target.value); setPg(0); }} />
        </div>
        <div className="chipRow segRow">
          <button className={"chip " + (seg === "all" ? "chipOn" : "")} onClick={() => { setSeg("all"); setPg(0); }}>All</button>
          {SEGS.map(([k, lbl]) => (
            <button key={k} className={"chip " + (seg === k ? "chipOn" : "")} onClick={() => { setSeg(k); setPg(0); }}>{lbl} ({segCount[k]})</button>
          ))}
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Product</th><th className="l">Segment</th><th>Spend</th><th>Rev</th><th>ROAS</th><th>Items</th><th>Sessions</th></tr></thead>
          <tbody>
            {pageRows.map((s) => {
              const d = chanData(s);
              const roas = d.spend > 0 ? d.rev / d.spend : null;
              const sg = adSeg(s);
              return (
                <tr key={s.sku}>
                  <td className="l">{s.sku}</td>
                  <td className="l nameCol">{s.name || "—"}</td>
                  <td className={"l adseg-" + (sg || "none")}>{sg ? segLabel[sg] : "—"}</td>
                  <td>{pcMoney(d.spend)}</td>
                  <td>{pcMoney(d.rev)}</td>
                  <td className={roas != null && roas < 2 ? "bad" : roas >= 8 ? "good" : ""}>{roas == null ? "—" : pcRound(roas, 1)}</td>
                  <td>{pcNum(d.items)}</td>
                  <td>{pcNum(d.sess)}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <div className="fineNote">{rows.length} SKUs running ad ({chan === "all" ? "both channels" : chan === "google" ? "Google" : "Facebook"}) · Rev/Items/Sessions from GA4 attributed</div>
        <Pager pg={pg} setPg={setPg} total={rows.length} pageSize={pageSize} />
      </div>

      {/* ⑥ Recommendations */}
      <SectionLabel>⑥ Recommendations</SectionLabel>
      <div className="panel aiPanel">
        <div className="aiHead">Recommendations <span className="aiTag">AI</span></div>
        <div className="aiBody">
          {gRoas && fRoas && gRoas > fRoas && `Google more efficient than Facebook (ROAS ${pcRound(gRoas,1)} vs ${pcRound(fRoas,1)}). `}
          {segCount.potential > 0 && `${segCount.potential} "Potential" SKUs (small spend, high ROAS) — consider increasing budget. `}
          {segCount.burn > 0 && `${segCount.burn} "Wasting money" SKUs — review or pause. `}
          {segCount.noclick > 0 && `${segCount.noclick} SKUs draw ad traffic but no conversion — check product page/price.`}
        </div>
      </div>
    </div>
  );
}


function ProductTypePerformance({ product }) {
  const skus = product && Array.isArray(product.skus) ? product.skus : [];
  if (!skus.length) {
    return <div className="noData">No MTD product type data</div>;
  }

  const agg = {};
  skus.forEach((sku) => {
    const type = String(sku.type || "").trim() || "Uncategorized";
    if (!agg[type]) {
      agg[type] = {
        type,
        gross: 0,
        net: 0,
        units: 0,
        cogs: 0,
        adSpend: 0,
        adRev: 0,
        skuCount: 0,
        skuSold: 0,
      };
    }
    const row = agg[type];
    row.gross += Number(sku.gross) || 0;
    row.net += Number(sku.net) || 0;
    row.units += Number(sku.units) || 0;
    row.cogs += Number(sku.cogs) || 0;
    row.adSpend += Number(sku.adSpend) || 0;
    row.adRev += Number(sku.adRev) || 0;
    row.skuCount += 1;
    if ((Number(sku.units) || 0) > 0) row.skuSold += 1;
  });

  const rows = Object.values(agg)
    .map((row) => {
      const profit = row.net - row.cogs - row.adSpend;
      return {
        ...row,
        profit,
        margin: row.net > 0 ? profit / row.net : null,
        roas: row.adSpend > 0 ? row.adRev / row.adSpend : null,
      };
    })
    .sort((a, b) => b.gross - a.gross);
  const totalGross = rows.reduce((sum, row) => sum + row.gross, 0);
  const totalNet = rows.reduce((sum, row) => sum + row.net, 0);
  const totalUnits = rows.reduce((sum, row) => sum + row.units, 0);
  const totalSkus = rows.reduce((sum, row) => sum + row.skuCount, 0);
  const totalSoldSkus = rows.reduce((sum, row) => sum + row.skuSold, 0);
  const totalAdSpend = rows.reduce((sum, row) => sum + row.adSpend, 0);
  const totalAdRev = rows.reduce((sum, row) => sum + row.adRev, 0);
  const totalProfit = rows.reduce((sum, row) => sum + row.profit, 0);
  const totalRoas = totalAdSpend > 0 ? totalAdRev / totalAdSpend : null;
  const totalMargin = totalNet > 0 ? totalProfit / totalNet : null;
  const leader = rows[0];
  const conclusion = leader
    ? `${leader.type} leads · ${fmtPct(totalGross > 0 ? leader.gross / totalGross : 0)} of MTD gross`
    : null;

  return (
    <SectionCard title="Product Type Performance" conclusion={conclusion}>
      <div className="tableWrap">
        <table className="dataTable compact">
          <thead>
            <tr>
              <th>Product type</th>
              <th className="r">Gross</th>
              <th className="r">Share</th>
              <th className="r">Net</th>
              <th className="r">Units</th>
              <th className="r">Sold SKUs</th>
              <th className="r">Ad spend</th>
              <th className="r">ROAS</th>
              {SHOW_MARGIN && <th className="r">CM</th>}
              {SHOW_MARGIN && <th className="r">Margin</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.type}>
                <td className="strong">{row.type}</td>
                <td className="r">{fmtUSD(row.gross)}</td>
                <td className="r">{fmtPct(totalGross > 0 ? row.gross / totalGross : 0)}</td>
                <td className="r">{fmtUSD(row.net)}</td>
                <td className="r">{fmtNum(row.units)}</td>
                <td className="r">{fmtNum(row.skuSold)} / {fmtNum(row.skuCount)}</td>
                <td className="r">{row.adSpend > 0 ? fmtUSD(row.adSpend) : "—"}</td>
                <td className={"r " + (row.roas == null ? "" : row.roas < 2 ? "bad" : row.roas >= 8 ? "good" : "")}>
                  {row.roas == null ? "—" : row.roas.toFixed(1)}
                </td>
                {SHOW_MARGIN && <td className={"r " + (row.profit < 0 ? "bad" : "")}>{fmtUSD(row.profit)}</td>}
                {SHOW_MARGIN && (
                  <td className={"r " + (row.margin == null ? "" : row.margin < 0 ? "bad" : row.margin >= 0.3 ? "good" : "")}>
                    {row.margin == null ? "—" : fmtPct(row.margin)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="strong">Total</td>
              <td className="r">{fmtUSD(totalGross)}</td>
              <td className="r">100.0%</td>
              <td className="r">{fmtUSD(totalNet)}</td>
              <td className="r">{fmtNum(totalUnits)}</td>
              <td className="r">{fmtNum(totalSoldSkus)} / {fmtNum(totalSkus)}</td>
              <td className="r">{totalAdSpend > 0 ? fmtUSD(totalAdSpend) : "—"}</td>
              <td className="r">{totalRoas == null ? "—" : totalRoas.toFixed(1)}</td>
              {SHOW_MARGIN && <td className={"r " + (totalProfit < 0 ? "bad" : "")}>{fmtUSD(totalProfit)}</td>}
              {SHOW_MARGIN && <td className="r">{totalMargin == null ? "—" : fmtPct(totalMargin)}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="fineNote">
        Source: MTD · grouped by Type · contribution margin = Net − COGS − SKU-attributed ad spend
      </div>
    </SectionCard>
  );
}


function SalesReport({ data, selectedBU }) {
  const bu = data.bus[selectedBU];
  // Sales report = CHỈ online channel (số thực + target đều của online)
  const r = bu ? rollupChannel(bu, "online") : null;

  // Target từ config_channel dòng "online" (tháng đang xem) — của riêng BU này
  const ctBU = data.channelTargets && data.channelTargets[selectedBU];
  const ct = ctBU && ctBU[data.month];
  const ctOnline = ct && ct.online ? ct.online : {};
  const salesTarget = ctOnline.revenue != null ? ctOnline.revenue : null;
  // map target online → cấu trúc tgt cũ để phần render dưới dùng lại
  const tgt = {
    sales: ctOnline.revenue != null ? ctOnline.revenue : null,
    traffic: ctOnline.session != null ? ctOnline.session : null,
    roas: ctOnline.roas != null ? ctOnline.roas : null,
    ret: null,                                    // return% không còn target theo kênh
    adcost: ctOnline.pctAdRev != null ? ctOnline.pctAdRev : null,
    conversion: ctOnline.conversion != null ? ctOnline.conversion : null,
  };

  // Session + Conv ĐỒNG BỘ Overview: sessions = 7 channels total marketing_data; conv = orders Shopify online / sessions
  const mkMS = data.marketing && data.marketing[selectedBU] && data.marketing[selectedBU][data.month];
  const mkSessS = (mkMS && mkMS.rows && mkMS.rows.length)
    ? mkMS.rows.reduce((s2, x) => s2 + (x.sessions || 0), 0) : null;
  const mkConvS = (mkSessS > 0 && r && r.orders != null) ? r.orders / mkSessS : null;

  // Nguồn ad: spend+rev THẬT (ad_spend_real, GA4 channel) nếu có; else MTD-SKU
  const prod = data.product && data.product[selectedBU];
  const isCurrentMonth = prod && prod.updatedMonth && data.month === prod.updatedMonth;
  const asrS = data.adSpendReal && data.adSpendReal[selectedBU];
  const asrSM = (asrS && data.month && asrS[data.month]) ? asrS[data.month] : null;

  // MTD online (cho fallback CM + ROAS): gộp brand → profit / net / ad-SKU
  let mtdProfit = null, mtdNet = 0, skuAd = 0;
  if (prod && prod.brands && prod.brands.length) {
    const bs = pcComputeBrands(prod.brands);
    mtdProfit = bs.reduce((s2, b) => s2 + b.profit, 0);
    mtdNet = bs.reduce((s2, b) => s2 + b.net, 0);
    skuAd = bs.reduce((s2, b) => s2 + b.adSpend, 0);
  }
  const onlineAdRealS = asrSM ? asrSM.spend : skuAd;   // ad thật online (đồng bộ Overview)

  // Contribution margin online (scorecard KPI) — ĐỒNG BỘ Overview: ưu tiên raw_daily
  // (r.net − r.cogs − ad thật), fallback MTD khi raw_daily chưa có cogs (tháng chưa rebuild).
  // Lưu ý: per-product / per-brand / Amazon vẫn giữ MTD ở report riêng của chúng.
  let onlineProfit = null, onlineMargin = null;
  if (r && r.cogs != null) {
    onlineProfit = r.net - r.cogs - onlineAdRealS;
    onlineMargin = r.net > 0 ? (onlineProfit / r.net) * 100 : null;
  } else if (mtdProfit != null) {
    onlineProfit = asrSM ? mtdProfit - (asrSM.spend - skuAd) : mtdProfit;
    onlineMargin = mtdNet > 0 ? (onlineProfit / mtdNet) * 100 : null;
  }

  // ROAS / Ad cost% / Conv online (dùng số thật nếu có) — chỉ tháng hiện tại
  if (r && prod && prod.brands && isCurrentMonth) {
    const bs = pcComputeBrands(prod.brands);
    const tNet = bs.reduce((s, b) => s + b.net, 0);
    const tAd = asrSM ? asrSM.spend : skuAd;
    const tAdRev = asrSM ? asrSM.rev : bs.reduce((s, b) => s + b.adRev, 0);
    r.roas = tAd > 0 ? tAdRev / tAd : r.roas;
    r.adCostPct = (tNet > 0 && tAd > 0) ? tAd / tNet : r.adCostPct;
    if (r.sessions && r.convRate == null) {
      const tItems = bs.reduce((s, b) => s + b.itemsPurch, 0);
      r.convRate = tItems / r.sessions;
    }
  }

  const p = bu && salesTarget ? pace(r.gross, salesTarget, data.daysElapsed, data.daysInMonth) : 0;
  const fc = bu ? forecastRange(bu.daily, r.gross, data.daysElapsed, data.daysInMonth) : null;
  const forecastPct = bu && salesTarget && fc ? fc.center / salesTarget : 0;
  const fStatus = salesTarget ? statusOf(forecastPct) : { tone: "muted" };

  // cảnh báo cho riêng BU này
  const alerts = bu ? buildAlerts([{ name: selectedBU, bu, r, p }], data.daysElapsed, data.daysInMonth) : [];

  if (!bu) return null;
  return (
    <>
          {/* ① KPI — Shopify (thật) + GA4/Ads (skeleton nếu chưa có) */}
          <div className="kpiCards">
            <KpiCard label="Gross Sales" value={fmtUSD(r.gross)} note={salesTarget ? fmtPct(r.gross / salesTarget) + " target" : "no target set"} bar={salesTarget ? r.gross / salesTarget : null} />
            <KpiCard label="Net Sales" value={fmtUSD(r.net)} note="after discount & refund" />
            <KpiCard label="Orders" value={fmtNum(r.orders)} />
            <KpiCard label="AOV" value={fmtUSD(r.aov)} />
            <KpiCard label="Return %" value={fmtPct(r.returnPct)}
              note={tgt.ret != null ? `ceiling ${fmtPct(tgt.ret)}` : "refund / net"}
              evalRes={evalTarget(r.returnPct, tgt.ret, "down")} />
            {SHOW_MARGIN && <KpiCard label="Contribution margin" value={onlineProfit == null ? null : fmtUSD(onlineProfit)}
              note={onlineMargin != null ? `margin ${onlineMargin.toFixed(0)}% · online` : "needs MTD cost"}
              skeleton={onlineProfit == null} />}
            <KpiCard label="Sessions" value={mkSessS == null ? null : fmtNum(mkSessS)}
              note={tgt.traffic != null ? `target ${fmtNum(tgt.traffic)}` : "7 channels total (GA4)"}
              skeleton={mkSessS == null}
              evalRes={mkSessS == null ? null : evalTarget(mkSessS, tgt.traffic, "up")} />
            <KpiCard label="Conv Rate" value={mkConvS == null ? null : fmtPct(mkConvS)}
              note={tgt.conversion != null ? `target ${fmtPct(tgt.conversion)}` : "orders / sessions"}
              skeleton={mkConvS == null}
              evalRes={mkConvS == null ? null : evalTarget(mkConvS, tgt.conversion, "up")} />
            <KpiCard label="ROAS" value={r.roas == null ? null : r.roas.toFixed(1)}
              note={tgt.roas != null ? `target ${tgt.roas.toFixed(1)}` : "ad rev / spend"}
              skeleton={r.roas == null}
              evalRes={r.roas == null ? null : evalTarget(r.roas, tgt.roas, "up")} />
            <KpiCard label="Ad Cost %" value={r.adCostPct == null ? null : fmtPct(r.adCostPct)}
              note={tgt.adcost != null ? `ceiling ${fmtPct(tgt.adcost)}` : "spend / gross"}
              skeleton={r.adCostPct == null}
              evalRes={r.adCostPct == null ? null : evalTarget(r.adCostPct, tgt.adcost, "down")} />
          </div>

          {/* ② Alert — đưa lên cao để quyết định nhanh */}
          <section className="detailAlerts">
            <div className="secHead"><span className="secTitle">Alert</span><span className="iTag rule">rule</span></div>
            {alerts.length === 0
              ? <div className="okLine">No alerts — BU within range</div>
              : alerts.slice(0, 5).map((a, i) => (
                  <div key={i} className="alertLine"><span className={"dot " + a.tone} /><span>{a.text}</span></div>
                ))}
          </section>

          {/* ③ Trend + forecast */}
          <TrendChart daily={bu.daily} target={salesTarget} daysInMonth={data.daysInMonth} />
          <section className="forecastBar" data-tone={fStatus.tone}>
            <div className="fLeft">
              <span className="fLabel">End-of-month forecast {fc && fc.wide ? "(low confidence — early month)" : ""}</span>
              <span className="fVal">{fc ? fmtUSD(fc.low) + " – " + fmtUSD(fc.high) : "—"}</span>
            </div>
            <div className="fRight">
              <span className="fConcl">{fStatus.tone === "pos" ? "On track to hit target" : fStatus.tone === "warn" ? "At risk of missing target" : "Off track to hit target"}</span>
              <span className="fSub">center {fc ? fmtUSD(fc.center) : "—"} · ~{fmtPct(forecastPct)} target</span>
            </div>
          </section>

          {/* ④ SECTION: Sales breakdown — chỉ theo store (online channel) */}
          <SectionCard title="Sales breakdown by store" conclusion="online channel">
            <div className="subBlock">
              <BreakdownTable bu={bu} mode="store" channelFilter="online" />
            </div>
          </SectionCard>

          {/* ⑤ SECTION: Product Type Performance — nguồn MTD, gộp theo cột Type */}
          <ProductTypePerformance product={prod} />

          {/* ⑥ SECTION: Traffic & Conversion — phễu online (Sessions→Orders→Revenue→Margin) */}
          <SectionCard title="Traffic & Conversion" conclusion={mkConvS == null ? null : `CR ${fmtPct(mkConvS)} on ${fmtNum(mkSessS)} sessions`}>
            <FunnelView sessions={mkSessS} convRate={mkConvS} orders={r.orders} gross={r.gross}
              profit={SHOW_MARGIN ? onlineProfit : null} margin={SHOW_MARGIN ? onlineMargin : null} />
          </SectionCard>
    </>
  );
}

// Section container tái dùng — có tiêu đề, tag tùy chọn, và câu kết luận 1 dòng
function SectionCard({ title, tag, conclusion, children }) {
  return (
    <section className="secCard">
      <div className="secHead">
        <span className="secTitle">{title}</span>
        {tag && <span className={"iTag " + (tag === "AI" ? "ai" : "rule")}>{tag}</span>}
        {conclusion && <span className="secConcl">{conclusion}</span>}
      </div>
      {children}
    </section>
  );
}

// Bar tỷ trọng doanh thu giữa các channel + legend
function ChannelBars({ bu }) {
  const colors = { online: "#6E86B8", marketplace: "#9A82C4", wholesale: "#5B9E8A" };
  // giữ mọi channel để legend đầy đủ, nhưng bar chỉ vẽ phần > 0
  const all = Object.entries(bu.channels);
  const total = all.reduce((a, [, c]) => a + c.gross, 0) || 1;
  const segs = all.filter(([, c]) => c.gross > 0);
  return (
    <div className="shareWrap">
      <div className="barRow">
        {segs.map(([name, c]) => {
          const pct = c.gross / total * 100;
          return (
            <div key={name} className="barSeg" style={{ width: pct + "%", background: colors[name] || "var(--ink3)" }} title={`${name} ${pct.toFixed(0)}%`}>
              {pct >= 12 && <span className="barLabel">{name} {Math.round(pct)}%</span>}
            </div>
          );
        })}
      </div>
      <div className="shareLegend">
        {all.map(([name, c]) => {
          const pct = total ? c.gross / total * 100 : 0;
          return (
            <span key={name} className="lgItem">
              <i className="lgDot" style={{ background: colors[name] || "var(--ink3)" }} />
              {name} {pct.toFixed(pct < 1 && pct > 0 ? 1 : 0)}%
            </span>
          );
        })}
      </div>
    </div>
  );
}

function BreakdownTable({ bu, mode, channelFilter }) {
  let rows = buildSplitRows(bu, mode);
  // Lọc chỉ store có bán ở kênh channelFilter (vd online)
  if (channelFilter) {
    rows = rows.filter((row) => (row.channel || "").toLowerCase() === channelFilter);
  }
  const roll = channelFilter ? rollupChannel(bu, channelFilter) : rollupBU(bu);
  return (
    <table className="dataTable compact">
      <thead>
        <tr>
          <th>{mode === "store" ? "Store" : "Channel"}</th>
          {mode === "store" && !channelFilter && <th>Channel</th>}
          <th className="r">Gross</th><th className="r">Net</th>
          <th className="r">Orders</th><th className="r">AOV</th><th className="r">Return %</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const aov = row.orders ? row.gross / row.orders : 0;
          const ret = row.net ? row.refund / row.net : 0;
          const warn = ret > 0.045;
          return (
            <tr key={i} className={warn ? "warnRow" : ""}>
              <td className="strong">{row.label}</td>
              {mode === "store" && !channelFilter && <td>{row.channel}</td>}
              <td className="r">{fmtUSD(row.gross)}</td>
              <td className="r">{fmtUSD(row.net)}</td>
              <td className="r">{fmtNum(row.orders)}</td>
              <td className="r">{fmtUSD(aov)}</td>
              <td className={"r" + (warn ? " negT" : "")}>{fmtPct(ret)}</td>
            </tr>
          );
        })}
        <tr className="totalRow">
          <td className="strong">Total {channelFilter ? "online" : "BU"}</td>
          {mode === "store" && !channelFilter && <td></td>}
          <td className="r">{fmtUSD(roll.gross)}</td>
          <td className="r">{fmtUSD(roll.net)}</td>
          <td className="r">{fmtNum(roll.orders)}</td>
          <td className="r">{fmtUSD(roll.aov)}</td>
          <td className="r">{fmtPct(roll.returnPct)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// Phễu online: Sessions → Conv → Orders → Revenue → (Margin nếu bật)
function FunnelView({ sessions, convRate, orders, gross, profit, margin }) {
  if (sessions == null) {
    return (
      <div className="funnel skel">
        {["Sessions", "Conv Rate", "Orders", "Revenue"].map((s) => (
          <div key={s} className="funStep"><div className="funBarSkel" /><span className="funLabel">{s}</span></div>
        ))}
        <div className="skelNote">Awaiting marketing_data — traffic funnel will show here</div>
      </div>
    );
  }
  const steps = [
    { label: "Sessions", val: fmtNum(sessions) },
    { label: "Conv Rate", val: fmtPct(convRate) },
    { label: "Orders", val: fmtNum(orders) },
    { label: "Revenue", val: fmtUSD(gross) },
  ];
  if (profit != null) steps.push({ label: "Contribution margin", val: fmtUSD(profit), sub: margin != null ? `margin ${margin.toFixed(0)}%` : null });
  return (
    <div className="funnel">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className="funStep"><div className="funVal">{s.val}</div><span className="funLabel">{s.label}</span>{s.sub && <span className="funSub">{s.sub}</span>}</div>
          {i < steps.length - 1 && <span className="funArrow">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// Ads: online vs marketplace (skeleton khi chưa có)
function AdsView({ bu, skeleton }) {
  if (skeleton) {
    return (
      <div className="adsSkel">
        <div className="adsSkelRow"><span>Online</span><div className="funBarSkel" /></div>
        <div className="adsSkelRow"><span>Marketplace</span><div className="funBarSkel" /></div>
        <div className="skelNote">Awaiting ad data — Spend, ROAS, Ad Cost% (split Online / Marketplace)</div>
      </div>
    );
  }
  const ch = bu.channels;
  const mk = (c) => ({
    spend: c.adSpend || 0, rev: c.adRev || 0,
    roas: c.adSpend ? (c.adRev / c.adSpend) : null,
    adCost: c.gross ? (c.adSpend / c.gross) : null,
  });
  const rows = [["Online", mk(ch.online)], ["Marketplace", mk(ch.marketplace)]];
  return (
    <table className="dataTable compact">
      <thead><tr><th>Channel ads</th><th className="r">Spend</th><th className="r">Ad Rev</th><th className="r">ROAS</th><th className="r">Ad Cost %</th></tr></thead>
      <tbody>
        {rows.map(([name, a]) => (
          <tr key={name}>
            <td className="strong">{name}</td>
            <td className="r">{fmtUSD(a.spend)}</td>
            <td className="r">{fmtUSD(a.rev)}</td>
            <td className="r">{a.roas == null ? "—" : a.roas.toFixed(1)}</td>
            <td className="r">{a.adCost == null ? "—" : fmtPct(a.adCost)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// câu kết luận cho section channel
function channelConclusion(bu) {
  const entries = Object.entries(bu.channels).filter(([, c]) => c.gross > 0);
  const total = entries.reduce((a, [, c]) => a + c.gross, 0) || 1;
  if (entries.length === 0) return null;
  const top = entries.sort((a, b) => b[1].gross - a[1].gross)[0];
  return `${top[0]} accounts for ${Math.round(top[1].gross / total * 100)}% of revenue`;
}

function TrendChart({ daily, target, daysInMonth }) {
  if (!daily || daily.length === 0) {
    return (
      <section className="chartCard">
        <div className="tableTitle">Daily revenue vs target pace</div>
        <div className="chartEmpty">Not enough daily data to plot trend.</div>
      </section>
    );
  }
  let cum = 0;
  const pts = daily.map((d) => { cum += d.gross; return { date: d.date, cum }; });
  const W = 640, H = 180, padL = 8, padR = 8, padT = 14, padB = 22;
  const targetEnd = target || Math.max(cum * 1.1, 1);
  const maxY = Math.max(targetEnd, cum) * 1.05;
  const n = daysInMonth || daily.length;
  const x = (i) => padL + (i / Math.max(n - 1, 1)) * (W - padL - padR);
  const y = (v) => H - padB - (v / maxY) * (H - padT - padB);
  const actualPath = pts.map((pt, i) => (i === 0 ? "M" : "L") + x(i) + " " + y(pt.cum)).join(" ");
  const paceStart = { x: x(0), y: y(0) };
  const paceEnd = { x: x(n - 1), y: y(targetEnd) };
  return (
    <section className="chartCard">
      <div className="chartHead">
        <span className="chartTitle">Cumulative revenue vs target pace</span>
        <div className="legend">
          <span className="lg"><i className="lgLine actual" />Actual</span>
          <span className="lg"><i className="lgLine pace" />Target pace</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="chartSvg" preserveAspectRatio="none">
        <line x1={paceStart.x} y1={paceStart.y} x2={paceEnd.x} y2={paceEnd.y} className="paceLine" />
        <path d={actualPath} className="actualLine" />
        {pts.map((pt, i) => (
          <circle key={i} cx={x(i)} cy={y(pt.cum)} r="2.5" className="actualDot" />
        ))}
      </svg>
      <div className="chartAxis">
        <span>{daily[0].date.slice(5)}</span>
        <span>day {daily.length}/{n}</span>
      </div>
    </section>
  );
}

function KpiCard({ label, value, note, bar, skeleton, evalRes }) {
  const toneClass = evalRes ? " " + evalRes.tone + "T" : "";
  return (
    <div className={"kpiCard" + (skeleton ? " skelCard" : "")}>
      <div className="kpiLabel">{label}</div>
      {skeleton
        ? <div className="funBarSkel" style={{ height: 20, marginTop: 4 }} />
        : <div className={"kpiValue" + toneClass}>{value == null ? "—" : value}</div>}
      {bar != null && !skeleton && (
        <div className="kpiBar"><div className="kpiBarFill" style={{ width: Math.min(bar * 100, 100) + "%" }} /></div>
      )}
      {note && <div className="kpiNote">{note}</div>}
    </div>
  );
}

// Tạo dòng bảng theo chế độ tách
function buildSplitRows(bu, mode) {
  if (mode === "channel") {
    return Object.entries(bu.channels)
      .filter(([, c]) => c.gross || c.orders || c.refund)
      .map(([name, c]) => ({ label: name, ...c }));
  }
  if (mode === "store") {
    return bu.stores
      .filter((s) => s.gross || s.orders || s.refund)
      .map((s) => ({ label: s.store, channel: s.channel, ...s }));
  }
  // total: một dòng gộp
  const ch = Object.values(bu.channels);
  const sum = (k) => ch.reduce((a, c) => a + (c[k] || 0), 0);
  return [{ label: "All channels", gross: sum("gross"), net: sum("net"), orders: sum("orders"), refund: sum("refund") }];
}

/* ================= STYLES ================= */
/* =====================================================================
   3 REPORT COMPONENT: BrandReport, ProductReport, InventoryReport
   Nhận props { data, selectedBU }. Data snapshot ở data.product[selectedBU].
   Dùng lớp tính toán PC_* đã thêm ở đầu file.
   Chèn vào cuối dashboard.jsx (trước const CSS hoặc trước export cuối).
   ===================================================================== */

// Khung "no product data yet" dùng chung
function PCNoData({ bu, what }) {
  return (
    <div className="noData">
      <div className="noDataTitle">{bu} — {what}</div>
      <div className="noDataHint">
        This report needs the MTD / Inventory tab in {bu}. Currently only Car has this data.
      </div>
    </div>
  );
}

/* ============================ BRAND REPORT (đã sửa) ============================ */
function BrandReport({ data, selectedBU }) {
  const [rankMode, setRankMode] = React.useState("gross");
  const [showAllBrands, setShowAllBrands] = React.useState(false);  // popup all brand
  const [brandQ, setBrandQ] = React.useState("");                    // search trong popup
  const [selBrand, setSelBrand] = React.useState(null);
  const [recFilter, setRecFilter] = React.useState("all");   // filter khuyến nghị
  const [adQ, setAdQ] = React.useState("");                    // search brand ở bảng ad
  const [adPg, setAdPg] = React.useState(0);
  const [adSort, setAdSort] = React.useState({ key: "adSpend", asc: false });

  React.useEffect(() => { setAdPg(0); }, [selectedBU]);

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.brands || prod.brands.length === 0)
    return <PCNoData bu={selectedBU} what="Brand" />;

  const brands = pcComputeBrands(prod.brands);

  const totGross = brands.reduce((s, b) => s + b.gross, 0);
  const totNet = brands.reduce((s, b) => s + b.net, 0);
  const totProfit = brands.reduce((s, b) => s + b.profit, 0);
  const totAd = brands.reduce((s, b) => s + b.adSpend, 0);
  const totAdRev = brands.reduce((s, b) => s + b.adRev, 0);
  const brandsSold = brands.filter((b) => b.gross > 0).length;
  const deadBrands = brands.filter((b) => b.gross === 0 && b.stock > 0);
  const sorted = [...brands].sort((a, b) => (rankMode === "gross" ? b.gross - a.gross : b.profit - a.profit));
  // BU có ≥2 store? (Heli chỉ HD → false → ẩn khối Đóng góp theo store)
  const multiStore = [
    brands.reduce((s, b) => s + (b.drcGross || 0), 0),
    brands.reduce((s, b) => s + (b.hdGross || 0), 0),
    brands.reduce((s, b) => s + (b.furGross || 0), 0),
  ].filter((v) => v > 0).length > 1;
  const leader = [...brands].sort((a, b) => b.gross - a.gross)[0] || {};
  const profitLeader = [...brands].sort((a, b) => b.profit - a.profit)[0] || {};
  const marginPct = pcPct(totProfit, totNet);
  const roasAll = totAd > 0 ? totAdRev / totAd : null;

  // Items viewed top 50% để phân loại action
  const viewedSorted = [...brands].map((b) => b.itemsViewed).sort((a, b) => b - a);
  const viewedMedian = viewedSorted[Math.floor(viewedSorted.length / 2)] || 0;

  const topN = 8;
  const topBrands = sorted.slice(0, topN);
  const restVal = sorted.slice(topN).reduce((s, b) => s + (rankMode === "gross" ? b.gross : b.profit), 0);
  // max của MỌI thanh (gồm cả "Others") — tránh thanh Others tràn khi long tail lớn hơn top 1
  const topVal = rankMode === "gross" ? (topBrands[0] ? topBrands[0].gross : 1) : (topBrands[0] ? topBrands[0].profit : 1);
  const maxVal = Math.max(topVal, restVal, 1);

  const funnelBrand = selBrand ? brands.find((b) => b.brand === selBrand) : leader;

  // Bảng gộp funnel + efficient: cột Action
  function brandAction(b) {
    const hiViews = b.itemsViewed >= viewedMedian;
    const hiConv = b.buyPerView >= 2;
    if (hiViews && hiConv) return { txt: "Healthy · maintain/scale", key: "scale" };
    if (hiViews && !hiConv) return { txt: "Good views, weak conversion · check price/page", key: "caution" };
    if (!hiViews && hiConv) return { txt: "Good conversion · push marketing", key: "hold" };
    return { txt: "Weak · review", key: "cut" };
  }

  // Bảng ngân sách ad: filter + search + sort + pagination
  const adBrands = brands.filter((b) => b.adSpend > 0)
    .filter((b) => recFilter === "all" || pcAdRecommendation(b).key === recFilter)
    .filter((b) => !adQ || (b.brand || "").toLowerCase().includes(adQ.toLowerCase()))
    .sort((a, b) => {
      const av = Number(a[adSort.key]) || 0;
      const bv = Number(b[adSort.key]) || 0;
      return adSort.asc ? av - bv : bv - av;
    });
  const adPageSize = 12;
  const adPageRows = adBrands.slice(adPg * adPageSize, adPg * adPageSize + adPageSize);
  const clickAdSort = (key) => {
    setAdSort((cur) => ({ key, asc: cur.key === key ? !cur.asc : false }));
    setAdPg(0);
  };
  const adThSort = (key, label) => (
    <th className="thSort" onClick={() => clickAdSort(key)}>
      {label}{adSort.key === key ? (adSort.asc ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Brand
          <span className="repTag">{data.month} MTD</span></div>
        {SHOW_SOURCE_NOTES && <span className="repNote">source: MTD tracking · 3 stores combined</span>}
      </div>

      {/* ① Overview */}
      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Brands sold" value={`${brandsSold} / ${brands.length}`} />
        <PCKpi label="Total Gross" value={pcMoney(totGross)} />
        {SHOW_MARGIN && <PCKpi label="Contribution margin" value={pcMoney(totProfit)} tone="good" sub="after COGS + ad" />}
        <PCKpi label="Contribution margin %" value={`${pcRound(marginPct, 1)}%`} tone="good" />
        <PCKpi label="Revenue leader" value={leader.brand || "—"} sub={`${pcRound(pcPct(leader.gross, totGross),0)}%`} small />
        <PCKpi label="Most profitable" value={profitLeader.brand || "—"} sub={pcMoney(profitLeader.profit)} tone="good" small />
        <PCKpi label="Dead brands" value={deadBrands.length} tone="warn" />
      </div>

      {/* ② Ranking */}
      <div className="secHeadRow">
        <SectionLabel inline>② Ranking</SectionLabel>
        <div className="toggle">
          <button className={rankMode === "gross" ? "tgOn" : "tgOff"} onClick={() => setRankMode("gross")}>Revenue</button>
          <button className={rankMode === "profit" ? "tgOn" : "tgOff"} onClick={() => setRankMode("profit")}>Profit</button>
        </div>
      </div>
      <div className="panel">
        {topBrands.map((b) => {
          const val = rankMode === "gross" ? b.gross : b.profit;
          return (
            <div className="pcBarRow" key={b.brand}>
              <div className="pcBarTop"><span>{b.brand}</span>
                <span className="muted">{pcMoney(val)} · {pcRound(pcPct(val, rankMode === "gross" ? totGross : totProfit), 0)}%{b.adSpend > 0 ? ` · ad ${pcMoney(b.adSpend)}` : ""}{b.roas != null ? ` · ROAS ${b.roas.toFixed(1)}` : (b.adSpend > 0 ? " · ROAS —" : "")}</span></div>
              <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(val, maxVal) + "%" }} /></div>
            </div>
          );
        })}
        {restVal > 0 && (
          <div className="pcBarRow brandRest" onClick={() => { setBrandQ(""); setShowAllBrands(true); }} title="View all brands">
            <div className="pcBarTop"><span>Others ({sorted.length - topN}) <span className="restHint">· click to view all ↗</span></span><span className="muted">{pcMoney(restVal)}</span></div>
            <div className="pcBarTrack"><div className="pcBarFill rest" style={{ width: pcPct(restVal, maxVal) + "%" }} /></div>
          </div>
        )}
      </div>

      {/* Popup toàn bộ brand + search */}
      {showAllBrands && (() => {
        const totAll = sorted.reduce((s, b) => s + (rankMode === "gross" ? b.gross : b.profit), 0);
        const list = sorted
          .map((b) => ({ brand: b.brand, val: rankMode === "gross" ? b.gross : b.profit, adSpend: b.adSpend, roas: b.roas }))
          .filter((b) => !brandQ || b.brand.toLowerCase().includes(brandQ.toLowerCase()));
        return (
          <div className="mdOverlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAllBrands(false); }}>
            <div className="mdPanel">
              <div className="mdHead">
                <span className="mdTitle">All brand ({sorted.length}) — {rankMode === "gross" ? "Revenue" : "Profit"}</span>
                <button className="mdClose" onClick={() => setShowAllBrands(false)}>✕</button>
              </div>
              <input className="searchBox mdSearch" placeholder="🔍 Search brand" value={brandQ} autoFocus
                onChange={(e) => setBrandQ(e.target.value)} />
              <div className="mdList">
                {list.map((b, i2) => (
                  <div className="mdRow" key={b.brand}>
                    <span className="mdRank">{i2 + 1}</span>
                    <span className="mdName">{b.brand}</span>
                    <span className={"mdVal " + (b.val < 0 ? "negT" : "")}>{pcMoney(b.val)}</span>
                    <span className="mdPct muted">{b.adSpend > 0 ? "ad " + pcMoney(b.adSpend) + (b.roas != null ? " · ROAS " + b.roas.toFixed(1) : "") : "—"}</span>
                  </div>
                ))}
                {list.length === 0 && <div className="mdEmpty muted">No brand matches "{brandQ}"</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ③ Funnel + Hiệu quả (GỘP) + cột Action */}
      <SectionLabel>③ Funnel &amp; performance by brand</SectionLabel>
      <div className="warnNote">⚠ 3-step funnel: Items viewed → Checkout → Purchase (Cart dropped — data unreliable)</div>
      <div className="panel">
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">Brand</th><th>Items viewed</th><th>Checkout</th><th>Purchased</th><th>Buy/view</th><th>Gross</th><th className="l actCol">Action</th></tr></thead>
          <tbody>
            {sorted.slice(0, 10).map((b) => {
              const act = brandAction(b);
              return (
                <tr key={b.brand}>
                  <td className="l">{b.brand}</td>
                  <td>{pcNum(b.itemsViewed)}</td>
                  <td>{pcNum(b.checkout)}</td>
                  <td>{pcNum(b.itemsPurch)}</td>
                  <td className={b.buyPerView >= 2 ? "good" : b.buyPerView < 1 ? "bad" : ""}>{b.buyPerView}%</td>
                  <td>{pcMoney(b.gross)}</td>
                  <td className={"l actCol rec-" + act.key}>{act.txt}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <div className="drillHead">
          <span>Drill-down funnel 1 brand</span>
          <select className="miniSel" value={funnelBrand ? funnelBrand.brand : ""} onChange={(e) => setSelBrand(e.target.value)}>
            {sorted.map((b) => <option key={b.brand} value={b.brand}>{b.brand}</option>)}
          </select>
        </div>
        {funnelBrand && (
          <div className="funnel3">
            <FunnelBar label="Items viewed" value={funnelBrand.itemsViewed} max={funnelBrand.itemsViewed} />
            <FunnelBar label={`Checkout (${pcRound(pcPct(funnelBrand.checkout, funnelBrand.itemsViewed),1)}%)`} value={funnelBrand.checkout} max={funnelBrand.itemsViewed} />
            <FunnelBar label={`Purchased (${pcRound(pcPct(funnelBrand.itemsPurch, funnelBrand.checkout),1)}%)`} value={funnelBrand.itemsPurch} max={funnelBrand.itemsViewed} tone="good" />
          </div>
        )}
        <div className="fineNote">Action: buy/view ≥ 2% = high conversion · Items viewed ≥ median = high interest</div>
      </div>

      {/* ④ Ad budget decision (filter + search) */}
      <SectionLabel>④ Ad budget decision</SectionLabel>
      <div className="warnNote">⚠ ROAS data unusually high — decide after auditing ROAS + full Cost</div>
      <div className="panel">
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search brand" value={adQ}
            onChange={(e) => { setAdQ(e.target.value); setAdPg(0); }} />
          <div className="chipRow">
            {[["all","All"],["scale","Scale up"],["hold","Hold"],["caution","Caution"],["cut","Cut/Reduce"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (recFilter === k ? "chipOn" : "")}
                onClick={() => { setRecFilter(k); setAdPg(0); }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">Brand</th>{adThSort("roas", "ROAS")}{SHOW_MARGIN && adThSort("marginPct", "Margin")}{adThSort("itemsPurchAd", "Items from ad")}{adThSort("itemsViewed", "Items viewed")}{adThSort("buyPerView", "Buy/view")}{adThSort("adSpend", "Ad spend")}{adThSort("adRev", "Rev from ad")}<th className="l recCol">Recommendation</th></tr></thead>
          <tbody>
            {adPageRows.map((b) => {
              const rec = pcAdRecommendation(b);
              return (
                <tr key={b.brand}>
                  <td className="l">{b.brand}</td>
                  <td className={b.roas != null && b.roas < 2 ? "bad" : ""}>{b.roas == null ? "—" : b.roas}</td>
                  {SHOW_MARGIN && <td className={b.marginPct < 30 ? "warn" : ""}>{pcRound(b.marginPct,0)}%</td>}
                  <td>{pcNum(b.itemsPurchAd || 0)}</td>
                  <td>{pcNum(b.itemsViewed)}</td>
                  <td>{b.buyPerView}%</td>
                  <td>{pcMoney(b.adSpend)}</td>
                  <td>{pcMoney(b.adRev)}</td>
                  <td className={"l recCol rec-" + rec.key}>{rec.label}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <div className="fineNote">{adBrands.length} brands with ad spend &gt; 0</div>
        <Pager pg={adPg} setPg={setAdPg} total={adBrands.length} pageSize={adPageSize} verbose />
        <div className="fineNote">Cut: ROAS&lt;2, or negative margin + ROAS&lt;5 · Scale: ROAS≥8 + margin≥30% · Reduce: ROAS&lt;5 (anchor blended ROAS 8)</div>
      </div>

      {/* Đóng góp theo store — chỉ hiện khi BU có ≥2 store (Heli 1 store HD → ẩn) */}
      {multiStore && (
        <>
          <SectionLabel>⑤ Contribution by store</SectionLabel>
          <div className="panel">
            <div className="legendRow"><span className="lg lg1" />DRC<span className="lg lg2" />HD<span className="lg lg3" />Fur</div>
            {sorted.slice(0, 6).map((b) => {
              const t = b.drcGross + b.hdGross + b.furGross;
              if (t <= 0) return null;
              return (
                <div className="pcBarRow" key={b.brand}>
                  <div className="pcBarTop"><span>{b.brand}</span><span className="muted">{pcMoney(t)}</span></div>
                  <div className="pcStackBar">
                    {b.drcGross > 0 && <div className="stk s1" style={{ width: pcPct(b.drcGross, t) + "%" }} />}
                    {b.hdGross > 0 && <div className="stk s2" style={{ width: pcPct(b.hdGross, t) + "%" }} />}
                    {b.furGross > 0 && <div className="stk s3" style={{ width: pcPct(b.furGross, t) + "%" }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Dead brands + AI — số thứ tự động theo khối trên */}
      <SectionLabel>{multiStore ? "⑥" : "⑤"} Dead brands + tips</SectionLabel>
      {deadBrands.length > 0 && (
        <div className="alertBox warn">
          <div className="abTitle">{deadBrands.length} brands with stock but no sales this month:</div>
          <div className="abBody">{deadBrands.slice(0, 8).map((b) => `${b.brand} (stock ${pcNum(b.stock)})`).join(" · ")}</div>
        </div>
      )}
      <div className="panel aiPanel">
        <div className="aiHead">Recommendations <span className="aiTag">AI</span></div>
        <div className="aiBody">
          {leader.brand} leads revenue ({pcRound(pcPct(leader.gross,totGross),0)}%).
          {profitLeader.brand !== leader.brand && ` ${profitLeader.brand} most profitable (${pcMoney(profitLeader.profit)}).`}
          {" "}See section ④ to decide ad budget by ROAS + margin.
        </div>
      </div>
    </div>
  );
}

/* ============================ PRODUCT REPORT (đã sửa: gộp bảng) ============================ */
function ProductReport({ data, selectedBU }) {
  const [topMode, setTopMode] = React.useState("gross");
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);
  const [flt, setFlt] = React.useState("all");   // filter cảnh báo
  const [bcgFilter, setBcgFilter] = React.useState(null);  // filter nhóm BCG (bấm ô)
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [brandFilter, setBrandFilter] = React.useState("all");
  const [sortK, setSortK] = React.useState(null);
  const [sortAsc, setSortAsc] = React.useState(true);
  const tableRef = React.useRef(null);

  React.useEffect(() => {
    setPg(0);
    setTypeFilter("all");
    setBrandFilter("all");
    setBcgFilter(null);
  }, [selectedBU]);

  function pickBcg(group) {
    setBcgFilter(bcgFilter === group ? null : group);
    setPg(0);
    setTimeout(() => { if (tableRef.current) tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50);
  }

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.skus) return <PCNoData bu={selectedBU} what="Product performance" />;

  const skus = pcComputeSkus(prod.skus);          // giờ là 6.999 SKU
  const active = skus.filter((s) => s.active !== false && (s.units > 0 || s.adSpend > 0 || s.itemsViewed > 0 || s.checkout > 0));
  const sold = skus.filter((s) => s.units > 0);
  const totGross = skus.reduce((s, x) => s + x.gross, 0);
  const totUnits = skus.reduce((s, x) => s + x.units, 0);
  const leader = [...skus].sort((a, b) => b.gross - a.gross)[0] || {};
  const waste = pcWasteSkus(skus);
  const oversell = skus.filter((s) => s.stock < 0).length;

  const bcgCount = { Star: 0, "Cash Cow": 0, "Question Mark": 0, Dog: 0 };
  sold.forEach((s) => { if (s.bcg) bcgCount[s.bcg]++; });

  const topSorted = [...sold].sort((a, b) =>
    topMode === "gross" ? b.gross - a.gross : topMode === "units" ? b.units - a.units : b.profit - a.profit);
  const topList = topSorted.slice(0, 6);
  const maxTop = topList[0] ? (topMode === "gross" ? topList[0].gross : topMode === "units" ? topList[0].units : topList[0].profit) : 1;

  // Gắn nhãn cho mỗi SKU (thứ tự ưu tiên)
  function skuFlag(s) {
    if (s.roas != null && s.roas >= 8 && s.marginAfterAd >= 30 && s.adSpend > 10) return "top";   // top performance
    if (s.roas != null && s.roas < 2 && s.adSpend > 10) return "burnad";                            // đốt ad (ROAS<2, spend>$10)
    if (s.units === 0 && s.itemsViewed > 0) return "traffic";
    if (s.units > 0 && s.stock > 0 && s.stock <= s.units) return "low";
    return "";
  }
  const skuType = (s) => (s.type || "").trim() || "Uncategorized";
  const typeOptions = Array.from(new Set(skus.map(skuType))).sort((a, b) => a.localeCompare(b));
  const brandOptions = Array.from(new Set(skus.map((s) => (s.brand || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

  // Bảng gộp: toàn bộ SKU + filter + filter BCG + search
  const tableRows = skus.filter((s) => {
    if (bcgFilter && s.bcg !== bcgFilter) return false;
    if (typeFilter !== "all" && skuType(s) !== typeFilter) return false;
    if (brandFilter !== "all" && (s.brand || "").trim() !== brandFilter) return false;
    if (flt !== "all") {
      const f = skuFlag(s);
      if (f !== flt) return false;
    }
    if (q && !((s.sku || "").toLowerCase().includes(q.toLowerCase()) || (s.name || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const textSortKeys = new Set(["sku", "name", "brand", "bcg"]);
  const sortedTableRows = sortK ? tableRows.slice().sort((a, b) => {
    const av = a[sortK];
    const bv = b[sortK];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (textSortKeys.has(sortK)) {
      const cmp = String(av).localeCompare(String(bv));
      return sortAsc ? cmp : -cmp;
    }
    const cmp = (Number(av) || 0) - (Number(bv) || 0);
    return sortAsc ? cmp : -cmp;
  }) : tableRows;
  const clickProductSort = (key) => {
    if (sortK === key) setSortAsc(!sortAsc);
    else {
      setSortK(key);
      setSortAsc(textSortKeys.has(key));
    }
    setPg(0);
  };
  const productThSort = (key, label, className) => (
    <th className={(className ? className + " " : "") + "thSort"} onClick={() => clickProductSort(key)}>
      {label}{sortK === key ? (sortAsc ? " ↑" : " ↓") : ""}
    </th>
  );
  const pageSize = 15;
  const pageRows = sortedTableRows.slice(pg * pageSize, pg * pageSize + pageSize);

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Product performance
          <span className="repTag">{data.month} MTD</span></div>
        {SHOW_SOURCE_NOTES && <span className="repNote">source: MTD tracking · 3 stores combined</span>}
      </div>

      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="SKUs sold" value={pcNum(sold.length)} />
        <PCKpi label="Active SKUs" value={pcNum(active.length)} sub="sold/ad/traffic" />
        <PCKpi label="Units sold" value={pcNum(totUnits)} />
        <PCKpi label="Total Gross" value={pcMoney(totGross)} />
        <PCKpi label="Burning ad, no sale" value={pcNum(waste.burnAd.length)} tone="warn" />
        <PCKpi label="Oversell" value={pcNum(oversell)} tone="warn" />
      </div>

      <div className="secHeadRow">
        <SectionLabel inline>② Top SKU</SectionLabel>
        <div className="toggle">
          <button className={topMode === "gross" ? "tgOn" : "tgOff"} onClick={() => setTopMode("gross")}>Revenue</button>
          <button className={topMode === "units" ? "tgOn" : "tgOff"} onClick={() => setTopMode("units")}>Units</button>
          <button className={topMode === "profit" ? "tgOn" : "tgOff"} onClick={() => setTopMode("profit")}>Profit</button>
        </div>
      </div>
      <div className="panel">
        {topList.map((s) => {
          const val = topMode === "gross" ? s.gross : topMode === "units" ? s.units : s.profit;
          return (
            <div className="pcBarRow" key={s.sku}>
              <div className="pcBarTop"><span>{s.sku} <span className="muted">· {s.name ? s.name.slice(0, 32) : s.brand}</span></span>
                <span className="muted">{topMode === "units" ? pcNum(val) + " sp" : pcMoney(val)}{s.adSpend > 0 ? ` · ad ${pcMoney(s.adSpend)}` : ""}{s.roas != null ? ` · ROAS ${s.roas.toFixed(1)}` : (s.adSpend > 0 ? " · ROAS —" : "")}</span></div>
              <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(val, maxTop) + "%" }} /></div>
            </div>
          );
        })}
      </div>

      <SectionLabel>③ SKU segments (BCG — sales × margin after ad)</SectionLabel>
      <div className="bcgGrid">
        <button className={"bcgCell star" + (bcgFilter === "Star" ? " bcgActive" : "")} onClick={() => pickBcg("Star")}><b>★ Star ({bcgCount.Star})</b><span>high sales + high margin → push</span></button>
        <button className={"bcgCell cow" + (bcgFilter === "Cash Cow" ? " bcgActive" : "")} onClick={() => pickBcg("Cash Cow")}><b>🐄 Cash Cow ({bcgCount["Cash Cow"]})</b><span>high sales + low margin → hold</span></button>
        <button className={"bcgCell qm" + (bcgFilter === "Question Mark" ? " bcgActive" : "")} onClick={() => pickBcg("Question Mark")}><b>❓ Question Mark ({bcgCount["Question Mark"]})</b><span>low sales + high margin → test push</span></button>
        <button className={"bcgCell dog" + (bcgFilter === "Dog" ? " bcgActive" : "")} onClick={() => pickBcg("Dog")}><b>🐕 Dog ({bcgCount.Dog})</b><span>low sales + low margin → liquidate</span></button>
      </div>
      <div className="fineNote">Threshold: gross ≥ ${PC_THRESHOLDS.bcgGrossHigh} · units ≥ {PC_THRESHOLDS.bcgUnitsHigh} · margin after ad ≥ {PC_THRESHOLDS.bcgMarginHigh}%. Click a cell to filter that SKU group in the table below.</div>

      {/* ④ SKU detail table GỘP (đốt tài nguyên + tồn kho + chi tiết) */}
      <SectionLabel>④ SKU detail table</SectionLabel>
      <div className="panel" ref={tableRef}>
        {bcgFilter && (
          <div className="bcgBadge">Filtering group: <b>{bcgFilter}</b>
            <button className="bcgClear" onClick={() => setBcgFilter(null)}>✕ clear filter</button>
          </div>
        )}
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search SKU / Product Name" value={q}
            onChange={(e) => { setQ(e.target.value); setPg(0); }} />
          <select className="miniSel" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPg(0); }}>
            <option value="all">All Product Types</option>
            {typeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <select className="miniSel" value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPg(0); }}>
            <option value="all">All Brands</option>
            {brandOptions.map((brand) => <option key={brand} value={brand}>{brand}</option>)}
          </select>
          <div className="chipRow">
            {[["all","All"],["top","🏆 Top performance"],["burnad","💸 Burning ad"],["traffic","👀 High items viewed"],["low","⚠ Low stock"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (flt === k ? "chipOn" : "")} onClick={() => { setFlt(k); setPg(0); }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr>{productThSort("sku", "SKU", "l")}{productThSort("name", "Product", "l")}{productThSort("brand", "Brand", "l")}{productThSort("bcg", "Group", "l")}{productThSort("units", "Units")}{productThSort("gross", "Gross")}{SHOW_MARGIN && productThSort("marginAfterAd", "Margin")}{productThSort("itemsViewed", "Items viewed")}{productThSort("roas", "ROAS")}{productThSort("adSpend", "Ad")}{productThSort("stock", "Stock")}</tr></thead>
          <tbody>
            {pageRows.map((s) => (
              <tr key={s.sku}>
                <td className="l">{s.sku}</td>
                <td className="l nameCol">{s.name || "—"}</td>
                <td className="l muted">{s.brand}</td>
                <td className={"l bcg-" + (s.bcg || "none").replace(/\s/g, "")}>{s.bcg || "—"}</td>
                <td>{s.units}</td>
                <td>{pcMoney(s.gross)}</td>
                {SHOW_MARGIN && <td>{s.hasCost && s.units > 0 ? pcRound(s.marginAfterAd, 0) + "%" : "—"}</td>}
                <td>{pcNum(s.itemsViewed)}</td>
                <td>{s.roas == null ? "—" : s.roas}</td>
                <td>{pcMoney(s.adSpend)}</td>
                <td className={s.stock < 0 ? "bad" : ""}>{s.stock}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">{tableRows.length} SKUs · filtered from all {skus.length}</div>
        <Pager pg={pg} setPg={setPg} total={tableRows.length} pageSize={pageSize} />
      </div>

      <SectionLabel>⑤ Recommendations</SectionLabel>
      <div className="panel aiPanel">
        <div className="aiHead">Recommendations <span className="aiTag">AI</span></div>
        <div className="aiBody">
          {leader.sku} leads revenue ({pcMoney(leader.gross)}).
          {waste.burnTraffic.length > 0 && ` ${waste.burnTraffic.length} SKUs draw traffic but 0 sales — filter "High traffic" to view.`}
          {waste.burnAd.length > 0 && ` ${waste.burnAd.length} SKUs burn ad with no orders — filter "Burning ad" to view.`}
        </div>
      </div>
    </div>
  );
}

/* ============================ INVENTORY REPORT (đã sửa: gộp bảng) ============================ */
function InventoryReport({ data, selectedBU }) {
  const [showCen, setShowCen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [pg, setPg] = React.useState(0);
  const [flt, setFlt] = React.useState("all");

  const prod = data.product && data.product[selectedBU];
  if (!prod || !prod.inventory) return <PCNoData bu={selectedBU} what="Inventory" />;

  const skus = pcComputeSkus(prod.skus || []);
  const invAll = pcComputeInventory(prod.inventory, skus);
  const CEN = "CEN Racing";
  const inv = showCen ? invAll : invAll.filter((i) => i.brand !== CEN);

  const totVal = inv.reduce((s, i) => s + (i.stockVal || 0), 0);
  const cenVal = invAll.filter((i) => i.brand === CEN).reduce((s, i) => s + (i.stockVal || 0), 0);
  const willOOS = inv.filter((i) => /will go oos/i.test(i.status)).length;
  const oos = inv.filter((i) => /^oos/i.test(i.status)).length;
  const highStock = inv.filter((i) => /high stock/i.test(i.status)).length;
  const onOrderCnt = inv.filter((i) => i.onOrder > 0).length;
  const oversell = inv.filter((i) => i.isOversell).length;

  const byBrand = pcInventoryByBrand(inv);
  const maxBrandVal = byBrand[0] ? byBrand[0].stockVal : 1;

  const stuck = inv.filter((i) => i.stockVal > 0 && i.sold === 0).sort((a, b) => b.stockVal - a.stockVal);

  // Nhãn loại cho bảng gộp
  function invFlag(i) {
    if (i.isOversell) return "oversell";
    const low = /will go oos|^oos|low/i.test(i.status);
    if (low && i.onOrder === 0) return "urgent";      // cần đặt gấp
    if (low && i.onOrder > 0) return "ordered";       // đã đặt
    return "";
  }
  const tableRows = inv.filter((i) => {
    if (flt !== "all") { if (invFlag(i) !== flt) return false; }
    if (q && !((i.sku || "").toLowerCase().includes(q.toLowerCase()) || (i.brand || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const pageSize = 15;
  const pageRows = tableRows.slice(pg * pageSize, pg * pageSize + pageSize);

  return (
    <div className="report">
      <div className="repHead">
        <div className="repTitle"><span className="mark">◆</span> {selectedBU} — Inventory
          <span className="repTag">{data.month}</span></div>
        <button className={"cenToggle " + (showCen ? "on" : "off")} onClick={() => setShowCen(!showCen)}>
          CEN Racing: {showCen ? "SHOWN — click to hide" : "HIDDEN — click to show"}
        </button>
      </div>
      {!showCen && (
        <div className="warnNote">Hiding CEN Racing ({pcMoney(cenVal)} stock capital — special case). Figures below exclude CEN.</div>
      )}

      <SectionLabel>① Overview</SectionLabel>
      <div className="kpiGrid">
        <PCKpi label="Inventory value" value={pcMoney(totVal)} sub={showCen ? "incl. CEN" : "CEN excluded"} />
        <PCKpi label="Will OOS" value={pcNum(willOOS)} tone="warn" />
        <PCKpi label="Out of stock (OOS)" value={pcNum(oos)} tone="warn" />
        <PCKpi label="High stock" value={pcNum(highStock)} />
        <PCKpi label="On order" value={pcNum(onOrderCnt)} tone="good" />
        <PCKpi label="Oversell" value={pcNum(oversell)} tone="warn" />
      </div>

      <SectionLabel>② Stock capital by brand</SectionLabel>
      <div className="panel">
        {byBrand.slice(0, 7).map((b) => (
          <div className="pcBarRow" key={b.brand}>
            <div className="pcBarTop"><span>{b.brand}</span>
              <span className="muted">{pcMoney(b.stockVal)} · {pcRound(pcPct(b.stockVal, totVal), 0)}%</span></div>
            <div className="pcBarTrack"><div className="pcBarFill" style={{ width: pcPct(b.stockVal, maxBrandVal) + "%" }} /></div>
          </div>
        ))}
      </div>
      {showCen && cenVal > 0 && (
        <div className="alertBox info">
          <div className="abTitle">Special case: CEN Racing</div>
          <div className="abBody">Inventory value {pcMoney(cenVal)} · very low turnover · mostly Super High Stock → handle separately (negotiate supplier / liquidate batch / stop restocking).</div>
        </div>
      )}

      {/* ③ Locked capital (giữ riêng) */}
      <SectionLabel>③ Locked capital — high stock, 0 sales (liquidate first)</SectionLabel>
      <div className="panel">
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Brand</th><th className="l">Status</th><th>Stock</th><th>Value</th><th className="l">Flag</th></tr></thead>
          <tbody>
            {stuck.slice(0, 8).map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.brand}</td>
                <td className="l">{i.status}</td>
                <td>{pcNum(i.stock)}</td>
                <td className="bad">{pcMoney(i.stockVal)}</td>
                <td className="l">{i.isDiscontinued ? "Discontinued" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">Discontinued flag = top liquidation priority (no restock)</div>
      </div>

      {/* ④ Inventory detail table GỘP (cần nhập + oversell + chi tiết) */}
      <SectionLabel>④ Inventory detail table</SectionLabel>
      <div className="panel">
        <div className="filterRow">
          <input className="searchBox" placeholder="🔍 Search SKU / brand" value={q}
            onChange={(e) => { setQ(e.target.value); setPg(0); }} />
          <div className="chipRow">
            {[["all","All"],["urgent","⚠ Reorder urgently"],["ordered","✓ Ordered"],["oversell","🔴 Oversell"]].map(([k,lbl]) => (
              <button key={k} className={"chip " + (flt === k ? "chipOn" : "")} onClick={() => { setFlt(k); setPg(0); }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="tblWrap"><table className="dataTbl">
          <thead><tr><th className="l">SKU</th><th className="l">Brand</th><th className="l">Status</th><th className="l">Level</th><th>Stock</th><th>On Order</th><th className="l">Back</th><th>Value</th><th>Sell</th></tr></thead>
          <tbody>
            {pageRows.map((i) => (
              <tr key={i.sku}>
                <td className="l">{i.sku}</td>
                <td className="l muted">{i.brand}</td>
                <td className="l">{i.status}</td>
                <td className="l muted">{i.level}</td>
                <td className={i.stock < 0 ? "bad" : ""}>{i.stock}</td>
                <td>{i.onOrder}</td>
                <td className="l">{pcDate(i.expected)}</td>
                <td>{pcMoney(i.stockVal)}</td>
                <td>{i.sold}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div className="fineNote">{tableRows.length} SKUs · filtered from {inv.length}</div>
        <Pager pg={pg} setPg={setPg} total={tableRows.length} pageSize={pageSize} />
      </div>

      <SectionLabel>⑤ Recommendations</SectionLabel>
      <div className="panel aiPanel">
        <div className="aiHead">Recommendations <span className="aiTag">AI</span></div>
        <div className="aiBody">
          {!showCen && `CEN Racing holds ${pcMoney(cenVal)} stock capital (hidden) — handle separately. `}
          {tableRows.length > 0 && `Filter "Reorder urgently" to see low-stock SKUs not yet ordered. `}
          {oversell > 0 && `${oversell} oversell SKUs — filter to check stock.`}
        </div>
      </div>
    </div>
  );
}


function SectionLabel({ children, inline }) {
  return <div className={"secLabel" + (inline ? " inline" : "")}>{children}</div>;
}
function PCKpi({ label, value, sub, tone, small }) {
  return (
    <div className="kpiCard">
      <div className="kpiLabel">{label}</div>
      <div className={"kpiValue" + (small ? " sm" : "") + (tone === "good" ? " good" : tone === "warn" ? " warn" : "")}>{value}</div>
      {sub && <div className="kpiSub">{sub}</div>}
    </div>
  );
}
function FunnelBar({ label, value, max, tone }) {
  const h = max > 0 ? Math.max(6, Math.round((value / max) * 80)) : 6;
  return (
    <div className="fnCol">
      <div className={"fnBar" + (tone === "good" ? " good" : "")} style={{ height: h + "px" }} />
      <div className="fnLabel">{label}<br />{pcNum(value)}</div>
    </div>
  );
}
function Pager({ pg, setPg, total, pageSize, verbose = false }) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  return (
    <div className={"pager" + (verbose ? " verbose" : "")}>
      <button disabled={pg === 0} onClick={() => setPg(pg - 1)}>{verbose ? "Previous" : "‹"}</button>
      <span>{verbose ? "Page " : ""}{pg + 1} / {pages}</span>
      <button disabled={pg >= pages - 1} onClick={() => setPg(pg + 1)}>{verbose ? "Next" : "›"}</button>
    </div>
  );
}


const CSS = `
:root{
  --bg:#0F1216; --panel:#161A20; --panel2:#1A1F26; --line:#232A33;
  --ink:#E8EDF2; --ink2:#98A3AE; --ink3:#69737E;
  /* trạng thái — hạ bão hòa, dịu mắt khi nhìn lâu */
  --pos:#5B9E6F; --posBg:#16241C; --warn:#C79A4B; --warnBg:#26200F; --neg:#D4687A; --negBg:#271519;
  /* accent trung tính (slate xanh trầm) — KHÔNG trùng hệ xanh-lá trạng thái */
  --accent:#7C8CA3; --accentInk:#AAB6C7;
  --mono:'SF Mono',ui-monospace,'Roboto Mono',Menlo,Consolas,monospace;
  --sans:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
}
*{box-sizing:border-box}
.app{background:var(--bg);min-height:100vh;color:var(--ink);font-family:var(--sans);font-size:14px;line-height:1.45}
.topbar{display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-bottom:1px solid var(--line);position:sticky;top:0;background:rgba(14,17,22,.92);backdrop-filter:blur(8px);z-index:5;flex-wrap:wrap;gap:10px}
.brand{display:flex;align-items:baseline;gap:10px}
.mark{color:var(--accentInk);font-size:13px}
.brandName{font-weight:600;letter-spacing:.2px}
.period{color:var(--ink3);font-size:12px;font-family:var(--mono)}
.snapshotBadge{font:600 10px var(--mono);letter-spacing:.35px;text-transform:uppercase;padding:4px 8px;border-radius:999px;border:1px solid var(--line);color:var(--ink2);background:var(--panel2)}
.snapshotBadge.final{color:var(--pos);border-color:rgba(91,158,111,.45);background:var(--posBg)}
.snapshotBadge.draft{color:var(--warn);border-color:rgba(199,154,75,.45);background:var(--warnBg)}
.snapshotBadge.missing{color:var(--neg);border-color:rgba(212,104,122,.45);background:var(--negBg)}
.monthPick{background:var(--panel2);color:var(--ink);border:1px solid var(--line);border-radius:6px;padding:4px 8px;font-size:12px;font-family:var(--mono);cursor:pointer}
.sourcePick{background:var(--panel2);color:var(--ink);border:1px solid var(--line);border-radius:6px;padding:4px 8px;font:600 11px var(--mono);cursor:pointer}
.sourcePick.live{color:var(--pos);border-color:rgba(91,158,111,.45)}
.sourcePick.draft{color:var(--warn);border-color:rgba(199,154,75,.55);background:var(--warnBg)}
.banner{display:flex;align-items:center;gap:10px;padding:11px 22px;font-size:13px}
.banner.error{background:var(--negBg);color:var(--neg);border-bottom:1px solid var(--neg)}
.banner.info{background:var(--panel2);color:var(--ink2);border-bottom:1px solid var(--line)}
.banner.snapshotTest{background:rgba(91,158,111,.08);color:var(--ink2);border-bottom:1px solid rgba(91,158,111,.25)}
.banner.snapshotTest.draft{background:var(--warnBg);border-bottom-color:rgba(199,154,75,.45)}
.banner.snapshotTest .bDot{background:var(--pos)}
.banner.snapshotTest.draft .bDot{background:var(--warn)}
.banner strong{color:inherit}
.banner .bDot{width:8px;height:8px;border-radius:50%;background:var(--neg);flex-shrink:0}
.loadingWrap{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:80px 20px;color:var(--ink2);font-size:13px}
.spinner{width:28px;height:28px;border:3px solid var(--line);border-top-color:var(--accentInk);border-radius:50%;animation:spin .8s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.tabs{display:flex;gap:4px}
.tab{background:transparent;border:1px solid transparent;color:var(--ink2);padding:6px 14px;border-radius:7px;cursor:pointer;font-size:13px;font-family:inherit}
.tab:hover{color:var(--ink)}
.tab.active{background:var(--panel2);border-color:var(--line);color:var(--ink)}
.wrap{max-width:1120px;margin:0 auto;padding:20px}
.cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px}
@media(max-width:760px){.cards{grid-template-columns:1fr}}
.buCard{text-align:left;background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:16px 18px;cursor:pointer;color:inherit;font-family:inherit;transition:border-color .15s,transform .15s}
.buCard:hover:not(:disabled){border-color:var(--accent);transform:translateY(-1px)}
.buCard.empty{cursor:default;opacity:.6}
.buCardTop{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
.buName{font-weight:600;font-size:15px}
.badge{font-size:11px;padding:3px 9px;border-radius:20px;font-weight:500}
.badge.pos{background:var(--posBg);color:var(--pos)}
.badge.warn{background:var(--warnBg);color:var(--warn)}
.badge.neg{background:var(--negBg);color:var(--neg)}
.badge.muted{background:var(--panel2);color:var(--ink3)}
.bigNum{font-family:var(--mono);font-size:30px;font-weight:700;letter-spacing:-.6px}
.subNum{color:var(--ink2);font-size:12px;margin:3px 0 14px;font-family:var(--mono)}
.kpiRow{display:flex;justify-content:space-between;border-top:1px solid var(--line);padding-top:11px}
.kpiRow>div{display:flex;flex-direction:column;gap:2px}
.kLabel{color:var(--ink3);font-size:11px}
.kVal{font-family:var(--mono);font-size:13px}
.emptyHint{color:var(--ink3);font-size:12px;padding:14px 0}
.companyStrip{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px}
@media(max-width:760px){.companyStrip{grid-template-columns:repeat(2,1fr)}}
.cItem{background:var(--panel2);border-radius:9px;padding:12px 14px;display:flex;flex-direction:column;gap:4px}
.cItem span{color:var(--ink2);font-size:12px}
.cItem strong{font-family:var(--mono);font-size:18px;font-weight:600}
.tableWrap{background:var(--panel);border:1px solid var(--line);border-radius:10px;overflow:hidden;margin-bottom:14px}
.tableTitle{padding:12px 16px;font-size:13px;font-weight:600;border-bottom:1px solid var(--line);color:var(--ink)}
.dataTable{width:100%;border-collapse:collapse;font-size:13px}
.dataTable th{text-align:left;padding:9px 16px;color:var(--ink3);font-weight:500;font-size:12px;background:var(--panel2)}
.dataTable td{padding:10px 16px;border-top:1px solid var(--line);font-family:var(--mono)}
.dataTable td.strong{font-family:var(--sans);font-weight:500}
.dataTable .r{text-align:right}
.dataTable td.good{color:var(--pos)}
.dataTable td.bad{color:var(--neg)}
.dataTable tfoot td{border-top:2px solid var(--line);background:var(--panel2);font-weight:600}
.dataTable tr.dim td{color:var(--ink3)}
.posT{color:var(--pos)} .warnT{color:var(--warn)} .negT{color:var(--neg)}
.warnRow{background:var(--negBg)}
.totalRow td{border-top:2px solid var(--line);font-weight:600;background:var(--panel2)}
.detailHead{display:flex;align-items:center;gap:14px;margin-bottom:18px;flex-wrap:wrap}
.back{background:transparent;border:none;color:var(--accentInk);cursor:pointer;font-size:13px;font-family:inherit;padding:0}
.buSwitch,.split{display:flex;gap:3px;background:var(--panel2);padding:3px;border-radius:8px}
.seg{background:transparent;border:none;color:var(--ink2);padding:5px 13px;border-radius:6px;cursor:pointer;font-size:12px;font-family:inherit}
.seg:hover:not(:disabled){color:var(--ink)}
.seg.active{background:var(--panel);color:var(--ink);box-shadow:0 1px 2px rgba(0,0,0,.3)}
.seg:disabled{opacity:.35;cursor:default}
.split{margin-left:auto}
.kpiCards{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:8px;margin-bottom:14px}
@media(max-width:860px){.kpiCards{grid-template-columns:repeat(2,1fr)}}
.kpiCard{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:13px 15px}
.kpiLabel{color:var(--ink2);font-size:12px;margin-bottom:5px}
.kpiValue{font-family:var(--mono);font-size:22px;font-weight:700;letter-spacing:-.3px}
.kpiBar{height:3px;background:var(--line);border-radius:2px;margin:8px 0 5px;overflow:hidden}
.kpiBarFill{height:100%;background:var(--pos)}
.kpiNote{color:var(--ink3);font-size:11px}
.forecastBar{display:flex;align-items:center;justify-content:space-between;border-radius:10px;padding:14px 18px;margin-bottom:14px;border:1px solid var(--line)}
.forecastBar[data-tone=pos]{background:var(--posBg);border-left:3px solid var(--pos)}
.forecastBar[data-tone=warn]{background:var(--warnBg);border-left:3px solid var(--warn)}
.forecastBar[data-tone=neg]{background:var(--negBg);border-left:3px solid var(--neg)}
.fLeft{display:flex;flex-direction:column;gap:2px}
.fLabel{font-size:12px;color:var(--ink2)}
.fVal{font-family:var(--mono);font-size:23px;font-weight:700;letter-spacing:-.3px}
.fRight{text-align:right;display:flex;flex-direction:column;gap:2px}
.fConcl{font-weight:600;font-size:14px}
.forecastBar[data-tone=pos] .fConcl{color:var(--pos)}
.forecastBar[data-tone=warn] .fConcl{color:var(--warn)}
.forecastBar[data-tone=neg] .fConcl{color:var(--neg)}
.fSub{font-size:11px;color:var(--ink2);font-family:var(--mono)}
.pendingNote{display:flex;align-items:center;gap:8px;color:var(--ink3);font-size:12px;padding:4px 2px}
.pendDot{width:7px;height:7px;border-radius:50%;background:var(--warn)}
.noData{text-align:center;padding:60px 20px}
.noDataTitle{font-size:16px;font-weight:600;margin-bottom:6px}
.noDataHint{color:var(--ink3);font-size:13px}
.foot{text-align:center;color:var(--ink3);font-size:11px;padding:20px;border-top:1px solid var(--line);font-family:var(--mono)}
.insightBlock{margin-bottom:16px}
.insightGrid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
@media(max-width:860px){.insightGrid{grid-template-columns:1fr}}
.insightCol{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:14px 16px;min-height:96px}
.insightHead{display:flex;align-items:center;gap:8px;margin-bottom:10px}
.iTitle{font-weight:600;font-size:13px}
.iTag{font-size:10px;padding:2px 7px;border-radius:5px;font-family:var(--mono)}
.iTag.ai{background:#1B1E3A;color:#9BA0FF}
.iTag.rule{background:var(--panel2);color:var(--ink3)}
.insightBody{font-size:12.5px;line-height:1.5}
.insightBody.muted{color:var(--ink3)}
.alertLine{display:flex;align-items:flex-start;gap:8px;padding:3px 0}
.alertLine .dot{width:7px;height:7px;border-radius:50%;margin-top:5px;flex-shrink:0}
.dot.neg{background:var(--neg)} .dot.warn{background:var(--warn)} .dot.pos{background:var(--pos)}
.okLine{color:var(--pos);font-size:12.5px}
.moreLine{color:var(--ink3);font-size:11px;margin-top:5px}
.wowCell{color:var(--ink3)}
.updated{color:var(--ink3);font-size:11px;font-family:var(--mono);display:flex;align-items:center;gap:5px}
.updated::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--pos);display:inline-block}
.chartCard{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:15px 16px;margin-bottom:14px}
.chartHead{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
.chartTitle{font-size:13px;font-weight:600}
.legend{display:flex;gap:14px}
.lg{display:flex;align-items:center;gap:6px;color:var(--ink2);font-size:11px}
.lgLine{width:14px;height:2px;display:inline-block}
.lgLine.actual{background:var(--accentInk)}
.lgLine.pace{background:var(--ink3);border-top:2px dashed var(--ink3);height:0}
.chartSvg{width:100%;height:180px;display:block}
.paceLine{stroke:var(--ink3);stroke-width:1.5;stroke-dasharray:5 4}
.actualLine{fill:none;stroke:var(--accentInk);stroke-width:2}
.actualDot{fill:var(--accentInk)}
.chartAxis{display:flex;justify-content:space-between;color:var(--ink3);font-size:11px;font-family:var(--mono);margin-top:4px}
.chartEmpty{color:var(--ink3);font-size:12px;padding:30px 0;text-align:center}
.kpiCards .skelCard{opacity:.85}
@media(max-width:860px){.kpiCards{grid-template-columns:repeat(2,1fr)}}
.detailAlerts{background:var(--panel);border:1px solid var(--line);border-left:3px solid var(--warn);border-radius:10px;padding:14px 16px;margin-bottom:14px}
.secCard{background:transparent;border:1px solid var(--line);border-radius:10px;padding:15px;margin-bottom:14px}
.secHead{display:flex;align-items:center;gap:10px;margin-bottom:14px}
.secTitle{font-weight:600;font-size:14px}
.secConcl{margin-left:auto;color:var(--ink2);font-size:12px;font-family:var(--mono)}
.subBlock{margin-bottom:18px}
.subBlock:last-child{margin-bottom:0}
.subTitle{color:var(--ink2);font-size:12px;margin-bottom:8px}
.barRow{display:flex;height:30px;border-radius:6px;overflow:hidden;margin-bottom:10px;background:var(--panel2)}
.barSeg{display:flex;align-items:center;padding:0 8px;min-width:0;transition:width .3s}
.barLabel{font-size:11px;color:#0E1116;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.shareWrap{margin-bottom:10px}
.shareLegend{display:flex;gap:16px;flex-wrap:wrap;margin-top:2px}
.lgItem{display:flex;align-items:center;gap:6px;color:var(--ink2);font-size:12px;font-family:var(--mono)}
.lgDot{width:9px;height:9px;border-radius:2px;display:inline-block}
.dataTable.compact td,.dataTable.compact th{padding:7px 12px;font-size:12px}
.funnel{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.funStep{background:var(--panel2);border-radius:8px;padding:12px 16px;flex:1;min-width:90px}
.funVal{font-family:var(--mono);font-size:18px;font-weight:600}
.funLabel{color:var(--ink3);font-size:11px;display:block;margin-top:3px}.funSub{font-size:9px;color:var(--pos);margin-top:1px;}
.funArrow{color:var(--ink3)}
.funnel.skel .funStep{flex:1}
.funBarSkel{height:20px;background:linear-gradient(90deg,var(--panel2),var(--line),var(--panel2));border-radius:5px;background-size:200% 100%;animation:sk 1.5s infinite}
@keyframes sk{0%{background-position:200% 0}100%{background-position:-200% 0}}
.skelNote{width:100%;color:var(--ink3);font-size:11px;margin-top:8px;text-align:center}
.adsSkel{display:flex;flex-direction:column;gap:10px}
.adsSkelRow{display:flex;align-items:center;gap:12px}
.adsSkelRow span{width:100px;color:var(--ink3);font-size:12px}
.adsSkelRow .funBarSkel{flex:1}
.detailShell{display:flex;max-width:1200px;margin:0 auto;min-height:calc(100vh - 110px)}
.sidebar{width:210px;flex-shrink:0;padding:20px 14px;border-right:1px solid var(--line);display:flex;flex-direction:column;gap:14px}
.buSwitch.vert{display:flex}
.reportNav{display:flex;flex-direction:column;gap:16px;margin-top:4px}
.navGroup{display:flex;flex-direction:column;gap:2px}
.navGroupTitle{color:var(--ink3);font-size:10px;letter-spacing:.6px;text-transform:uppercase;padding:0 8px 4px;font-weight:600}
.navItem{display:flex;align-items:center;justify-content:space-between;background:transparent;border:none;color:var(--ink2);padding:7px 10px;border-radius:7px;cursor:pointer;font-size:13px;font-family:inherit;text-align:left}
.navItem:hover{background:var(--panel2);color:var(--ink)}
.navItem.active{background:var(--panel2);color:var(--ink);font-weight:500}
.navItem.pending{color:var(--ink3)}
.navDot{width:6px;height:6px;border-radius:50%;background:var(--warn);flex-shrink:0}
.reportArea{flex:1;padding:20px;min-width:0}
.comingSoon{padding:50px 20px;max-width:520px}
.csTitle{font-size:16px;font-weight:600;margin-bottom:8px}
.csHint{color:var(--ink3);font-size:13px;margin-bottom:24px}
.csSkelRows{display:flex;flex-direction:column;gap:12px}
.csSkelRows .funBarSkel{height:44px}
@media(max-width:760px){
  .detailShell{flex-direction:column}
  .sidebar{width:auto;border-right:none;border-bottom:1px solid var(--line);flex-direction:row;flex-wrap:wrap;align-items:center}
  .reportNav{flex-direction:row;flex-wrap:wrap;gap:8px}
  .navGroup{flex-direction:row;align-items:center;gap:4px}
  .navGroupTitle{display:none}
}


/* ===== Product/Brand/Inventory report styles ===== */
.report{max-width:100%;}
.repHead{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;padding-bottom:12px;border-bottom:.5px solid #2A2F37;}
.repTitle{font-weight:500;font-size:15px;display:flex;align-items:center;gap:8px;}
.repTag{font-size:12px;color:#8B929C;background:#1B2027;padding:2px 8px;border-radius:6px;}
.repNote{font-size:11px;color:#8B929C;background:#20262E;padding:3px 8px;border-radius:5px;}
.secLabel{font-size:11px;color:#6B7280;letter-spacing:.5px;margin:18px 0 6px;}
.secLabel.inline{margin:0;}
.secHeadRow{display:flex;justify-content:space-between;align-items:center;margin:18px 0 6px;}
.toggle{display:flex;gap:4px;}
.toggle button{font-size:10px;padding:3px 9px;border-radius:5px;border:none;cursor:pointer;font-family:inherit;}
.tgOn{background:#2E4A6E;color:#B5D4F4;}
.tgOff{background:#20262E;color:#8B929C;}
.kpiGrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(112px,1fr));gap:8px;margin-bottom:12px;}
.kpiCard{background:#1B2027;border-radius:8px;padding:9px 11px;}
.kpiLabel{font-size:10px;color:#8B929C;}
.kpiValue{font-size:18px;font-weight:500;margin-top:2px;}
.kpiValue.sm{font-size:13px;margin-top:5px;}
.kpiValue.good{color:#5B9E6F;}
.kpiValue.warn{color:#C79A4B;}
.kpiSub{font-size:9px;color:#6B7280;margin-top:2px;}
.panel{background:#1B2027;border-radius:8px;padding:14px;margin-bottom:6px;}
.pcBarRow{margin-bottom:9px;}
.pcBarTop{display:flex;justify-content:space-between;font-size:11px;margin-bottom:3px;}
.pcBarTrack{height:8px;background:#20262E;border-radius:3px;}
.pcBarFill{height:100%;background:#6E86B8;border-radius:3px;}
.pcBarFill.rest{background:#4A515C;}
.muted{color:#8B929C;}
.tblWrap{overflow-x:auto;}
.dataTbl{width:100%;font-size:11px;border-collapse:collapse;min-width:480px;}
.dataTbl th{color:#8B929C;text-align:right;border-bottom:.5px solid #2A2F37;padding:6px 0;font-weight:400;}
.dataTbl th.l,.dataTbl td.l{text-align:left;}
.dataTbl td{text-align:right;border-bottom:.5px solid #232830;padding:6px 0;}
.dataTbl td.good{color:#5B9E6F;}
.dataTbl td.bad{color:#D4687A;}
.dataTbl td.warn{color:#C79A4B;}
.rec-scale{color:#5B9E6F;}
.rec-hold{color:#B5D4F4;}
.rec-caution{color:#C79A4B;}
.rec-cut{color:#D4687A;}
.warnNote{font-size:10px;color:#C79A4B;margin-bottom:8px;}
.fineNote{font-size:9px;color:#6B7280;margin-top:8px;}
.drillHead{display:flex;justify-content:space-between;align-items:center;margin:12px 0;font-size:12px;font-weight:500;}
.miniSel{font-size:11px;background:#20262E;color:#B5D4F4;padding:3px 10px;border-radius:5px;border:none;font-family:inherit;}
.funnel3{display:flex;gap:8px;align-items:flex-end;height:100px;}
.fnCol{flex:1;text-align:center;display:flex;flex-direction:column;justify-content:flex-end;}
.fnBar{background:#6E86B8;border-radius:4px;}
.fnBar.good{background:#5B9E6F;}
.fnLabel{font-size:9px;color:#8B929C;margin-top:5px;}
.legendRow{display:flex;gap:8px;font-size:10px;color:#8B929C;margin-bottom:10px;align-items:center;}
.lg{width:9px;height:9px;border-radius:2px;display:inline-block;margin-left:6px;}
.lg1{background:#6E86B8;}.lg2{background:#9A82C4;}.lg3{background:#5B9E6F;}
.pcStackBar{display:flex;height:16px;border-radius:3px;overflow:hidden;}
.stk{height:100%;}.stk.s1{background:#6E86B8;}.stk.s2{background:#9A82C4;}.stk.s3{background:#5B9E6F;}
.bcgGrid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.bcgCell{background:#1B2027;padding:10px 12px;border-left:3px solid #4A515C;display:flex;flex-direction:column;gap:4px;text-align:left;cursor:pointer;font-family:inherit;width:100%;transition:background .15s;border-top:none;border-right:none;border-bottom:none;}
.bcgCell b{font-size:12px;}.bcgCell span{font-size:10px;color:#8B929C;}
.bcgCell.star{border-left-color:#5B9E6F;}.bcgCell.star b{color:#5B9E6F;}
.bcgCell.cow{border-left-color:#6E86B8;}.bcgCell.cow b{color:#B5D4F4;}
.bcgCell.qm{border-left-color:#C79A4B;}.bcgCell.qm b{color:#C79A4B;}
.bcgCell.dog{border-left-color:#D4687A;}.bcgCell.dog b{color:#D4687A;}
.bcg-Star{color:#5B9E6F;}.bcg-CashCow{color:#B5D4F4;}.bcg-QuestionMark{color:#C79A4B;}.bcg-Dog{color:#D4687A;}
.splitGrid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:6px;}
.alertBox{background:#1B2027;padding:11px 13px;border-left:3px solid #4A515C;margin-bottom:6px;}
.alertBox.bad{border-left-color:#D4687A;}
.alertBox.warn{border-left-color:#C79A4B;}
.alertBox.good{border-left-color:#5B9E6F;}
.alertBox.info{border-left-color:#6E86B8;}
.abTitle{font-size:11px;font-weight:500;margin-bottom:6px;}
.alertBox.bad .abTitle{color:#D4687A;}
.alertBox.warn .abTitle{color:#C79A4B;}
.alertBox.good .abTitle{color:#5B9E6F;}
.alertBox.info .abTitle{color:#B5D4F4;}
.abBody{font-size:10px;color:#8B929C;line-height:1.8;}
.aiPanel{margin-top:6px;}
.aiHead{font-size:13px;font-weight:500;margin-bottom:8px;display:flex;align-items:center;gap:8px;}
.aiTag{font-size:10px;color:#7C8CA3;background:#20262E;padding:2px 7px;border-radius:5px;}
.aiBody{font-size:12px;color:#8B929C;line-height:1.6;}
.filterRow{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;gap:10px;}
.searchBox{flex:1;background:#20262E;border:none;color:#E4E7EB;padding:6px 10px;border-radius:5px;font-size:11px;font-family:inherit;}
.pager{display:flex;justify-content:center;align-items:center;gap:12px;margin-top:10px;font-size:11px;color:#8B929C;}
.pager button{background:#20262E;border:none;color:#B5D4F4;width:26px;height:26px;border-radius:5px;cursor:pointer;}
.pager button:disabled{opacity:.3;cursor:default;}
.pager.verbose button{width:auto;padding:0 10px;}
.cenToggle{font-size:11px;padding:5px 12px;border-radius:6px;border:none;cursor:pointer;font-family:inherit;}
.cenToggle.off{background:#2E4A6E;color:#B5D4F4;}
.cenToggle.on{background:#4A3A2E;color:#C79A4B;}
@media(max-width:640px){.bcgGrid,.splitGrid{grid-template-columns:1fr;}}
.chipRow{display:flex;gap:5px;flex-wrap:wrap;}
.chip{font-size:10px;padding:4px 10px;border-radius:5px;border:none;cursor:pointer;font-family:inherit;background:#20262E;color:#8B929C;}
.chipOn{background:#2E4A6E;color:#B5D4F4;}
.dataTbl td.recCol,.dataTbl th.recCol{padding-left:18px;}
.dataTbl td.actCol,.dataTbl th.actCol{padding-left:18px;}
.filterRow{flex-wrap:wrap;gap:8px;}
.mkScoreRow{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:16px;}
.mkScore{background:linear-gradient(135deg,#1B2433,#161B22);border:0.5px solid #2A3548;border-radius:10px;padding:11px 12px;}
.mkScore.hl{border-color:#35506E;}
.mkScoreLabel{font-size:9px;color:#8B929C;}
.mkScoreVal{font-size:16px;font-weight:600;color:#E4E7EB;}
.mkScoreSub{font-size:8px;color:#8B929C;}
.mkScoreSub.posT{color:#5B9E6F;}.mkScoreSub.warnT{color:#C79A4B;}.mkScoreSub.negT{color:#D4687A;}
.mkToggle{display:flex;align-items:center;gap:6px;margin-bottom:14px;}
.mkToggleLabel{font-size:10px;color:#6B7280;margin-right:2px;}
.mkTgBtn{font-size:11px;padding:4px 12px;border-radius:6px;background:#1B2027;border:0.5px solid #2A2F37;color:#8B929C;cursor:pointer;font-family:inherit;}
.mkTgBtn.active{background:#22303F;color:#6E86B8;border-color:#35506E;}
.mkCard{background:#161B22;border:0.5px solid #2A2F37;border-radius:10px;padding:14px;margin-bottom:16px;}
.mkBarRow{margin-bottom:11px;}
.mkBarHead{display:flex;justify-content:space-between;align-items:center;font-size:11px;margin-bottom:4px;}
.mkBarName{display:flex;align-items:center;gap:6px;color:#E4E7EB;}
.mkDot{width:8px;height:8px;border-radius:2px;display:inline-block;}
.mkBarNums{color:#8B929C;display:flex;align-items:center;gap:8px;}
.mkTgtBadge{font-size:9px;padding:1px 7px;border-radius:4px;}
.mkTgtBadge.pos{background:#1E3A2A;color:#5B9E6F;}
.mkTgtBadge.warn{background:#3A2F1A;color:#C79A4B;}
.mkTgtBadge.neg{background:#3A1F26;color:#D4687A;}
.mkBarTrack{height:6px;background:#1B2027;border-radius:3px;}
.mkBarFill{height:100%;border-radius:3px;}
.mkCardFoot{border-top:0.5px solid #2A2F37;padding-top:8px;margin-top:4px;font-size:10px;color:#6B7280;}
.mkNote{font-size:9px;color:#6B7280;margin-top:8px;}
.ovBrandBlock{margin-top:14px;padding-top:12px;border-top:1px solid var(--line);}
.obRow{display:grid;grid-template-columns:130px minmax(0,1fr) 170px 150px;gap:12px;align-items:center;padding:5px 0;font-size:12px;}
.obName{font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.obTrack{height:6px;background:rgba(110,134,184,.15);border-radius:3px;overflow:hidden;display:block;}
.obFill{display:block;height:100%;background:#6E86B8;border-radius:3px;}
.obFill.neg{background:#D4687A;}
.obFill.none{width:0;}
.obVal{text-align:right;font-variant-numeric:tabular-nums;}
.obTgt{color:var(--ink3);font-size:11px;}
.obBadge{font-size:11px;white-space:nowrap;}
.obBadge:empty{display:none;}
.obRest{font-size:11px;padding:6px 0 0;}
.obAll{margin-left:auto;background:none;border:none;color:#6E86B8;font-size:11px;cursor:pointer;padding:0;}
.obAll:hover{text-decoration:underline;}
.ovCoGrid{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-bottom:6px;}
.ovCoCard{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:12px 14px;}
.ovCoLbl{font-size:11px;color:var(--ink2);margin-bottom:5px;}
.ovCoVal{font-size:20px;font-weight:600;}
.ovCoSub{font-size:10px;color:var(--ink3);margin-top:3px;}
@media(max-width:1100px){.ovCoGrid{grid-template-columns:repeat(3,1fr);}}
.ovSectionHead{display:flex;align-items:center;justify-content:space-between;margin:18px 0 10px;}
.ovModeToggle{display:flex;gap:2px;border:0.5px solid var(--line);border-radius:7px;padding:2px;}
.ovModeToggle button{background:none;border:none;color:var(--ink3);font-size:11px;padding:4px 12px;border-radius:5px;cursor:pointer;}
.ovModeToggle button.on{background:rgba(110,134,184,.15);color:var(--ink);}
.ovcCard{background:var(--panel);border:1px solid var(--line);border-radius:12px;padding:14px 16px;margin-bottom:10px;}
.ovcRow.ovcEmpty{display:flex;align-items:center;gap:12px;background:var(--panel);border:1px solid var(--line);border-radius:12px;padding:12px 16px;margin-bottom:10px;opacity:.7;}
.ovcHead{display:flex;align-items:center;gap:12px;margin-bottom:10px;}
.ovcName{font-size:16px;font-weight:600;}
.ovcRR{font-size:12px;color:var(--ink2);}
.ovcDetail{margin-left:auto;font-size:12px;color:#6E86B8;cursor:pointer;}
.ovcDetail:hover{text-decoration:underline;}
.ovcTotRow{display:flex;align-items:center;gap:18px;padding:9px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line);flex-wrap:wrap;}
.ovcTotVal{font-size:21px;font-weight:600;}
.ovcBar{flex:1;max-width:190px;min-width:120px;}
.ovcBarLbl{font-size:10px;color:var(--ink3);margin-bottom:3px;}
.ovcBarTrack{height:6px;background:rgba(99,158,111,.15);border-radius:3px;}
.ovcBarFill{height:100%;background:#5B9E6F;border-radius:3px;}
.ovcTotMeta{font-size:13px;color:var(--ink2);}
.ovcTotMeta b{font-weight:500;color:var(--ink);}
.ovcChans{display:flex;gap:10px;flex-wrap:wrap;padding:12px 0 2px;}
.ovcCardCh{flex:1;min-width:200px;background:var(--panel2);border-radius:8px;padding:11px 13px;border-left:3px solid var(--line);}
.ovcChTop{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;}
.ovcChLabel{font-size:12px;color:var(--ink2);}
.ovcChPct{font-size:11px;color:var(--ink3);}
.ovcChRev{font-size:18px;font-weight:600;margin-bottom:6px;}
.ovcChMetrics{font-size:11px;display:flex;flex-wrap:wrap;gap:3px 8px;}
.ovcChMetrics .sub{color:var(--ink2);}
.ovcBrand{display:flex;align-items:center;gap:8px;margin-top:10px;padding-top:10px;border-top:1px solid var(--line);font-size:12px;flex-wrap:wrap;}
.ovcBrandLbl{color:var(--ink2);}
.ovcBrandItem b{font-weight:500;}
.ovcBrandMore{font-size:11px;}
.ovcBrandToggle{margin-left:auto;color:#6E86B8;font-size:11px;cursor:pointer;}
.ovcBrandToggle:hover{text-decoration:underline;}
.ovcBrandPanel{margin-top:10px;padding-top:8px;border-top:1px dashed var(--line);}
.ovcBpRow{display:grid;grid-template-columns:120px minmax(0,1fr) 150px 46px;gap:12px;align-items:center;font-size:12px;padding:3px 0;}
.ovcBpName{font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ovcBpTrack{height:5px;background:rgba(110,134,184,.15);border-radius:3px;overflow:hidden;}
.ovcBpFill{display:block;height:100%;background:#6E86B8;border-radius:3px;}
.ovcBpFill.neg{background:#D4687A;}
.ovcBpFill.none{width:0;}
.ovcBpVal{text-align:right;font-variant-numeric:tabular-nums;}
.ovcBpBadge{text-align:right;font-size:11px;}
.chipHint{font-size:10px;color:var(--ink3);margin:4px 2px 0;font-style:italic;}
.chipOff{opacity:.35;cursor:default;}
.chipSep{width:1px;align-self:stretch;background:var(--line);margin:0 4px;}
.thSort{cursor:pointer;user-select:none;white-space:nowrap;}
.thSort:hover{color:var(--ink);}
.tdSub{font-size:9px;color:var(--ink3);margin-top:1px;}
.tdName{max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.mkSegGrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-bottom:14px;}
.mkSegCard{background:var(--panel);border:1px solid var(--line);border-radius:8px;padding:10px 12px;cursor:pointer;transition:border-color .15s;}
.mkSegCard:hover{border-color:#3a4356;}
.mkSegCard.on{border-color:#6E86B8;background:rgba(110,134,184,.08);}
.mkSegLbl{font-size:10px;color:var(--ink3);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px;}
.mkSegN{font-size:16px;font-weight:600;}
.mkSegRev{font-size:10px;color:var(--ink3);margin-top:2px;}
.mkInsight.click{cursor:pointer;border-radius:6px;transition:background .15s;}
.mkInsight.click:hover{background:rgba(110,134,184,.07);}
.mkInsight{display:flex;gap:8px;align-items:flex-start;font-size:11px;color:#B5B9C0;padding:4px 0;line-height:1.5;}
.rrTag{font-size:10px;color:#8B929C;background:#1B2027;border:0.5px solid #2A2F37;padding:2px 8px;border-radius:5px;white-space:nowrap;}
.brandRest{cursor:pointer;border-radius:6px;padding:4px 6px;margin:0 -6px;transition:background .12s;}
.brandRest:hover{background:#1B2027;}
.restHint{font-size:9px;color:#6E86B8;}
.mdOverlay{position:fixed;inset:0;background:rgba(5,8,12,0.72);z-index:200;display:flex;align-items:center;justify-content:center;padding:24px;}
.mdPanel{background:#12161D;border:0.5px solid #2A3548;border-radius:12px;width:min(560px,92vw);max-height:78vh;display:flex;flex-direction:column;padding:16px;box-shadow:0 18px 60px rgba(0,0,0,0.55);}
.mdHead{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;}
.mdTitle{font-size:12px;font-weight:600;color:#E4E7EB;}
.mdClose{background:#1B2027;border:0.5px solid #2A2F37;color:#8B929C;border-radius:6px;width:24px;height:24px;cursor:pointer;font-size:11px;line-height:1;}
.mdClose:hover{color:#E4E7EB;background:#242B35;}
.mdSearch{width:100%;margin-bottom:8px;}
.mdList{overflow-y:auto;flex:1;min-height:0;}
.mdRow{display:flex;align-items:center;gap:10px;padding:6px 8px;border-bottom:0.5px solid #1E242D;font-size:11px;}
.mdRow:hover{background:#171C24;}
.mdRank{color:#5A6270;font-size:9px;width:24px;flex-shrink:0;text-align:right;}
.mdName{flex:1;color:#D3D7DC;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.mdVal{font-family:'JetBrains Mono',monospace;font-size:11px;color:#E4E7EB;width:76px;text-align:right;flex-shrink:0;}
.mdPct{font-size:10px;min-width:44px;text-align:right;flex-shrink:0;white-space:nowrap;}
.mdEmpty{padding:16px;text-align:center;font-size:11px;}
.hlLow{color:#D4687A;background:#3A1F26;padding:1px 6px;border-radius:4px;font-size:10px;}
.hlOver{color:#C79A4B;background:#3A2F1A;padding:1px 6px;border-radius:4px;font-size:10px;}
.hlOk{color:#5B9E6F;background:#1E3A2A;padding:1px 6px;border-radius:4px;font-size:10px;}
.mkStackLegend{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:14px;font-size:9px;color:#8B929C;}
.mkLegItem{display:flex;align-items:center;gap:4px;}
.mkStackRow{margin-bottom:13px;}
.mkStackHead{display:flex;justify-content:space-between;font-size:10.5px;margin-bottom:4px;}
.mkStackName{color:#E4E7EB;font-weight:500;}
.mkStackTot{color:#8B929C;}
.mkStackBar{display:flex;height:22px;border-radius:4px;overflow:hidden;background:#1B2027;}
.mkStackSeg{display:flex;align-items:center;justify-content:center;font-size:8px;color:#fff;min-width:0;overflow:hidden;}
.mkWebGrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px;}
.mkWebCard{background:#161B22;border:0.5px solid #2A2F37;border-radius:10px;padding:13px;}
.mkWebHead{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px;padding-bottom:8px;border-bottom:0.5px solid #2A2F37;}
.mkWebName{font-size:12px;font-weight:600;color:#6E86B8;}
.mkWebTot{font-size:13px;font-weight:600;}
.mkWebLine{display:flex;justify-content:space-between;font-size:10px;line-height:2;color:#B5B9C0;}
.mkWebLine.muted{color:#6B7280;}
.mkWebCh{display:flex;align-items:center;gap:5px;}
.mkWebNum{color:#8B929C;}
@media(max-width:820px){.mkScoreRow{grid-template-columns:repeat(2,1fr);}.mkWebGrid{grid-template-columns:1fr;}}
.emKpiRow{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:18px;}
.emKpi{background:linear-gradient(135deg,#1B2433,#161B22);border:0.5px solid #2A3548;border-radius:10px;padding:11px 13px;}
.emKpiLabel{font-size:9px;color:#8B929C;}
.emKpiVal{font-size:17px;font-weight:600;color:#E4E7EB;}
.emKpiTgt{font-size:8px;margin-top:1px;}
.emKpiTgt.pos{color:#5B9E6F;}.emKpiTgt.warn{color:#C79A4B;}.emKpiTgt.neg{color:#D4687A;}.emKpiTgt.muted{color:#6B7280;}
.emTwoCol{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px;}
.emCard{background:#161B22;border:0.5px solid #2A2F37;border-radius:10px;padding:12px;}
.emBar{margin-bottom:10px;}
.emBarTop{display:flex;justify-content:space-between;font-size:11px;margin-bottom:3px;}
.emBarTop .onlineC{color:#6E86B8;}.emBarTop .mktC{color:#9A82C4;}
.emBarTrack{height:6px;border-radius:3px;}
.emBarTrack.online{background:#22303F;}.emBarTrack.mkt{background:#2B2440;}
.emBarFill{height:100%;border-radius:3px;}
.emBarFill.online{background:#6E86B8;}.emBarFill.mkt{background:#9A82C4;}
.emTotal{border-top:0.5px solid #2A2F37;padding-top:8px;font-size:11px;display:flex;justify-content:space-between;}
.emTotal span{color:#8B929C;}
.emSite{font-size:10px;padding:1px 6px;border-radius:4px;}
.emSite.online{background:#22303F;color:#6E86B8;}.emSite.mkt{background:#2B2440;color:#9A82C4;}.emSite.whole{background:#1E3A2A;color:#5B9E6F;}
.emListHealth{display:flex;gap:14px;flex-wrap:wrap;font-size:10px;color:#8B929C;margin-top:8px;padding-top:8px;border-top:0.5px solid #2A2F37;}
.emCustRow{display:flex;gap:8px;margin-bottom:10px;}
.emCustBox{flex:1;background:#1B2027;border-radius:6px;padding:8px;}
.emCustLabel{font-size:9px;color:#8B929C;}
.emCustVal{font-size:15px;font-weight:600;}
.emCustPct{font-size:8px;color:#6B7280;}
.emDiscount{font-size:10px;color:#8B929C;line-height:1.8;}
.emInsight{display:flex;gap:8px;align-items:flex-start;font-size:11px;color:#B5B9C0;padding:4px 0;line-height:1.5;}
.stIcon.neg{color:#D4687A;}.stIcon.pos{color:#5B9E6F;}
.muted2{color:#6B7280;}
@media(max-width:820px){.emKpiRow{grid-template-columns:repeat(2,1fr);}.emTwoCol{grid-template-columns:1fr;}}
.ovSectionLabel{font-size:11px;color:#6B7280;letter-spacing:0.5px;margin:6px 0;}
.ovCompanyStrip{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:22px;}
.ovCoItem{background:linear-gradient(135deg,#1B2433,#161B22);border:0.5px solid #2A3548;border-radius:10px;padding:13px 15px;}
.ovCoItem span{font-size:10px;color:#8B929C;display:block;}
.ovCoItem strong{font-size:20px;font-weight:600;color:#E4E7EB;}
.ovBU{background:#161B22;border:0.5px solid #2A2F37;border-radius:12px;padding:18px;margin-bottom:12px;}
.ovBUEmpty{opacity:0.7;}
.ovBUHead{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;}
.ovBUHeadL{display:flex;align-items:center;gap:10px;}
.ovBUName{font-size:15px;font-weight:600;}
.ovBULink{font-size:11px;color:#6E86B8;cursor:pointer;}
.ovBUEmptyHint{font-size:11px;color:#6B7280;}
.ovTotalRow{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:16px;padding-bottom:16px;border-bottom:0.5px solid #2A2F37;}
.ovTotLabel{font-size:10px;color:#8B929C;}
.ovTotVal{font-size:18px;font-weight:600;color:#E4E7EB;}
.ovTotSub{font-size:9px;color:#8B929C;}
.ovChan{margin-bottom:12px;}
.ovChanHead{display:flex;align-items:center;gap:8px;margin-bottom:8px;}
.ovDot{width:8px;height:8px;border-radius:2px;}
.ovDot.online{background:#6E86B8;}
.ovDot.mkt{background:#9A82C4;}
.ovDot.whole{background:#5B9E6F;}
.ovChanName{font-size:12px;font-weight:600;}
.ovChanPct{font-size:10px;padding:1px 7px;border-radius:4px;}
.ovChanPct.online{background:#22303F;color:#6E86B8;}
.ovChanPct.mkt{background:#2B2440;color:#9A82C4;}
.ovChanPct.whole{background:#1E3A2A;color:#5B9E6F;}
.ovChanNote{font-size:9px;color:#C79A4B;}
.ovChanCells{display:grid;grid-template-columns:repeat(7,1fr);gap:8px;}
.ovCell{background:#1B2027;border-radius:6px;padding:8px 10px;}
.ovCellLabel{font-size:9px;color:#8B929C;}
.ovCellVal{font-size:14px;font-weight:600;color:#E4E7EB;}
.ovCellVal.muted2{color:#4A515C;}
.ovCellTgt{font-size:8px;margin-top:1px;}
.ovCellTgt.pos{color:#5B9E6F;}
.ovCellTgt.warn{color:#C79A4B;}
.ovCellTgt.neg{color:#D4687A;}
.ovCellTgt.muted{color:#6B7280;}
.ovHint{background:#1B2027;border-left:3px solid #6E86B8;padding:9px 12px;border-radius:0 6px 6px 0;font-size:11px;color:#8B929C;margin-top:4px;}
.ovHintLabel{color:#B5D4F4;}
.posT{color:#5B9E6F;}
.warnT{color:#C79A4B;}
@media(max-width:820px){.ovCompanyStrip,.ovTotalRow{grid-template-columns:repeat(2,1fr);}.ovChanCells{grid-template-columns:repeat(3,1fr);}}
.chanBlock{margin-bottom:4px;}
.chanHead{width:100%;display:flex;align-items:center;justify-content:space-between;padding:9px 10px;border-radius:7px;background:transparent;border:none;color:var(--text,#E4E7EB);font-size:12.5px;font-weight:600;font-family:inherit;cursor:pointer;transition:background .15s;}
.chanHead:hover{background:#1B2027;}
.chanOpen{background:#22303F;color:#6E86B8;}
.chanOpen:hover{background:#22303F;}
.chanPending{color:#8B929C;font-weight:500;cursor:pointer;}
.chanCaret{font-size:9px;color:#6B7280;}
.chanOpen .chanCaret{color:#6E86B8;}
.chanEmpty{font-size:10px;color:#6B7280;padding:6px 12px 10px;line-height:1.6;}
.breadcrumb{display:flex;align-items:center;gap:7px;font-size:11px;margin-bottom:16px;flex-wrap:wrap;}
.bcItem{color:#8B929C;cursor:pointer;}
.bcItem:hover{color:#B5D4F4;}
.bcSep{color:#4A515C;}
.bcCurrent{color:#B5D4F4;font-weight:500;}
.tabDiv{width:1px;height:18px;background:var(--line,#2A2F37);margin:0 4px;align-self:center;}
.lockScreen{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0E1116;font-family:var(--font-sans,system-ui);}
.lockBox{background:#161B22;border:0.5px solid #2A2F37;border-radius:14px;padding:36px 32px;width:320px;display:flex;flex-direction:column;align-items:center;box-shadow:0 8px 40px rgba(0,0,0,.4);}
.lockMark{font-size:28px;color:#7C8CA3;margin-bottom:14px;}
.lockTitle{font-size:16px;font-weight:600;color:#E4E7EB;margin-bottom:4px;text-align:center;}
.lockSub{font-size:12px;color:#8B929C;margin-bottom:22px;text-align:center;}
.lockInput{width:100%;box-sizing:border-box;background:#0E1116;border:1px solid #2A2F37;border-radius:8px;padding:11px 13px;color:#E4E7EB;font-size:14px;font-family:inherit;outline:none;transition:border .15s;}
.lockInput:focus{border-color:#6E86B8;}
.lockErr{border-color:#D4687A;}
.lockErrMsg{font-size:11px;color:#D4687A;margin-top:8px;align-self:flex-start;}
.lockBtn{width:100%;margin-top:16px;background:#2E4A6E;color:#B5D4F4;border:none;border-radius:8px;padding:11px;font-size:14px;font-weight:500;cursor:pointer;font-family:inherit;transition:background .15s;}
.lockBtn:hover{background:#3A5680;}
.splitGrid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;}
.platCard{background:#1B2027;border-radius:8px;padding:14px;}
.platCard.ggl{border-top:2px solid #6E86B8;}
.platCard.fb{border-top:2px solid #9A82C4;}
.platName{font-size:13px;font-weight:500;margin-bottom:10px;}
.miniTbl{width:100%;font-size:11px;border-collapse:collapse;}
.miniTbl td{padding:3px 0;color:#8B929C;}
.miniTbl td:last-child{text-align:right;color:#E4E7EB;}
.allocLabel{font-size:11px;color:#8B929C;margin-bottom:6px;}
.allocBar{display:flex;height:22px;border-radius:4px;overflow:hidden;margin-bottom:12px;}
.allocG{background:#6E86B8;display:flex;align-items:center;padding-left:8px;font-size:10px;color:#fff;white-space:nowrap;}
.allocF{background:#9A82C4;display:flex;align-items:center;padding-left:8px;font-size:10px;color:#fff;white-space:nowrap;}
.segRow{margin-bottom:12px;}
.chanLbl{font-size:10px;color:#6B7280;align-self:center;margin-right:2px;}
.adseg-good{color:#5B9E6F;}
.adseg-burn{color:#D4687A;}
.adseg-reduce{color:#C79A4B;}
.adseg-margin{color:#C79A4B;}
.adseg-potential{color:#6E86B8;}
.adseg-noclick{color:#8B929C;}
.dataTbl td.nameCol{max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#B5B9C0;}
.bcgCell:hover{background:#20262E;}
.bcgActive{outline:1.5px solid #6E86B8;background:#20262E;}
.bcgBadge{font-size:11px;color:#8B929C;margin-bottom:10px;display:flex;align-items:center;gap:10px;}
.bcgBadge b{color:#B5D4F4;}
.bcgClear{font-size:10px;background:#20262E;color:#8B929C;border:none;padding:3px 8px;border-radius:5px;cursor:pointer;font-family:inherit;}

/* ============================================================
   MOBILE / RESPONSIVE — đặt CUỐI để override rule desktop ở trên.
   Nguyên tắc: không bóp chữ; grid nhiều cột → ít cột; hàng cột cứng
   → xếp chồng; thanh tab & bảng → cuộn ngang mượt (không vỡ layout).
   ============================================================ */

/* ---- Nhúng trong KPI dashboard (tab marketing) ----
   Bỏ full-height + sticky của bản standalone: khung ngoài đã có header
   sticky riêng, để nguyên sẽ có 2 thanh dính chồng nhau + thừa 100vh. */
.app.embedded { min-height: 0; background: transparent; }
.app.embedded .topbar {
  position: static; backdrop-filter: none; background: transparent;
  padding-left: 0; padding-right: 0;
}
.app.embedded .wrap { padding-left: 0; padding-right: 0; max-width: 100%; }

/* ---- Tablet & nhỏ hơn (≤ 900px) ---- */
@media (max-width: 900px) {
  .ovCoGrid { grid-template-columns: repeat(3, 1fr); }
  .mkSegGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  /* hàng có cột cố định → cho phần giữa co lại, tránh tràn ngang */
  .obRow { grid-template-columns: 110px minmax(0, 1fr) 120px 110px; gap: 8px; }
  .ovcBpRow { grid-template-columns: 100px minmax(0, 1fr) 110px 40px; gap: 8px; }
}

/* ---- Mobile (≤ 640px) ---- */
@media (max-width: 640px) {
  /* khung: bớt padding để lấy bề ngang */
  .wrap { padding: 12px; }
  .topbar { padding: 10px 12px; gap: 8px; }
  .panel { padding: 11px; border-radius: 7px; }
  .report { overflow-x: hidden; }          /* chặn tràn ngang toàn trang */

  /* thanh tab: cuộn ngang thay vì vỡ dòng */
  .tabs {
    overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none;
    -webkit-overflow-scrolling: touch; padding-bottom: 2px;
  }
  .tabs::-webkit-scrollbar { display: none; }
  .tab { flex: 0 0 auto; padding: 6px 11px; font-size: 12.5px; }

  /* tiêu đề report + nút: xuống dòng thay vì chen nhau */
  .repHead { flex-wrap: wrap; gap: 8px; align-items: flex-start; }
  .repTitle { font-size: 14px; }
  .secHeadRow { flex-wrap: wrap; gap: 6px; }
  .legendRow { flex-wrap: wrap; }
  .bcgBadge { flex-wrap: wrap; gap: 6px; }

  /* KPI: 2 cột cố định cho dễ đọc (thay vì auto-fit ra 1 cột quá rộng) */
  .kpiGrid, .kpiCards { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }

  /* các grid nhiều cột → 2 cột */
  .ovCoGrid, .ovChanCells, .mkSegGrid, .companyStrip,
  .ovCompanyStrip, .ovTotalRow, .mkScoreRow, .emKpiRow {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .cards, .insightGrid, .mkWebGrid, .emTwoCol, .bcgGrid, .splitGrid {
    grid-template-columns: 1fr;
  }

  /* hàng cột cứng → xếp chồng, căn trái */
  .obRow, .ovcBpRow {
    grid-template-columns: 1fr; gap: 2px;
    padding: 8px 0; border-bottom: .5px solid #232830;
  }

  /* ô tìm kiếm chiếm trọn dòng khi filterRow wrap */
  .filterRow { flex-direction: column; align-items: stretch; }
  .searchBox { width: 100%; flex: none; font-size: 12px; padding: 8px 10px; }
  .chipRow { gap: 4px; }
  .chip { font-size: 10px; }

  /* BẢNG: giữ nguyên độ rộng chữ, cuộn ngang — KHÔNG bóp chữ */
  .tblWrap {
    overflow-x: auto; -webkit-overflow-scrolling: touch;
    margin: 0 -11px; padding: 0 11px;      /* cuộn tràn hết mép panel */
  }
  .dataTbl { min-width: 560px; }
  .dataTbl th, .dataTbl td { white-space: nowrap; padding: 7px 8px 7px 0; }
  .dataTbl td.nameCol { max-width: 130px; }

  /* thanh bar & nhãn dài → không đẩy vỡ layout */
  .pcBarTop { gap: 8px; }
  .pcBarTop > span:first-child {
    min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  /* vùng chạm: nút bấm đủ lớn theo chuẩn mobile */
  .toggle button, .cenToggle, .bcgClear, .chip { min-height: 32px; }
  .bcgCell { padding: 10px; }
}

/* ---- Mobile nhỏ (≤ 400px) ---- */
@media (max-width: 400px) {
  .kpiGrid, .kpiCards, .ovCoGrid, .ovChanCells, .mkSegGrid,
  .companyStrip, .ovCompanyStrip, .ovTotalRow, .mkScoreRow, .emKpiRow {
    grid-template-columns: 1fr;
  }
  .wrap { padding: 10px; }
  .repTitle { font-size: 13px; }
}
`;
